import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'; // v2026-11-04a — Factures : n° de facture visible avant paiement
import { consumePrefetch } from './prefetch';
// Code-splitting — onglets chargés à la demande (bundle initial allégé)
// Si un chunk n'existe plus (nouveau déploiement), on recharge la page.
const lazyChunk = (fn) => lazy(() => fn().catch(e => {
  if (e?.name === 'ChunkLoadError' || /Loading chunk/.test(e?.message)) window.location.reload();
  throw e;
}));
const ImportTab   = lazyChunk(() => import('./Import'));
const FacturesTab = lazyChunk(() => import('./Factures'));
const FacturationTab = lazyChunk(() => import('./Facturation'));
const EquipeTab   = lazyChunk(() => import('./Equipe'));
const VinylesTab  = lazyChunk(() => import('./Vinyles'));
const MagazinesTab = lazyChunk(() => import('./Magazines'));
const CalculsPage = lazyChunk(() => import('./CalculsPage'));
const TicketsTab  = lazyChunk(() => import('./Tickets'));
const DesignSystemTab = lazyChunk(() => import('./DesignSystem'));
const AffichesTab = lazyChunk(() => import('./Affiches'));
const StoryOpenTab = lazyChunk(() => import('./StoryOpen'));
const StoryProgrammeTab = lazyChunk(() => import('./StoryProgramme'));
const DisposTab = lazyChunk(() => import('./Dispos'));
const EmploiDuTempsTab = lazyChunk(() => import('./EmploiDuTemps'));
const EtiquettesTab = lazyChunk(() => import('./Etiquettes'));
const EtiquettesPrixTab = lazyChunk(() => import('./EtiquettesPrix'));
const WishlistTab = lazyChunk(() => import('./Wishlist'));
const TodosTab    = lazyChunk(() => import('./Todos'));
const MesTodos    = lazyChunk(() => import('./Todos').then(m => ({ default: m.MesTodos })));
const TicketsResolusBanner = lazyChunk(() => import('./Tickets').then(m => ({ default: m.TicketsResolusBanner })));
const FichesPaieTab = lazyChunk(() => import('./FichesPaie'));
const RapportsTab   = lazyChunk(() => import('./Rapports'));
const MonitoringTab = lazyChunk(() => import('./Rapports').then(m => ({ default: m.MonitoringTab })));
const DabTab        = lazyChunk(() => import('./Dab'));
const SciInvestTab  = lazyChunk(() => import('./SciInvest'));
const SciFacturesTab = lazyChunk(() => import('./SciFactures'));
const FacturesStripeTab = lazyChunk(() => import('./FacturesStripe'));
const BureauCreancesTab = lazyChunk(() => import('./BureauCreances'));
const FocusTab      = lazyChunk(() => import('./Focus'));
const PersoProjetsTab = lazyChunk(() => import('./PersoProjets'));
const RessourcesTab = lazyChunk(() => import('./Ressources'));
const ProspectionTab = lazyChunk(() => import('./Prospection'));
const ReservationsVinylesTab = lazyChunk(() => import('./ReservationsVinyles'));
const ProspectionBureauTab = lazyChunk(() => import('./ProspectionBureau'));
const VoixOffTab = lazyChunk(() => import('./VoixOff'));
const AccesTab = lazyChunk(() => import('./Acces'));
const FermetureTab = lazyChunk(() => import('./Fermeture'));
const FermetureRappel = lazyChunk(() => import('./Fermeture').then(m => ({ default: m.FermetureRappel })));
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell, PieChart, Pie,
} from 'recharts';
import {
  LayoutDashboard, Building2, Briefcase, Coffee,
  Upload, Menu, X, CheckCircle, Circle,
  ChevronLeft, ChevronRight,
  Users, Receipt, Sun, Disc3, Percent, Sandwich,
  ChevronDown, Settings as SettingsIcon, User, Wifi, Info,
  BookOpen, Inbox, Newspaper, TrendingUp, TrendingDown, FileText, ShoppingBag, Printer, Wrench, MoreHorizontal,
  CalendarDays,
  Tag, Search, AlertTriangle, ListChecks, ClipboardList, Download, Heart, Sparkles, Trophy, Package,
  ExternalLink, Link2, Activity, Calculator, ChartLine, Scale, Landmark, Target, Layers,
  Mic,
  Laptop,
  Sheet, KeyRound,
} from 'lucide-react';
import { FS, TYPO, S, R, SHADCN, softTone } from './theme';
import useIsMobile from './hooks/useIsMobile';
import Button from './components/Button';
import Skeleton from './components/Skeleton';
import EmptyState from './components/EmptyState';
import ArticleTable from './components/ArticleTable';
import SquareItemLink from './components/SquareItemLink';
import Card from './components/Card';
import NotificationsCard from './components/NotificationsCard';
import Stat from './components/Stat';
import AnimatedAmount from './components/AnimatedAmount';
import TodayDateTitle from './components/TodayDateTitle';
import MonthTitle from './components/MonthTitle';
import NetMiniChart from './components/NetMiniChart';
import DayPicker from './components/DayPicker';
import VirementConseille from './components/VirementConseille';
import MoisSelector from './components/MoisSelector';
import StatusPill from './components/StatusPill';
import Input from './components/Input';
import DeltaPill from './components/DeltaPill';
import Alert from './components/Alert';
import LiveIndicator from './components/LiveIndicator';
import Collapse from './components/Collapse';
import DetailPanel from './components/DetailPanel';
import PageHeader from './components/PageHeader';
import { EntityAccentContext } from './components/EntityAccent';
import PageLoader from './components/PageLoader';
import ThemeToggle, { ThemeSegmented } from './components/ThemeToggle';
import WeatherBadge from './components/WeatherBadge';
import NotifBell from './components/Annonces';
import SegmentedControl from './components/SegmentedControl';
import Avatar, { MEMBRES_CONNUS, getMemberColorOverrides, saveMemberColorOverrides, couleurMembre, texteSurFond, setMemberPhotos } from './components/Avatar';
import SectionTitle from './components/SectionTitle';

// ── Constantes comptables — à ajuster ici uniquement ─────────────────────────
const TVA_SUR_PLACE        = 0.10;   // restauration sur place
const TVA_CAFE             = 0.10;   // alias — restauration / boissons (inchangé)
const TVA_A_EMPORTER       = 0.055;  // food/boissons à emporter, café grain, épicerie fine
const TVA_PRESSE           = 0.021;  // magazines, presse, livres
const TVA_RETAIL           = 0.20;   // vinyles, merch, services
const IS_RATE_1            = 0.15;   // IS taux réduit PME (jusqu'à 42 500 €/an)
const IS_RATE_2            = 0.25;   // IS taux normal
const IS_SEUIL             = 42500;  // seuil taux réduit IS
// Les constantes métier (SQUARE_FEE_RATE, FOOD_COST_*,
// RATIO_FOOD_VS_ACHATS) sont chargées depuis dashboard_config dans le composant App.

// Mots-clés pour classifier les transactions bancaires
const KEYWORDS_FOOD = [
  'METRO','PROMOCASH','THIRIET','PICARD','LAIT','BOISSON','BOULANGERIE','PAIN',
  'ALIMENTAIRE','EPICERIE','COURSES','LECLERC','MONOPRIX','CARREFOUR','CASINO',
  'LIDL','ALDI','BIO C BON','NATURALIA','BIOCOOP','COFFEE','CAFÉ IMPORT','TORREFACT',
  'MALONGO','ILLY','LAVAZZA','PERRIER','EVIAN','BADOIT','SCHWEPPES',
];
const KEYWORDS_RETAIL = [
  'VINYL','VINYLE','DISQUE','RECORD','MERCH','MERCHANDIS','FNAC','AMAZON',
  'CULTURA','SOUNDIT','DEEJAY','BEATPORT','MUSICAL','SONO','MATERIEL AUDIO',
  'DISC','ALBUM','MUSIC STORE','GIBERT','CROCODISC',
];

// ── Infos café — fiche d'identité de l'entreprise (à ajuster ici uniquement) ──
// Valeurs vides → affichées « À compléter » (placeholder discret) côté UI.
const CAFE_INFOS = {
  identite: [
    { label: 'Raison sociale',   value: 'CAFE SONORE' },
    { label: 'Nom commercial',   value: 'Café Sonore' },
    { label: 'Forme juridique',  value: 'SAS — Société par actions simplifiée' },
    { label: 'SIREN',            value: '940 806 656', num: true },
    { label: 'SIRET (siège)',    value: '940 806 656 00028', num: true },
    { label: 'Code APE / NAF',   value: '5630Z — Débits de boissons', num: true, hint: 'à confirmer (avis INSEE)' },
    { label: 'RCS',              value: '940 806 656 R.C.S. Bordeaux', num: true },
    { label: 'EUID',             value: 'FR3302.940806656', num: true },
    { label: 'Capital social',   value: '1 000 €', num: true, hint: '1 000 actions de 1 €' },
  ],
  fiscal: [
    { label: 'N° TVA intracom.', value: 'FR19 940 806 656', num: true },
    { label: 'Régime de TVA',    value: 'Réel', hint: 'modalité (simplifié / normal) à confirmer' },
  ],
  contact: [
    { label: 'Adresse',          value: '3 rue de la Chapelle Saint-Jean, 33000 Bordeaux' },
    { label: 'Téléphone',        value: '' },
    { label: 'Email',            value: 'fardeaucorentin@gmail.com', hint: 'email du gérant — pas d’adresse société dédiée' },
    { label: 'Site web',         value: '' },
    { label: 'PDL Électricité',  value: '50013094518990', num: true, hint: 'non recoupé' },
  ],
  // Un bloc par compte ; `doc` = id servi par /api/supabase-data?resource=doc
  banques: [
    {
      nom: 'BNP Paribas', detail: 'Agence Bordeaux Capucins · compte chèque', doc: 'rib-bnp',
      rows: [
        { label: 'IBAN', value: 'FR76 3000 4003 4000 0102 6639 171', num: true },
        { label: 'BIC',  value: 'BNPAFRPPXXX', num: true },
      ],
    },
    {
      nom: 'Revolut', detail: 'Revolut Bank UAB · compte Run EUR · ouvert le 21 mai 2026', doc: 'rib-revolut',
      rows: [
        { label: 'IBAN', value: 'FR76 2823 3000 0189 6918 7666 149', num: true },
        { label: 'BIC',  value: 'REVOFRP2', num: true, hint: 'SWIFT hors EEE : BIC intermédiaire CHASDEFX' },
      ],
    },
  ],
  direction: [
    { label: 'Président',          value: 'EURL Fardeau', hint: 'personne morale — SIREN 884 028 473' },
    { label: 'Représentant légal', value: 'Corentin Fardeau' },
    { label: 'Immatriculation',    value: '13 février 2025' },
    { label: "Début d'activité",   value: '5 février 2025' },
    { label: "Clôture d'exercice", value: '30 juin' },
    { label: 'Durée',              value: '99 ans — jusqu’au 13 février 2124' },
  ],
};

// ── Tab definitions ───────────────────────────────────────────────────────────
// ── Entités ───────────────────────────────────────────────────────────────────
const ENTITIES = [
  { id: 'cafe',     label: 'Café Sonore',   Icon: Coffee },
  { id: 'bureau',   label: 'Bureau Sonore', Icon: Briefcase },
  { id: 'sci',      label: 'SCI Sonore',    Icon: Building2 },
  // Espace perso de Corentin (vue Focus) — transverse aux 3 entités, donc
  // entité à part plutôt que rangé sous le Café. persoOnly : filtré si
  // /api/me ne renvoie pas perso:true.
  { id: 'perso',    label: 'Perso',         Icon: ListChecks, persoOnly: true },
  { id: 'outils',   label: 'Outils',        Icon: Wrench },
  { id: 'settings', label: 'Réglages',      Icon: SettingsIcon },
];

// ── Navigation à 2 niveaux : entité → section → onglets ──────────────────────
// Bureau/SCI n'ont qu'une section : la liste des sections est alors masquée
// côté sidebar et seuls les onglets sont affichés (UX).
const ENTITY_NAV = {
  cafe: [
    { id: 'home', label: "Aujourd'hui", Icon: Sun, tabs: [
      { id: 'today', label: "Aujourd'hui", Icon: Sun },
      // Tâches de la semaine + fermeture du soir — aussi en panneau (FermetureRappel).
      { id: 'fermeture', label: 'Checklist', Icon: ListChecks },
    ]},
    { id: 'finance', label: 'Finances', Icon: TrendingUp, tabs: [
      { id: 'cafe',     label: 'Ce mois',      Icon: Coffee },
      { id: 'factures', label: 'Factures',     Icon: Receipt },
      // Factures clients (privatisations, événements) émises via Stripe Invoicing.
      // À ne pas confondre avec « Factures » juste au-dessus : celles des fournisseurs.
      { id: 'facturation_cafe', label: 'Facturation', Icon: FileText },
      { id: 'dab',      label: 'DAB',          Icon: Disc3 },
      { id: 'rapports', label: 'Comptabilité', Icon: Calculator },
      { id: 'import',   label: 'Import',       Icon: Upload },
      { id: 'records',  label: 'Records',      Icon: Trophy },
    ]},
    // Monitoring : analyse des ventes/marges — distinct de la Comptabilité
    // (seul onglet visible du comptable). Un onglet sidebar par rapport.
    { id: 'monitoring', label: 'Monitoring', Icon: Activity, tabs: [
      { id: 'articles_ventes',     label: 'Top ventes',     Icon: TrendingUp },
      { id: 'monitoring_ventes',    label: 'Ventes',         Icon: Receipt },
      { id: 'monitoring_evolution', label: 'Évolution',      Icon: ChartLine },
      { id: 'monitoring_boissons', label: 'Boissons',       Icon: Coffee },
      { id: 'monitoring_casse',    label: 'Foods',          Icon: AlertTriangle },
      // Onglet séparé de Foods à dessein : Verdicts a sa propre fenêtre (3 mois
      // fiables) et pas de MoisSelector. Cohabiter avec un suivi mensuel forçait
      // à justifier partout pourquoi les deux tableaux divergent sur un produit.
      { id: 'monitoring_verdicts', label: 'Verdicts food',  Icon: Scale },
      { id: 'monitoring_traca',    label: 'Traçabilité',    Icon: ClipboardList },
      { id: 'monitoring_meteo',    label: 'Météo × ventes', Icon: Sun },
    ]},
    { id: 'rh', label: 'RH', Icon: Users, tabs: [
      { id: 'planning',     label: 'Planning',      Icon: Users },
      // Le tableur de Charlotte, dans le dashboard : même fonctionnement que sa
      // feuille (une semaine par bloc, on tape « 7h45 - 16h45 »). Réservé à qui
      // écrit le planning.
      { id: 'emploi_du_temps', label: 'Emploi du temps', Icon: Sheet },
      { id: 'todos',        label: 'Todos',          Icon: ListChecks },
      // DSN (masse salariale, cotisations) : réservé aux admins, pas aux membres.
      { id: 'dsn',          label: 'DSN',            Icon: FileText },
      { id: 'fiches_paie',  label: 'Fiches de paie', Icon: FileText },
      // Créneaux libres de Corentin (free/busy de ses agendas Google) — pour que
      // Baptiste cale un point sans demander. Même gate que Fiches de paie.
      { id: 'dispos',       label: 'Dispos Corentin', Icon: CalendarDays },
    ]},
    // Section produit : gestion catalogue/stock uniquement (Top ventes → Monitoring).
    { id: 'produits', label: 'Produits', Icon: ShoppingBag, tabs: [
      { id: 'articles_liste',   label: 'Catalogue',  Icon: Tag },
      { id: 'vinyles',          label: 'Vinyles',    Icon: Disc3 },
      // Click & collect depuis cafesonore.com : disques à sortir du bac, à remettre.
      { id: 'reservations_vinyles', label: 'Réservations', Icon: Inbox },
      { id: 'magazines',        label: 'Magazines',  Icon: Newspaper },
      { id: 'wishlist',         label: 'Wishlist',   Icon: Heart },
    ]},
    // Développement commercial — pipeline des privatisations / événements B2B.
    { id: 'commercial', label: 'Commercial', Icon: Target, tabs: [
      { id: 'prospection', label: 'Prospection B2B', Icon: Target },
    ]},
    // Fiche d'identité (SIRET, TVA, RIB, Kbis) — utile au comptable aussi.
    { id: 'infos', label: 'Infos', Icon: Info, tabs: [
      { id: 'infos_cafe', label: 'Infos Café', Icon: Info },
    ]},
  ],
  // Bureau : la période (mois / année) est un toggle in-page, pas un dédoublement
  // d'onglets. Une seule section → la sidebar n'affiche que les 3 onglets.
  bureau: [
    { id: 'bureau', label: 'Bureau Sonore', Icon: Briefcase, tabs: [
      { id: 'bureau_global',   label: 'Global',      Icon: PieChart },
      { id: 'bureau_depenses', label: 'Dépenses',    Icon: TrendingDown },
      { id: 'facturation',     label: 'Facturation', Icon: FileText },
      { id: 'bureau_creances', label: 'Créances Café', Icon: Landmark },
      { id: 'import_bureau',   label: 'Import',      Icon: Upload },
      // CRM light agences / prods / réalisateurs + leads IA quotidiens.
      { id: 'prospection_bureau', label: 'Prospection', Icon: Target },
      // Casting voix — démos reçues, caractéristiques et contacts.
      { id: 'voix_off', label: 'Voix off', Icon: Mic },
    ]},
  ],
  sci: [
    { id: 'finance', label: 'Suivi Financier', Icon: TrendingUp, tabs: [
      { id: 'sci', label: 'Ce mois', Icon: Building2 },
      { id: 'sci_invest', label: 'Investissement', Icon: TrendingUp },
      // Factures émises par la SCI (loyers, régul, avoirs) — remplace Dext.
      { id: 'sci_factures', label: 'Factures', Icon: Receipt },
      { id: 'import_sci', label: 'Import', Icon: Upload },
    ]},
  ],
  // Vue Focus — tâches perso + agenda de Corentin. persoOnly : visible
  // uniquement si /api/me renvoie perso:true (userId === CORENTIN_USER_ID).
  // La protection réelle est côté serveur (?resource=perso-todos → 403).
  // Section unique → la sidebar n'affiche que l'onglet (même UX que Bureau/SCI).
  perso: [
    { id: 'perso', label: 'Perso', Icon: ListChecks, tabs: [
      { id: 'focus',         label: 'Focus',   Icon: ListChecks, persoOnly: true },
      { id: 'perso_projets', label: 'Projets', Icon: Layers,     persoOnly: true },
    ]},
  ],
  outils: [
    { id: 'outils', label: 'Outils', Icon: Wrench, tabs: [
      { id: 'tickets',         label: 'Tickets',        Icon: Inbox },
      { id: 'story_open', label: 'Story Open', Icon: Sparkles },
      { id: 'story_programme', label: 'Story Programme', Icon: CalendarDays },
      { id: 'affiches',   label: 'Affiches',   Icon: Printer },
      { id: 'etiquettes', label: 'Étiquettes café', Icon: Coffee },
      { id: 'etiquettes_prix', label: 'Étiquettes A4', Icon: Tag },
    ]},
  ],
  settings: [
    { id: 'settings', label: 'Réglages', Icon: SettingsIcon, tabs: [
      { id: 'settings_profile',     label: 'Profil',         Icon: User },
      // Qui a accès à quoi, compte par compte (lib/modules.js). Super-admin seul.
      { id: 'settings_acces',       label: 'Accès',          Icon: KeyRound,        superAdminOnly: true },
      { id: 'settings_connections', label: 'Connexions',     Icon: Wifi },
      { id: 'ressources',           label: 'Ressources',     Icon: Link2 },
      { id: 'settings_personnel',   label: 'Personnel',      Icon: Users,           adminOnly: true },
      { id: 'settings_methode',     label: 'Méthode',        Icon: BookOpen,        adminOnly: true },
      { id: 'settings_designsystem',label: 'Design System',  Icon: LayoutDashboard, adminOnly: true },
      { id: 'settings_debug',       label: 'Debug',          Icon: LayoutDashboard, adminOnly: true },
    ]},
  ],
};

// Helpers nav — filtrent par accès et résolvent section/onglets.
// Qui voit quel onglet : décidé côté serveur (lib/modules.js) et rendu par
// /api/me — `ongletsGeres` (onglets soumis aux accès) et `onglets` (ceux que
// ce compte peut ouvrir, rôle + cases cochées dans Réglages → Accès). Le même
// fichier décide des portes API : un onglet visible ne répond plus 403.
// Hors modules : le profil (tous), l'espace perso, les réglages d'admin.
const getSections = (entity, role, session = null) =>
  (ENTITY_NAV[entity] || [])
    .map(sec => ({ ...sec, tabs: sec.tabs.filter(t => {
      if (t.persoOnly) return !!session?.perso;
      if (t.superAdminOnly) return role === 'admin';
      if (role === 'admin') return true;
      if (session?.ongletsGeres?.includes(t.id)) return !!session.onglets?.includes(t.id);
      if (t.adminOnly) return role === 'cafe_admin';
      return t.id === 'settings_profile';
    }) }))
    .filter(sec => sec.tabs.length > 0);

const getAllTabs = (entity, role, session = null) =>
  getSections(entity, role, session).flatMap(sec => sec.tabs);

const findSectionForTab = (entity, role, tabId, session = null) =>
  getSections(entity, role, session).find(sec => sec.tabs.some(t => t.id === tabId));

// Migration des anciens IDs d'onglets (renommages depuis la v2026-05-23).
// Bureau : mois/année fusionnés en un seul onglet + toggle période → on remappe
// les anciens IDs period-spécifiques vers les nouveaux IDs uniques.
const TAB_ALIAS = {
  equipe: 'planning',
  tva: 'articles_liste', // TVA fusionnée dans le Catalogue Square (sous-vue in-page)
  bureau: 'bureau_global',
  bureau_mois_global:    'bureau_global',
  bureau_annee_global:   'bureau_global',
  bureau_mois_depenses:  'bureau_depenses',
  bureau_annee_depenses: 'bureau_depenses',
  bureau_mois_recettes:  'bureau_global',
  bureau_annee_recettes: 'bureau_global',
  facturation_mois:      'facturation',
  facturation_annee:     'facturation',
};
const normalizeTabId = (id) => TAB_ALIAS[id] || id;

const MOIS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
const MOIS_LABELS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
// Aligné sur les labels renvoyés par /api/qonto-treso (MOIS_FR côté API)
const MOIS_ABBR = ['Janv','Fév','Mars','Avr','Mai','Juin','Juil','Août','Sep','Oct','Nov','Déc'];

// ── Version build & cache ─────────────────────────────────────────────────────
const APP_VERSION    = process.env.REACT_APP_VERSION || 'dev';
const CACHE_VERSION  = '2026-v3'; // ← incrémenter pour invalider tous les caches locaux

// ── Cache-buster + auth — force du frais et gère la 401 (session expirée) ────
const apiFetch = async (url) => {
  const bustedUrl = url.includes('?') ? `${url}&v=${Date.now()}` : `${url}?v=${Date.now()}`;
  const res = await fetch(bustedUrl, {
    cache:       'no-store',
    credentials: 'same-origin',
    headers:     { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' },
  });
  // Session expirée → on revient au login (Root remontera /api/me et affichera Login)
  // Garde anti-boucle : un seul reload par session navigateur, sinon une 401 persistante
  // (env auth manquante en local, cookie invalide, etc.) provoque un reload infini.
  if (res.status === 401 && !url.startsWith('/data.json')) {
    if (typeof window !== 'undefined') {
      try {
        if (!sessionStorage.getItem('sonore_401_reloaded')) {
          sessionStorage.setItem('sonore_401_reloaded', '1');
          window.location.reload();
        }
      } catch { window.location.reload(); }
    }
  }
  return res;
};

// ── apiFetchCached — Stale-While-Revalidate (cache mémoire, non persisté) ───
// Les données financières (soldes, salaires, IBANs) ne doivent pas être
// stockées dans localStorage, accessible à tout script JS en cas de XSS.
const API_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const _apiMemCache = new Map();
const apiFetchCached = async (url) => {
  const cached = _apiMemCache.get(url);

  const refresh = () => apiFetch(url).then(async (res) => {
    if (res.ok) {
      try {
        const text = await res.clone().text();
        _apiMemCache.set(url, { ts: Date.now(), body: text });
      } catch (_) {}
    }
    return res;
  });

  // Cache frais → renvoie immédiatement, refetch en background (stale-while-revalidate)
  if (cached && Date.now() - cached.ts < API_CACHE_TTL_MS) {
    refresh().catch(() => {});
    return new Response(cached.body, {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  // Cache absent ou périmé → attend la réponse réseau
  return refresh();
};

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt     = (v) => Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k€' : v + '€';
const fmtFull = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const fmtDec  = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

// ── Thermomètre de rentabilité ────────────────────────────────────────────────
const Thermometer = ({ ca, seuil, manque, couverturePct }) => {
  const pct      = Math.min(ca / seuil, 1);
  const fillColor = pct >= 1 ? SHADCN.success : pct >= 0.85 ? SHADCN.warning : pct >= 0.6 ? SHADCN.warning : SHADCN.error;
  const ticks     = [25, 50, 75, 100]; // % markers
  return (
    <div style={{ marginBottom: S[1] }}>
      {/* Track */}
      <div style={{ position: 'relative', height: 28, background: SHADCN.muted, borderRadius: R.md, overflow: 'visible', marginBottom: 10 }}>
        {/* Fill */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: '100%',
          transform: `scaleX(${pct})`,
          transformOrigin: 'left',
          background: fillColor,
          borderRadius: R.pill,
          transition: 'transform 1s cubic-bezier(0.23, 1, 0.32, 1)',
        }} />
        {/* Tick marks */}
        {ticks.map(t => (
          <div key={t} style={{
            position: 'absolute', left: `${t}%`, top: -2, bottom: -2,
            width: 1, background: SHADCN.border,
            transform: 'translateX(-50%)',
          }} />
        ))}
        {/* Seuil label */}
        <div style={{
          ...TYPO.eyebrow, position: 'absolute', right: 0, top: -20,
          color: SHADCN.mutedFg, transform: 'translateX(50%)', whiteSpace: 'nowrap',
        }}>Seuil</div>
      </div>
      {/* Labels */}
      <div style={{ ...TYPO.body, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ ...TYPO.num, color: fillColor, fontWeight: 500 }}>{couverturePct}%</span>
          <span style={{ ...TYPO.num, color: SHADCN.mutedFg }}>{fmtFull(ca)} CA ce mois</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          {manque > 0
            ? <span style={{ ...TYPO.num, color: SHADCN.error }}>Manque <strong>{fmtFull(manque)}</strong></span>
            : <span style={{ color: SHADCN.success }}>✓ Seuil atteint</span>
          }
        </div>
      </div>
    </div>
  );
};

// ── CockpitBar — Thermomètre de Santé ────────────────────────────────────────
//  Barre segmentée en 3 zones avec curseur prorata et fill multi-couleurs.
const CockpitBar = ({ ca, seuil, flag1, flag2, prorataPct, isMobile }) => {
  const pct   = Math.min(Math.max(ca / seuil, 0), 1.05);
  const f1Pct = flag1 / seuil;   // ~37 % — Survie
  const f2Pct = flag2 / seuil;   // ~70 % — Sécurité
  const h     = isMobile ? 72 : 96;

  // Largeur de chaque segment coloré
  const seg1W = Math.min(pct, f1Pct);
  const seg2W = pct > f1Pct ? Math.min(pct, f2Pct) - f1Pct : 0;
  const seg3W = pct > f2Pct ? Math.min(pct, 1) - f2Pct : 0;

  return (
    <div style={{ position: 'relative', marginBottom: 28, userSelect: 'none' }}>

      {/* Barre principale */}
      <div style={{ position: 'relative', height: h, borderRadius: R.md, background: SHADCN.muted, overflow: 'hidden' }}>

        {/* Fonds de zones */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
          <div style={{ width: `${f1Pct * 100}%`, background: softTone(SHADCN.error).bg, borderRight: `2px dashed ${softTone(SHADCN.error).border}` }} />
          <div style={{ width: `${(f2Pct - f1Pct) * 100}%`, background: softTone(SHADCN.warning).bg, borderRight: `2px dashed ${softTone(SHADCN.warning).border}` }} />
          <div style={{ flex: 1, background: softTone(SHADCN.success).bg }} />
        </div>

        {/* Segments colorés (fill) — flat, no gradient */}
        {seg1W > 0 && (
          <div style={{
            position: 'absolute', top: 8, bottom: 8, left: 0,
            width: `${seg1W * 100}%`,
            background: SHADCN.error,
            opacity: 0.85,
            borderRadius: seg2W === 0 && seg3W === 0 ? '8px' : '8px 0 0 8px',
          }} />
        )}
        {seg2W > 0 && (
          <div style={{
            position: 'absolute', top: 8, bottom: 8, left: `${f1Pct * 100}%`,
            width: `${seg2W * 100}%`,
            background: SHADCN.warning,
            opacity: 0.85,
            borderRadius: seg3W === 0 ? '0 8px 8px 0' : 0,
          }} />
        )}
        {seg3W > 0 && (
          <div style={{
            position: 'absolute', top: 8, bottom: 8, left: `${f2Pct * 100}%`,
            width: `${seg3W * 100}%`,
            background: SHADCN.success,
            opacity: 0.85,
            borderRadius: '0 8px 8px 0',
          }} />
        )}

        {/* Curseur prorata "Où on devrait être" */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: `${prorataPct * 100}%`,
          width: 3, background: SHADCN.fg,
          transform: 'translateX(-50%)',
          borderRadius: R.xs,
          opacity: 0.55,
        }}>
          <div style={{
            ...TYPO.eyebrow, position: 'absolute', bottom: '110%',
            left: '50%', transform: 'translateX(-50%)',
            background: SHADCN.fg, color: SHADCN.bg, fontWeight: 700,
            padding: '2px 6px', borderRadius: R.xs, whiteSpace: 'nowrap',
          }}>
            Où on devrait être aujourd'hui
          </div>
        </div>

        {/* Étiquette CA actuel (flottante à la pointe du fill) */}
        {ca > 0 && (
          <div style={{
            ...TYPO.h2, ...TYPO.num,
            position: 'absolute', top: '50%', transform: 'translateY(-50%)',
            left: `calc(${Math.min(pct, 0.95) * 100}% - ${isMobile ? 52 : 68}px)`,
            color: pct < f1Pct ? SHADCN.error : pct < f2Pct ? SHADCN.warning : SHADCN.success,
            textShadow: '0 1px 6px rgba(0,0,0,0.8)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(ca)}
          </div>
        )}
      </div>

      {/* Flags sous la barre */}
      <div style={{ position: 'relative', height: 44, marginTop: 6 }}>
        {/* Flag 1 — Survie */}
        <div style={{ position: 'absolute', left: `${f1Pct * 100}%`, transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ width: 1, height: 10, background: softTone(SHADCN.error).border, margin: '0 auto' }} />
          <div style={{ ...TYPO.eyebrow, ...TYPO.num, color: SHADCN.error, whiteSpace: 'nowrap', marginTop: 3, fontWeight: 500 }}>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(flag1)}
          </div>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.error, opacity: 0.8 }}>🔴 Loyer + salaires</div>
        </div>
        {/* Flag 2 — Sécurité */}
        <div style={{ position: 'absolute', left: `${f2Pct * 100}%`, transform: 'translateX(-50%)', textAlign: 'center' }}>
          <div style={{ width: 1, height: 10, background: softTone(SHADCN.warning).border, margin: '0 auto' }} />
          <div style={{ ...TYPO.eyebrow, ...TYPO.num, color: SHADCN.warning, whiteSpace: 'nowrap', marginTop: 3, fontWeight: 500 }}>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(flag2)}
          </div>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.warning, opacity: 0.8 }}>🟠 Toutes charges</div>
        </div>
        {/* Flag 3 — Rentable */}
        <div style={{ position: 'absolute', right: 0, textAlign: 'right' }}>
          <div style={{ width: 1, height: 10, background: softTone(SHADCN.success).border, marginLeft: 'auto' }} />
          <div style={{ ...TYPO.eyebrow, ...TYPO.num, color: SHADCN.success, whiteSpace: 'nowrap', marginTop: 3, fontWeight: 500 }}>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(seuil)}
          </div>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.success, opacity: 0.8 }}>🟢 Rentable !</div>
        </div>
      </div>
    </div>
  );
};

// ── Sidebar (desktop) ─────────────────────────────────────────────────────────
const SIDEBAR_W_OPEN  = 220;
const SIDEBAR_W_CLOSE = 56;

// Sidebar — niveau 1 = entité (dropdown), niveau 2 = sections (Suivi X) ou
// onglets directs si l'entité n'a qu'une section (bureau, sci, settings).
// ── Hooks nav ─────────────────────────────────────────────────────────────────
// Ferme un menu/dropdown au clic extérieur ou sur Échap.
function useDismiss(ref, open, onClose) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, onClose]);
}

// Détecte le débordement horizontal d'un conteneur scrollable → { left, right }
// pour afficher un dégradé d'affordance sur le bord concerné.
function useScrollFades(ref, deps) {
  const [fade, setFade] = React.useState({ left: false, right: false });
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setFade({
        left:  scrollLeft > 1,
        right: scrollLeft + clientWidth < scrollWidth - 1,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    return () => { el.removeEventListener('scroll', update); if (ro) ro.disconnect(); };
  }, deps);
  return fade;
}

// Rangée d'onglets scrollable horizontalement avec dégradés de bord (affordance
// de débordement). Les enfants sont rendus dans le conteneur scrollable.
const ScrollFadeRow = ({ children, style, innerStyle }) => {
  const ref = React.useRef(null);
  const fade = useScrollFades(ref, [children]);
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0, ...style }}>
      <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto', overflowY: 'hidden', ...innerStyle }}>
        {children}
      </div>
      {fade.left && (
        <div aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 24, pointerEvents: 'none',
          background: `linear-gradient(to right, ${SHADCN.bg}, transparent)` }} />
      )}
      {fade.right && (
        <div aria-hidden style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 24, pointerEvents: 'none',
          background: `linear-gradient(to left, ${SHADCN.bg}, transparent)` }} />
      )}
    </div>
  );
};

const Sidebar = ({ activeTab, activeSection, sections, activeEntity, entities, setActiveTab, setActiveSection, setActiveEntity, onOpenPanel, panelOpen, onLogout, onSync, collapsed, onToggle }) => {
  const [entityOpen, setEntityOpen] = React.useState(false);
  const entityRef = React.useRef(null);
  const closeEntity = React.useCallback(() => setEntityOpen(false), []);
  useDismiss(entityRef, entityOpen, closeEntity);
  const currentEntity = entities.find(e => e.id === activeEntity) || ENTITIES.find(e => e.id === activeEntity);
  const singleEntity = entities.length <= 1;
  const w = collapsed ? SIDEBAR_W_CLOSE : SIDEBAR_W_OPEN;
  // Si une seule section : on affiche directement ses onglets (UX inchangée
  // pour bureau / sci / settings). Sinon on affiche la liste des sections.
  const singleSection = sections.length <= 1;
  const navItems = singleSection
    ? (sections[0]?.tabs || []).map(t => ({ ...t, _kind: 'tab' }))
    : sections.map(s => ({ id: s.id, label: s.label, Icon: s.Icon, _kind: 'section' }));
  const activeNavId = singleSection ? activeTab : activeSection?.id;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0,
      width: w, height: '100vh',
      background: SHADCN.sidebar,
      borderRight: `1px solid ${SHADCN.sidebarBorder}`,
      color: SHADCN.sidebarFg,
      fontFamily: SHADCN.font,
      display: 'flex', flexDirection: 'column', zIndex: 200,
      transition: 'width 0.22s ease', overflow: 'hidden',
    }}>
      {/* Header logo + collapse */}
      <div style={{
        padding: collapsed ? '18px 0' : '18px 16px 14px',
        borderBottom: `1px solid ${SHADCN.sidebarBorder}`,
        display: 'flex', alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        minHeight: 64, flexShrink: 0,
      }}>
        {!collapsed && (
          <img src="/logo-white.png" alt="café sonore" style={{ height: 22, width: 'auto', display: 'block' }} />
        )}
        <button onClick={onToggle} title={collapsed ? 'Ouvrir' : 'Réduire'}
          style={{
            background: 'transparent', border: `1px solid ${SHADCN.sidebarBorder}`, borderRadius: SHADCN.radius,
            color: SHADCN.mutedFg, cursor: 'pointer',
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: `background ${SHADCN.durBase} ${SHADCN.easeOut}, color ${SHADCN.durBase} ${SHADCN.easeOut}, border-color ${SHADCN.durBase} ${SHADCN.easeOut}, transform 90ms ${SHADCN.easeOut}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.color = SHADCN.fg; e.currentTarget.style.background = SHADCN.sidebarAccent; }}
          onMouseLeave={e => { e.currentTarget.style.color = SHADCN.mutedFg; e.currentTarget.style.background = 'transparent'; }}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Entity selector */}
      <div ref={entityRef} style={{ padding: collapsed ? '12px 8px' : 12, borderBottom: `1px solid ${SHADCN.sidebarBorder}`, flexShrink: 0, position: 'relative' }}>
        {collapsed ? (
          currentEntity && <div style={{ display:'flex', justifyContent:'center' }}>
            <currentEntity.Icon size={18} color={SHADCN.mutedFg} strokeWidth={1.8} />
          </div>
        ) : (
          <>
            {singleEntity ? (
              <div style={{
                fontFamily: SHADCN.font, fontSize: FS.body, fontWeight: 500, lineHeight: 1.4,
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '10px 12px', borderRadius: SHADCN.radius,
                background: SHADCN.sidebarAccent, border: `1px solid ${SHADCN.sidebarBorder}`,
                color: SHADCN.sidebarFg,
              }}>
                {currentEntity && <currentEntity.Icon size={16} strokeWidth={1.8} />}
                {currentEntity?.label}
              </div>
            ) : (
            <button
              onClick={() => setEntityOpen(o => !o)}
              style={{
                fontFamily: SHADCN.font, fontSize: FS.body, fontWeight: 500, lineHeight: 1.4,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '10px 12px', borderRadius: SHADCN.radius,
                background: SHADCN.sidebarAccent, border: `1px solid ${SHADCN.sidebarBorder}`,
                color: SHADCN.sidebarFg,
                cursor: 'pointer', transition: `background ${SHADCN.durBase} ${SHADCN.easeOut}, color ${SHADCN.durBase} ${SHADCN.easeOut}, border-color ${SHADCN.durBase} ${SHADCN.easeOut}, transform 90ms ${SHADCN.easeOut}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = SHADCN.muted; }}
              onMouseLeave={e => { e.currentTarget.style.background = SHADCN.sidebarAccent; }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {currentEntity && <currentEntity.Icon size={16} strokeWidth={1.8} />}
                {currentEntity?.label}
              </span>
              <ChevronDown size={14} color={SHADCN.mutedFg} style={{ transform: entityOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            )}
            {!singleEntity && entityOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% - 2px)', left: 12, right: 12,
                background: SHADCN.popover, border: `1px solid ${SHADCN.sidebarBorder}`, borderRadius: SHADCN.radius,
                zIndex: 400, overflow: 'hidden',
                boxShadow: SHADCN.shadowXl,
              }}>
                {entities.map(e => (
                  <button key={e.id}
                    onClick={() => { setActiveEntity(e.id); setEntityOpen(false); }}
                    style={{
                      fontFamily: SHADCN.font, fontSize: FS.body, lineHeight: 1.4,
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      padding: '10px 12px',
                      background: e.id === activeEntity ? SHADCN.sidebarAccent : 'transparent',
                      border: 'none',
                      color: e.id === activeEntity ? SHADCN.fg : SHADCN.mutedFg,
                      fontWeight: e.id === activeEntity ? 600 : 400,
                      cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s, color 0.1s',
                    }}
                    onMouseEnter={ev => { ev.currentTarget.style.background = SHADCN.sidebarAccent; ev.currentTarget.style.color = SHADCN.fg; }}
                    onMouseLeave={ev => {
                      ev.currentTarget.style.background = e.id === activeEntity ? SHADCN.sidebarAccent : 'transparent';
                      ev.currentTarget.style.color = e.id === activeEntity ? SHADCN.fg : SHADCN.mutedFg;
                    }}
                  >
                    <e.Icon size={16} strokeWidth={1.8} />
                    {e.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Nav — sections (multi) ou onglets (mono) — <Button variant="sidebar"> */}
      <nav style={{ flex: 1, padding: collapsed ? '8px 6px' : S[2], display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', overflowX: 'hidden' }}>
        {navItems.filter(it => it.id !== 'settings').map(it => {
          const active = activeNavId === it.id;
          const onClick = it._kind === 'section' ? () => setActiveSection(it.id) : () => setActiveTab(it.id);
          return (
            <Button key={it.id} variant="sidebar" active={active} onClick={onClick}
              title={collapsed ? it.label : undefined}
              leftIcon={it.Icon ? <it.Icon size={16} strokeWidth={active ? 2 : 1.7} /> : null}
              style={collapsed ? { padding: 10, justifyContent: 'center', minHeight: 38 } : { minHeight: 38 }}
            >
              {!collapsed && it.label}
            </Button>
          );
        })}
      </nav>

      {/* Footer — <Button variant="sidebar"> */}
      <div style={{ padding: collapsed ? '8px 6px' : S[2], borderTop: `1px solid ${SHADCN.sidebarBorder}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button variant="sidebar" active={panelOpen === 'outils'}
          onClick={() => (onOpenPanel ? onOpenPanel('outils') : setActiveEntity('outils'))} title={collapsed ? 'Outils' : undefined}
          leftIcon={<Wrench size={13} strokeWidth={2.2} />}
          style={collapsed ? { padding: 10, justifyContent: 'center' } : { padding: `${S[2]}px ${S[3]}px` }}
        >
          {!collapsed && 'Outils'}
        </Button>
        <Button variant="sidebar" active={panelOpen === 'settings'}
          onClick={() => (onOpenPanel ? onOpenPanel('settings') : setActiveEntity('settings'))} title={collapsed ? 'Réglages' : undefined}
          leftIcon={<SettingsIcon size={13} strokeWidth={2.2} />}
          style={collapsed ? { padding: 10, justifyContent: 'center' } : { padding: `${S[2]}px ${S[3]}px` }}
        >
          {!collapsed && 'Réglages'}
        </Button>
        {!collapsed && (
          <Button variant="sidebar" onClick={onSync} style={{ padding: `${S[2]}px ${S[3]}px` }}>
            ↻ Synchro
          </Button>
        )}
        <Button variant="sidebar" onClick={onLogout} title={collapsed ? 'Déconnexion' : undefined}
          leftIcon={<X size={13} strokeWidth={2.4} />}
          style={collapsed ? { padding: 10, justifyContent: 'center' } : { padding: `${S[2]}px ${S[3]}px` }}
        >
          {!collapsed && 'Déconnexion'}
        </Button>
      </div>
    </div>
  );
};

// ── Mobile Drawer ─────────────────────────────────────────────────────────────
const MobileDrawer = ({ open, onClose, activeTab, activeSection, sections, activeEntity, entities, setActiveTab, setActiveSection, setActiveEntity, onOpenPanel, panelOpen, onLogout, onSync }) => {
  const [entityOpen, setEntityOpen] = React.useState(false);
  const entityRef = React.useRef(null);
  const closeEntity = React.useCallback(() => setEntityOpen(false), []);
  useDismiss(entityRef, entityOpen, closeEntity);
  const currentEntity = entities.find(e => e.id === activeEntity) || ENTITIES.find(e => e.id === activeEntity);
  const singleEntity = entities.length <= 1;
  const singleSection = sections.length <= 1;
  const navItems = singleSection
    ? (sections[0]?.tabs || []).map(t => ({ ...t, _kind: 'tab' }))
    : sections.map(s => ({ id: s.id, label: s.label, Icon: s.Icon, _kind: 'section' }));
  const activeNavId = singleSection ? activeTab : activeSection?.id;
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: SHADCN.overlay,
        opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
        transition: 'opacity 0.3s ease', zIndex: 300,
      }} />
      <div style={{
        position: 'fixed', top: 0, left: 0,
        width: 280, height: '100vh',
        background: SHADCN.sidebar, borderRight: `1px solid ${SHADCN.sidebarBorder}`,
        color: SHADCN.sidebarFg,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease', zIndex: 301,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 14px 12px', borderBottom: `1px solid ${SHADCN.sidebarBorder}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img src="/logo-white.png" alt="café sonore" style={{ height: 22, width: 'auto', display: 'block' }} />
          <button onClick={onClose} style={{
            background: SHADCN.sidebarAccent, border: `1px solid ${SHADCN.sidebarBorder}`,
            borderRadius: R.md, color: SHADCN.sidebarFg, cursor: 'pointer',
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Entity selector */}
        <div ref={entityRef} style={{ padding: S[2], borderBottom: `1px solid ${SHADCN.sidebarBorder}`, position: 'relative' }}>
          {singleEntity ? (
            <div style={{
              ...TYPO.body, fontWeight: 500,
              display: 'flex', alignItems: 'center', gap: 9,
              width: '100%', padding: '9px 12px', borderRadius: R.md,
              background: SHADCN.sidebarAccent, border: `1px solid ${SHADCN.sidebarBorder}`,
              color: SHADCN.sidebarFg,
            }}>
              {currentEntity && <currentEntity.Icon size={15} strokeWidth={1.8} />}
              {currentEntity?.label}
            </div>
          ) : (
          <button onClick={() => setEntityOpen(o => !o)} style={{
            ...TYPO.body, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '9px 12px', borderRadius: R.md,
            background: SHADCN.sidebarAccent, border: `1px solid ${SHADCN.sidebarBorder}`,
            color: SHADCN.sidebarFg, cursor: 'pointer',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              {currentEntity && <currentEntity.Icon size={15} strokeWidth={1.8} />}
              {currentEntity?.label}
            </span>
            <ChevronDown size={13} color={SHADCN.mutedFg} />
          </button>
          )}
          {!singleEntity && entityOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% - 2px)', left: 8, right: 8, background: SHADCN.cardSolid, border: `1px solid ${SHADCN.sidebarBorder}`, borderRadius: R.md, zIndex: 400, overflow: 'hidden', boxShadow: SHADCN.shadowLg }}>
              {entities.map(e => (
                <button key={e.id} onClick={() => { setActiveEntity(e.id); setEntityOpen(false); onClose(); }} style={{
                  ...TYPO.body,
                  display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '11px 14px',
                  background: e.id === activeEntity ? SHADCN.muted : 'transparent',
                  border: 'none',
                  color: e.id === activeEntity ? SHADCN.fg : SHADCN.mutedFg,
                  fontWeight: e.id === activeEntity ? 600 : 400, cursor: 'pointer', textAlign: 'left',
                }}>
                  <e.Icon size={15} strokeWidth={1.8} />
                  {e.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Nav — sections ou onglets selon le nombre de sections */}
        <nav style={{ flex: 1, padding: S[2], display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
          {navItems.filter(it => it.id !== 'settings').map(it => {
            const active = activeNavId === it.id;
            const onClick = () => {
              if (it._kind === 'section') setActiveSection(it.id);
              else setActiveTab(it.id);
              onClose();
            };
            return (
              <button key={it.id}
                onClick={onClick}
                style={{
                  ...TYPO.body, fontWeight: active ? 500 : 400,
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: active ? SHADCN.sidebarAccent : 'transparent',
                  border: `1px solid ${active ? SHADCN.sidebarBorder : 'transparent'}`,
                  borderRadius: R.md, padding: '11px 14px', minHeight: 46,
                  color: active ? SHADCN.fg : SHADCN.mutedFg,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                }}>
                {it.Icon && <it.Icon size={16} strokeWidth={active ? 2 : 1.7} />}
                {it.label}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: S[2], borderTop: `1px solid ${SHADCN.sidebarBorder}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[
            { id: 'outils',   label: 'Outils',      Icon: Wrench,        onClick: () => { (onOpenPanel ? onOpenPanel('outils') : setActiveEntity('outils')); onClose(); },   active: panelOpen === 'outils' },
            { id: 'settings', label: 'Réglages',    Icon: SettingsIcon,  onClick: () => { (onOpenPanel ? onOpenPanel('settings') : setActiveEntity('settings')); onClose(); }, active: panelOpen === 'settings' },
            { id: 'sync',     label: '↻ Synchro',   Icon: null,          onClick: () => { onSync?.(); onClose(); },                  active: false },
            { id: 'logout',   label: 'Déconnexion', Icon: X,             onClick: () => { onLogout?.(); onClose(); },                active: false },
          ].map(it => (
            <button key={it.id} onClick={it.onClick} style={{
              ...TYPO.body, fontWeight: it.active ? 500 : 400,
              display: 'flex', alignItems: 'center', gap: 10,
              background: it.active ? SHADCN.sidebarAccent : 'transparent',
              border: `1px solid ${it.active ? SHADCN.sidebarBorder : 'transparent'}`,
              borderRadius: R.md, padding: '11px 14px', minHeight: 46,
              color: it.active ? SHADCN.fg : SHADCN.mutedFg,
              cursor: 'pointer', textAlign: 'left', width: '100%',
            }}>
              {it.Icon && <it.Icon size={16} strokeWidth={it.active ? 2 : 1.7} />}
              {it.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// ── TopBar ────────────────────────────────────────────────────────────────────
// Barre supérieure compacte : onglets style navigateur collés en haut quand
// l'entité a plusieurs sections (multi-onglets). Sur mobile, le hamburger
// reste accessible en début de ligne. Le cog Réglages a été retiré (l'accès
// passe par le dropdown d'entité).
//
// Sur mobile, les sections n'ont pas de sidebar : on expose un sélecteur de
// section directement dans la TopBar (ligne 1) pour éviter d'ouvrir le drawer
// à chaque changement de section ; les onglets de la section active passent
// alors sur une 2ᵉ ligne. Toute rangée scrollable porte un dégradé de bord
// (ScrollFadeRow) qui signale le contenu hors écran.
const TopBarPills = ({ items, activeId, onPick }) => (
  <ScrollFadeRow innerStyle={{ padding: '6px 0' }}>
    {items.map(t => {
      const active = activeId === t.id;
      return (
        <Button key={t.id} variant="sidebar" active={active}
          onClick={() => onPick(t.id)}
          leftIcon={t.Icon ? <t.Icon size={14} strokeWidth={active ? 2 : 1.7} /> : null}
          style={{ padding: `7px ${S[3]}px`, minHeight: 34, width: 'auto', flexShrink: 0 }}
        >
          {t.label}
        </Button>
      );
    })}
  </ScrollFadeRow>
);

const TopBar = ({ isMobile, onMenuClick, onClose, activeTab, activeSection, sections = [], setActiveTab, setActiveSection, role }) => {
  const sectionTabs = activeSection?.tabs || [];
  const showSectionBar = isMobile && sections.length > 1;
  // Le top bar ne répète pas ce que la sidebar montre déjà : quand l'entité n'a
  // qu'une section (Bureau, SCI, Perso), la sidebar liste directement ses
  // onglets — les réafficher ici faisait doublon. Sur mobile la sidebar est un
  // drawer fermé : le top bar y reste la seule navigation visible.
  const showTabBar     = sectionTabs.length > 1 && (isMobile || sections.length > 1);
  const padX = isMobile ? S[3] : S[4];

  const menuButton = isMobile && onClose ? (
    <button onClick={onClose} aria-label="Retour" style={{ background: 'none', border: 'none', color: SHADCN.mutedFg, cursor: 'pointer', padding: S[1], display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
      <ChevronLeft size={20} />
    </button>
  ) : isMobile && !onClose ? (
    <button onClick={onMenuClick} aria-label="Menu" style={{ background: 'none', border: 'none', color: SHADCN.mutedFg, cursor: 'pointer', padding: S[1], display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
      <Menu size={20} />
    </button>
  ) : null;

  return (
    <div style={{
      position: 'sticky', top: 0,
      background: SHADCN.bg,
      borderBottom: `1px solid ${SHADCN.border}`,
      paddingTop: 'env(safe-area-inset-top)',
      zIndex: 100,
    }}>
      {/* Ligne 1 — menu + (sections sur mobile / onglets sur desktop) + thème */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: padX, paddingRight: padX, minHeight: isMobile ? 52 : 56 }}>
        {menuButton}
        {showSectionBar ? (
          <TopBarPills items={sections} activeId={activeSection?.id} onPick={setActiveSection} />
        ) : showTabBar ? (
          <TopBarPills items={sectionTabs} activeId={activeTab} onPick={setActiveTab} />
        ) : (
          <div style={{ flex: 1 }} />
        )}
        <WeatherBadge />
        <NotifBell role={role} isMobile={isMobile} />
        <ThemeToggle size="sm" />
      </div>
      {/* Ligne 2 — onglets de la section active (mobile multi-section seulement) */}
      {showSectionBar && showTabBar && (
        <div style={{ display: 'flex', alignItems: 'center', paddingLeft: padX, paddingRight: padX, paddingBottom: 2 }}>
          <TopBarPills items={sectionTabs} activeId={activeTab} onPick={setActiveTab} />
        </div>
      )}
    </div>
  );
};

// ── MetricCard ────────────────────────────────────────────────────────────────
// Tinted via softTone() quand `highlight` est passé — sinon surface neutre.
const MetricCard = ({ label, value, sub, color, highlight }) => {
  const t = highlight ? softTone(highlight) : null;
  return (
    <div style={{
      background: t ? t.bg : SHADCN.bg,
      border: `1px solid ${t ? t.border : SHADCN.border}`,
      borderRadius: R.md, padding: `${S[4]}px ${S[4]}px`,
      display: 'flex', flexDirection: 'column', gap: S[1],
    }}>
      <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>{label}</div>
      <div style={{ ...TYPO.metric, color: color || SHADCN.fg }}>
        {typeof value === 'number' ? fmtFull(value) : value}
      </div>
      {sub && <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{sub}</div>}
    </div>
  );
};

// ── AlertItem ─────────────────────────────────────────────────────────────────
// Dérive ses couleurs depuis softTone() (DS) — plus de rgba en dur.
const AlertItem = ({ alerte }) => {
  const baseColor = { rouge: SHADCN.error, orange: SHADCN.warning, vert: SHADCN.success }[alerte.niveau];
  const c = softTone(baseColor);
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: R.md, padding: `${S[3]}px ${S[4]}px`, display: 'flex', gap: S[3], alignItems: 'flex-start' }}>
      <div style={{ width: 7, height: 7, borderRadius: R.pill, background: c.text, flexShrink: 0, marginTop: 6 }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: S[2], marginBottom: S[1], alignItems: 'center' }}>
          <span style={{ ...TYPO.caption, fontWeight: 500, color: c.text }}>{alerte.qui}</span>
          <span style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>— {alerte.date}</span>
        </div>
        <div style={{ ...TYPO.body, color: SHADCN.fg }}>{alerte.texte}</div>
      </div>
    </div>
  );
};


// Header standard des Cards : titre h2 + slot droit optionnel (pill, action…).
// Le border-bottom est porté par le conteneur → toujours pleine largeur,
// que la carte ait un slot droit ou non.
// SectionTitle partagé — voir components/SectionTitle.jsx

// ── InfoRows — liste « libellé / valeur » (fiche d'identité) ────────────────
// Surfaces neutres, valeur en fg ; valeur vide → placeholder discret muté.
// `hint` optionnel → note grise sous la valeur (ex. « à confirmer »).
const InfoRows = ({ rows }) => {
  const [copied, setCopied] = React.useState(null);
  const items = (rows || []).filter(Boolean);
  const handleCopy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {items.map((row, i) => (
        <div key={row.label} style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: S[4],
          padding: '12px 0',
          borderBottom: i < items.length - 1 ? `1px solid ${SHADCN.border}` : 'none',
        }}>
          <div style={{ ...TYPO.body, color: SHADCN.mutedFg, flexShrink: 0 }}>{row.label}</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 0 }}>
            {row.value ? (
              <div
                onClick={() => handleCopy(row.label, row.value)}
                title="Copier"
                style={{
                  ...TYPO.body, ...(row.num ? TYPO.num : null), fontWeight: 500,
                  color: copied === row.label ? SHADCN.success : SHADCN.fg,
                  textAlign: 'right', wordBreak: 'break-word',
                  cursor: 'copy', transition: 'color 0.2s',
                }}
              >
                {copied === row.label ? '✓ Copié' : row.value}
              </div>
            ) : (
              <div style={{ ...TYPO.body, color: SHADCN.mutedFg, opacity: 0.55, fontStyle: 'italic', textAlign: 'right' }}>
                À compléter
              </div>
            )}
            {row.hint && (
              <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, opacity: 0.7, textAlign: 'right' }}>{row.hint}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── DocDownload — lien de téléchargement d'un document légal (RIB, Kbis) ────
// PDF servis par la route protégée /api/supabase-data?resource=doc.
const DocDownload = ({ id, label }) => (
  <Button
    as="a"
    href={`/api/supabase-data?resource=doc&id=${id}`}
    download
    size="sm"
    variant="ghost"
    leftIcon={<Download size={14} />}
  >
    {label}
  </Button>
);

// Card et PageHeader sont importés depuis ./components/* (light theme partagé).

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: SHADCN.bg, border: `1px solid ${SHADCN.border}`, borderRadius: R.md, padding: `10px ${S[4]}px`, boxShadow: SHADCN.shadowMd }}>
      <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ ...TYPO.body, ...TYPO.num, display: 'flex', gap: S[2], alignItems: 'center', color: p.color }}>
          <span style={{ width: 8, height: 8, borderRadius: R.xs, background: p.color, display: 'inline-block', flexShrink: 0 }} />
          {p.name}: <strong>{fmtFull(p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

// ── ArticlesCatalogue — liste filtrable du catalogue Square ──────────────────
const ArticlesCatalogue = ({ items, loading, taxesUsed }) => {
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [tauxFilter, setTauxFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState(''); // '', 'ok', 'anomalie'
  const [stockFilter, setStockFilter] = useState(''); // '', 'en-stock', 'epuise'

  const categories = useMemo(() => items ? [...new Set(items.map(i => i.category))].sort() : [], [items]);
  const tauxDisponibles = useMemo(() => items ? [...new Set(items.flatMap(i => i.appliedPercentages))].sort((a, b) => a - b) : [], [items]);

  if (loading && !items) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: S[5] }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: S[2] }}>
          <Skeleton h={12} w={180} />
          <Skeleton h={28} w={120} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: S[2] }}>
          {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} h={38} />)}
        </div>
        <Skeleton h={44} lines={8} gap={1} radius={R.md} />
      </div>
    );
  }
  if (!items) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Catalogue indisponible"
        hint="Les données Square n'ont pas pu être chargées. Recharge la page pour réessayer."
        action={<Button variant="ghost" onClick={() => window.location.reload()}>Recharger</Button>}
      />
    );
  }

  const qLower = q.trim().toLowerCase();
  const filtered = items.filter(it => {
    if (qLower && !(it.name || '').toLowerCase().includes(qLower)) return false;
    if (catFilter && it.category !== catFilter) return false;
    if (tauxFilter === 'none') {
      if (it.appliedPercentages.length) return false;
    } else if (tauxFilter !== '') {
      const t = parseFloat(tauxFilter);
      if (!it.appliedPercentages.some(p => Math.abs(p - t) < 0.01)) return false;
    }
    if (statutFilter === 'ok' && it.anomaly) return false;
    if (statutFilter === 'anomalie' && !it.anomaly) return false;
    if (stockFilter === 'en-stock' && !(it.stock > 0)) return false;
    if (stockFilter === 'epuise' && !(it.stock === 0)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (!!a.anomaly !== !!b.anomaly) return a.anomaly ? -1 : 1;
    return (a.name || '').localeCompare(b.name || '');
  });

  const resetAll = () => { setQ(''); setCatFilter(''); setTauxFilter(''); setStatutFilter(''); setStockFilter(''); };
  const hasFilter = q || catFilter || tauxFilter || statutFilter || stockFilter;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: S[5] }}>
      <div>
        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[1] }}>Catalogue Square — Café Sonore</div>
        <div style={{ ...TYPO.h2, color: SHADCN.fg }}>
          {sorted.length} {sorted.length > 1 ? 'articles' : 'article'}
          {hasFilter ? ` sur ${items.length}` : ''}
        </div>
        <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1] }}>
          Filtre par catégorie, taux ou conformité pour vérifier les TVA appliquées.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: S[2] }}>
        <Input
          type="text" placeholder="Rechercher un article…"
          value={q} onChange={e => setQ(e.target.value)}
        />
        <Input as="select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">Toutes les catégories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </Input>
        <Input as="select" value={tauxFilter} onChange={e => setTauxFilter(e.target.value)}>
          <option value="">Tous les taux</option>
          {tauxDisponibles.map(t => <option key={t} value={t}>{t}%</option>)}
          <option value="none">Sans taux</option>
        </Input>
        <Input as="select" value={statutFilter} onChange={e => setStatutFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="ok">Conformes</option>
          <option value="anomalie">Anomalies</option>
        </Input>
        <Input as="select" value={stockFilter} onChange={e => setStockFilter(e.target.value)}>
          <option value="">Tout le stock</option>
          <option value="en-stock">En stock (&gt;0)</option>
          <option value="epuise">Épuisé (= 0)</option>
        </Input>
      </div>

      {hasFilter && (
        <div>
          <button type="button" onClick={resetAll} data-sonore-btn style={{
            ...TYPO.caption, fontWeight: 500, background: 'transparent', color: SHADCN.fg,
            border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline',
            textUnderlineOffset: 3, textDecorationColor: SHADCN.border,
          }}>
            Réinitialiser les filtres
          </button>
        </div>
      )}

      <ArticleTable
        rows={sorted}
        max={500}
        moreLabel={n => `+ ${n} autres (affine les filtres)`}
        emptyLabel="Aucun article ne correspond."
        columns={[
          { key: 'name', label: 'Article', render: a => a.name || '(sans nom)' },
          { key: 'category', label: 'Catégorie', nowrap: true, cellStyle: { color: SHADCN.mutedFg }, render: a => a.category },
          {
            key: 'stock', label: 'Stock', align: 'right', nowrap: true,
            cellStyle: a => ({ ...TYPO.num, color: a.stock === null ? SHADCN.mutedFg : a.stock > 0 ? SHADCN.success : SHADCN.error }),
            render: a => a.stock === null ? '—' : a.stock,
          },
          {
            key: 'applique', label: 'TVA appliquée', nowrap: true, cellStyle: TYPO.num,
            render: a => a.appliedPercentages.length ? a.appliedPercentages.map(p => `${p}%`).join(' + ') : '—',
          },
          {
            key: 'attendu', label: 'Attendu', nowrap: true, cellStyle: { ...TYPO.num, color: SHADCN.mutedFg },
            render: a => a.expectedPercentage !== null && a.expectedPercentage !== undefined ? `${a.expectedPercentage}%` : '—',
          },
          {
            key: 'statut', label: 'Statut', nowrap: true,
            render: a => a.anomaly
              ? <span title={a.detail}><StatusPill variant="error" size="sm">Anomalie</StatusPill></span>
              : <StatusPill variant="success" size="sm">Conforme</StatusPill>,
          },
          { key: 'square', label: 'Square', nowrap: true, render: a => <SquareItemLink itemId={a.itemId} /> },
        ]}
      />

      {taxesUsed?.length > 0 && (
        <div>
          <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: 6 }}>Taux définis dans Square</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {taxesUsed.map(t => (
              <span key={t.id} style={{
                ...TYPO.caption, ...TYPO.num,
                background: t.enabled ? SHADCN.bg : SHADCN.bg,
                border: `1px solid ${SHADCN.border}`,
                color: t.enabled ? SHADCN.fg : SHADCN.mutedFg,
                padding: '4px 8px', borderRadius: R.sm, whiteSpace: 'nowrap',
              }}>
                {t.name} — {t.percentage}%{!t.enabled && ' (désactivée)'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── PersonnelColors — panneau réglages couleurs équipe (admin) ───────────────
function PersonnelColors({ onSave }) {
  const [draft, setDraft] = useState(() => {
    const saved = getMemberColorOverrides();
    const out = {};
    MEMBRES_CONNUS.forEach(m => { out[m.id] = saved[m.id] ?? m.defaultColor; });
    return out;
  });
  const [saved, setSaved] = useState(false);

  const handleSaveColors = () => {
    saveMemberColorOverrides(draft);
    onSave(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  return (
    <Card>
      <SectionTitle>Couleurs du personnel</SectionTitle>
      <p style={{ ...TYPO.body, color: SHADCN.mutedFg, marginBottom: 20, marginTop: -4 }}>
        Personnalisez la couleur de chaque membre de l'équipe — utilisée dans les avatars et la vue du jour.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {MEMBRES_CONNUS.map((m, i, arr) => {
          const color = draft[m.id];
          const fg = texteSurFond(color);
          const isLast = i === arr.length - 1;
          return (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 0',
              borderBottom: isLast ? 'none' : `1px solid ${SHADCN.border}`,
            }}>
              {/* Avatar preview live */}
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: color, color: fg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: SHADCN.font, fontWeight: 600, fontSize: FS.label,
                flexShrink: 0, userSelect: 'none', transition: 'background 0.1s',
              }}>
                {m.prenom.charAt(0).toUpperCase()}
              </div>
              {/* Nom */}
              <div style={{ ...TYPO.body, fontWeight: 500, color: SHADCN.fg, minWidth: 88 }}>
                {m.prenom}
              </div>
              {/* Color swatch cliquable */}
              <label style={{ display: 'flex', alignItems: 'center', gap: S[2], cursor: 'pointer', flex: 1 }}>
                <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: R.md, background: color, border: `2px solid ${SHADCN.border}` }} />
                  <input
                    type="color"
                    value={color}
                    onChange={e => setDraft(d => ({ ...d, [m.id]: e.target.value }))}
                    style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                  />
                </div>
                <span style={{ fontFamily: 'monospace', fontSize: FS.caption, color: SHADCN.mutedFg, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {color}
                </span>
              </label>
              {/* Reset si modifié */}
              {draft[m.id] !== m.defaultColor && (
                <button
                  onClick={() => setDraft(d => ({ ...d, [m.id]: m.defaultColor }))}
                  style={{ ...TYPO.caption, background: 'transparent', border: 'none', color: SHADCN.mutedFg, cursor: 'pointer', padding: `${S[1]}px ${S[2]}px`, borderRadius: R.xs, textDecoration: 'underline', flexShrink: 0 }}
                  title={`Couleur par défaut : ${m.defaultColor}`}
                >
                  Réinitialiser
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: S[3], marginTop: 20 }}>
        <button
          onClick={handleSaveColors}
          style={{ ...TYPO.body, fontWeight: 700, fontSize: FS.body, background: SHADCN.fg, color: SHADCN.bg, border: 'none', borderRadius: R.md, padding: `10px ${S[5]}px`, cursor: 'pointer', transition: 'opacity 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          Enregistrer
        </button>
        {saved && (
          <span style={{ ...TYPO.caption, color: SHADCN.success, display: 'flex', alignItems: 'center', gap: 5 }}>
            <CheckCircle size={13} /> Couleurs enregistrées
          </span>
        )}
      </div>
    </Card>
  );
}

// ── <AvatarCropModal> ─────────────────────────────────────────────────────
function AvatarCropModal({ src, onCancel, onConfirm }) {
  const FRAME = 320;
  const OUT = 512;
  const imgElRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [minScale, setMinScale] = useState(1);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const m = Math.max(FRAME / img.width, FRAME / img.height);
      setNat({ w: img.width, h: img.height });
      setMinScale(m);
      setScale(m);
      setOffset({ x: 0, y: 0 });
      imgElRef.current = img;
    };
    img.src = src;
  }, [src]);

  const clamp = (s, o) => {
    const dispW = nat.w * s, dispH = nat.h * s;
    const maxX = Math.max(0, (dispW - FRAME) / 2);
    const maxY = Math.max(0, (dispH - FRAME) / 2);
    return { x: Math.max(-maxX, Math.min(maxX, o.x)), y: Math.max(-maxY, Math.min(maxY, o.y)) };
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    setOffset(clamp(scale, { x: dragRef.current.ox + dx, y: dragRef.current.oy + dy }));
  };
  const onPointerUp = () => { dragRef.current = null; };
  const onScale = (v) => {
    const s = Number(v);
    setScale(s);
    setOffset((o) => clamp(s, o));
  };

  const confirm = () => {
    if (!imgElRef.current || !nat.w) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUT; canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    const srcSize = FRAME / scale;
    const sx = nat.w / 2 - offset.x / scale - srcSize / 2;
    const sy = nat.h / 2 - offset.y / scale - srcSize / 2;
    ctx.drawImage(imgElRef.current, sx, sy, srcSize, srcSize, 0, 0, OUT, OUT);
    onConfirm(canvas.toDataURL('image/jpeg', 0.9));
  };

  return (
    <div onClick={onCancel} style={{ position:'fixed', inset:0, background:SHADCN.overlayStrong, display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding: S[4] }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: SHADCN.card, border:`1px solid ${SHADCN.border}`, borderRadius: R.lg, padding: S[5], display:'flex', flexDirection:'column', gap: S[4], maxWidth:'90vw' }}>
        <div style={{ ...TYPO.h2, color: SHADCN.fg }}>Recadrer la photo</div>
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ position:'relative', width: FRAME, height: FRAME, overflow:'hidden', borderRadius: R.md, background: SHADCN.muted, cursor: dragRef.current ? 'grabbing' : 'grab', touchAction:'none', userSelect:'none' }}
        >
          {nat.w > 0 && (
            <img
              src={src}
              alt=""
              draggable={false}
              style={{
                position:'absolute', left:'50%', top:'50%',
                width: `${nat.w * scale}px`, height: `${nat.h * scale}px`,
                maxWidth:'none', maxHeight:'none',
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                pointerEvents:'none',
              }}
            />
          )}
          <div style={{ position:'absolute', inset:0, pointerEvents:'none', borderRadius:'50%', boxShadow:`0 0 0 9999px rgba(0,0,0,0.55)` }} />
          <div style={{ position:'absolute', inset:0, pointerEvents:'none', borderRadius:'50%', border:`1px dashed rgba(255,255,255,0.65)` }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: S[3] }}>
          <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, minWidth: 36 }}>Zoom</span>
          <input
            type="range" min={minScale} max={minScale * 4} step={0.01}
            value={scale} onChange={(e) => onScale(e.target.value)}
            style={{ flex:1, accentColor: SHADCN.fg }}
          />
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap: S[3] }}>
          <button onClick={onCancel} style={{ ...TYPO.body, background:'transparent', color: SHADCN.fg, border:`1px solid ${SHADCN.border}`, borderRadius: R.md, padding:'8px 14px', cursor:'pointer' }}>Annuler</button>
          <button onClick={confirm} style={{ ...TYPO.body, background: SHADCN.fg, color: SHADCN.bg, border:'none', borderRadius: R.md, padding:'8px 14px', cursor:'pointer' }}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

// ── <ProfilePanel> ────────────────────────────────────────────────────────
function ProfilePanel({ role, session, onLogout, onSessionRefresh }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null);

  const isMember = (role === 'membre' || role === 'bureau_membre') && session?.id;
  const canEditProfile = !!session?.id;
  const photoUrl = session?.photo_url || null;
  const prenom   = session?.prenom || (role === 'admin' ? 'Corentin Fardeau' : role === 'cafe_admin' ? 'Admin Café' : role === 'cafe_planning' ? 'Planning' : 'Membre');

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadError(null);
    const reader = new FileReader();
    reader.onerror = () => setUploadError('Lecture impossible');
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const uploadCropped = async (dataUrl) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const r = await fetch('/api/update-photo', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUrl }),
      });
      if (!r.ok) {
        const { error } = await r.json().catch(() => ({}));
        throw new Error(error || 'Upload échoué');
      }
      if (onSessionRefresh) await onSessionRefresh();
      try {
        const ur = await fetch('/api/users', { credentials: 'same-origin' });
        if (ur.ok) {
          const { users } = await ur.json();
          const map = {};
          for (const u of (users || [])) {
            if (u.square_member_id && u.photo_url) map[u.square_member_id] = u.photo_url;
          }
          setMemberPhotos(map);
        }
      } catch (_) {}
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handlePwd = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) { setPwdMsg({ err: 'Mot de passe trop court (6 caractères min)' }); return; }
    setPwdSaving(true);
    try {
      const r = await fetch('/api/update-password', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwd }),
      });
      if (!r.ok) {
        const { error } = await r.json().catch(() => ({}));
        throw new Error(error || 'Échec');
      }
      setPwd(''); setPwdMsg({ ok: 'Mot de passe modifié' });
    } catch (err) {
      setPwdMsg({ err: err.message });
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <>
      {cropSrc && (
        <AvatarCropModal
          src={cropSrc}
          onCancel={() => { setCropSrc(null); if (fileRef.current) fileRef.current.value = ''; }}
          onConfirm={uploadCropped}
        />
      )}
      <Card>
        <SectionTitle>Profil</SectionTitle>
        <div style={{ display:'flex', flexDirection:'column', gap: S[4] }}>
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 0', borderBottom:`1px solid ${SHADCN.border}` }}>
            {photoUrl ? (
              <div style={{ width:64, height:64, borderRadius:'50%', backgroundImage:`url(${photoUrl})`, backgroundSize:'cover', backgroundPosition:'center', border:`1px solid ${SHADCN.border}` }} />
            ) : (
              <div style={{ width:64, height:64, borderRadius:'50%', background:SHADCN.muted, border:`1px solid ${SHADCN.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <User size={26} color={SHADCN.mutedFg} strokeWidth={1.5} />
              </div>
            )}
            <div>
              <div style={{ ...TYPO.h2, color:SHADCN.fg }}>{prenom}</div>
              <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop:3 }}>
                {role === 'admin' ? 'Administrateur · Gérant Groupe Sonore'
                  : role === 'cafe_admin' ? (session?.login ? `@${session.login} · Admin Café Sonore` : 'Admin Café Sonore')
                  : role === 'comptable' ? (session?.login ? `@${session.login} · Comptable` : 'Comptable')
                  : role === 'bureau_membre' ? (session?.login ? `@${session.login} · Café + Prospection Bureau` : 'Café + Prospection Bureau')
                  : role === 'cafe_planning' ? (session?.login ? `@${session.login} · Planning du Café` : 'Planning du Café')
                  : session?.login ? `@${session.login} · Accès Café Sonore` : 'Accès Café Sonore'}
              </div>
            </div>
            {(() => {
              const tone = softTone(role === 'admin' ? SHADCN.info : role === 'cafe_admin' ? SHADCN.warning : role === 'comptable' ? SHADCN.mutedFg : SHADCN.success);
              return (
                <div style={{ ...TYPO.eyebrow, fontWeight:500, marginLeft:'auto', background: tone.bg, border:`1px solid ${tone.border}`, borderRadius: R.xs, padding:'4px 10px', color: tone.text }}>
                  {role === 'admin' ? 'Super-Admin' : role === 'cafe_admin' ? 'Admin Café' : role === 'comptable' ? 'Comptable' : role === 'bureau_membre' ? 'Membre + Bureau' : role === 'cafe_planning' ? 'Planning' : 'Membre'}
                </div>
              );
            })()}
          </div>

          {canEditProfile && (
            <div style={{ display:'flex', flexDirection:'column', gap: S[3] }}>
              <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg }}>Photo de profil</div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleFile} style={{ display:'none' }} />
              <div style={{ display:'flex', alignItems:'center', gap: S[3] }}>
                <button onClick={() => fileRef.current?.click()} disabled={uploading} style={{ ...TYPO.body, background:SHADCN.fg, color:SHADCN.bg, border:'none', borderRadius:R.md, padding:'8px 14px', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
                  {uploading ? 'Envoi…' : (photoUrl ? 'Changer la photo' : 'Ajouter une photo')}
                </button>
                {uploadError && <span style={{ ...TYPO.caption, color: SHADCN.error }}>{uploadError}</span>}
              </div>
            </div>
          )}

          {canEditProfile && (
            <form onSubmit={handlePwd} autoComplete="off" className="sonore-pwd-form" style={{ display:'flex', flexDirection:'column', gap: S[3], borderTop:`1px solid ${SHADCN.border}`, paddingTop: S[4] }}>
              <style>{`
                .sonore-pwd-form input { color: ${SHADCN.fg} !important; color-scheme: dark; caret-color: ${SHADCN.fg}; }
                .sonore-pwd-form input:-webkit-autofill { -webkit-box-shadow: 0 0 0 1000px ${SHADCN.bg} inset !important; -webkit-text-fill-color: ${SHADCN.fg} !important; }
              `}</style>
              <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg }}>Changer mot de passe</div>
              <div style={{ display:'flex', alignItems:'center', gap: S[3] }}>
                <input type="password" name="new-password" autoComplete="new-password" value={pwd} onChange={(e) => { setPwd(e.target.value); setPwdMsg(null); }} placeholder="Nouveau mot de passe" style={{ ...TYPO.body, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, color:SHADCN.fg, borderRadius:R.md, minWidth: 240 }} />
                <button type="submit" disabled={pwdSaving || !pwd} style={{ ...TYPO.body, background:SHADCN.fg, color:SHADCN.bg, border:'none', borderRadius:R.md, padding:'8px 14px', cursor: (pwdSaving || !pwd) ? 'not-allowed' : 'pointer', opacity: (pwdSaving || !pwd) ? 0.6 : 1 }}>
                  {pwdSaving ? '…' : 'Enregistrer'}
                </button>
                {pwdMsg?.ok && <span style={{ ...TYPO.caption, color: SHADCN.success }}>{pwdMsg.ok}</span>}
                {pwdMsg?.err && <span style={{ ...TYPO.caption, color: SHADCN.error }}>{pwdMsg.err}</span>}
              </div>
            </form>
          )}

          <button onClick={onLogout} style={{ ...TYPO.body, display:'flex', alignItems:'center', gap:9, background:'transparent', border:`1px solid ${SHADCN.border}`, borderRadius: R.md, padding:'10px 14px', color:SHADCN.error, cursor:'pointer', width:'fit-content', marginTop: S[2], transition:`background ${SHADCN.durBase} ${SHADCN.easeOut}, color ${SHADCN.durBase} ${SHADCN.easeOut}, border-color ${SHADCN.durBase} ${SHADCN.easeOut}, transform 90ms ${SHADCN.easeOut}` }}>
            <X size={13} strokeWidth={2.5} /> Déconnexion
          </button>
        </div>
      </Card>
    </>
  );
}

// ── <MembersAdminPanel> ───────────────────────────────────────────────────
function MembersAdminPanel({ currentRole = 'admin' }) {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ login: '', prenom: '', password: '', square_member_id: '', role: 'membre' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await fetch('/api/users', { credentials: 'same-origin' });
      if (!r.ok) throw new Error('Chargement impossible');
      const { users } = await r.json();
      setUsers(users || []);
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const r = await fetch('/api/signup', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const { error } = await r.json().catch(() => ({}));
        throw new Error(error || 'Création échouée');
      }
      setForm({ login: '', prenom: '', password: '', square_member_id: '', role: 'membre' });
      setShowForm(false);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, prenom) => {
    if (!window.confirm(`Supprimer le compte de ${prenom} ?`)) return;
    try {
      const r = await fetch(`/api/users?id=${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'same-origin' });
      if (!r.ok) throw new Error('Suppression échouée');
      await load();
    } catch (e) { setErr(e.message); }
  };

  return (
    <Card style={{ marginTop: S[4] }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: S[3] }}>
        <SectionTitle style={{ marginBottom: 0 }}>Membres</SectionTitle>
        <button onClick={() => setShowForm(s => !s)} style={{ ...TYPO.body, fontSize: FS.caption, background: showForm ? 'transparent' : SHADCN.fg, color: showForm ? SHADCN.fg : SHADCN.bg, border: `1px solid ${SHADCN.fg}`, borderRadius: R.md, padding: '6px 12px', cursor:'pointer' }}>
          {showForm ? 'Annuler' : '+ Ajouter un membre'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} autoComplete="off" className="sonore-members-form" style={{ display:'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: S[2], padding: S[3], marginBottom: S[3], border:`1px dashed ${SHADCN.border}`, background: SHADCN.muted }}>
          <style>{`
            .sonore-members-form input, .sonore-members-form select { color: ${SHADCN.fg} !important; color-scheme: dark; caret-color: ${SHADCN.fg}; }
            .sonore-members-form input::placeholder { color: ${SHADCN.mutedFg}; opacity: 1; }
            .sonore-members-form input:-webkit-autofill,
            .sonore-members-form input:-webkit-autofill:focus,
            .sonore-members-form input:-webkit-autofill:hover {
              -webkit-box-shadow: 0 0 0 1000px ${SHADCN.bg} inset !important;
              -webkit-text-fill-color: ${SHADCN.fg} !important;
              caret-color: ${SHADCN.fg} !important;
              transition: background-color 9999s ease-in-out 0s;
            }
          `}</style>
          <input required name="new-login" autoComplete="off" placeholder="Identifiant (ex: baptiste)" value={form.login} onChange={e => setForm(f => ({ ...f, login: e.target.value }))} style={{ ...TYPO.body, color: SHADCN.fg, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, borderRadius:R.md }} />
          <input required name="new-prenom" autoComplete="off" placeholder="Prénom" value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} style={{ ...TYPO.body, color: SHADCN.fg, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, borderRadius:R.md }} />
          <input required type="password" name="new-password" autoComplete="new-password" placeholder="Mot de passe initial" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={{ ...TYPO.body, color: SHADCN.fg, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, borderRadius:R.md }} />
          <select value={form.square_member_id} onChange={e => setForm(f => ({ ...f, square_member_id: e.target.value }))} style={{ ...TYPO.body, color: SHADCN.fg, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, borderRadius:R.md }}>
            <option value="">— Lier au planning Square —</option>
            {MEMBRES_CONNUS.map(m => <option key={m.id} value={m.id}>{m.prenom}</option>)}
          </select>
          {currentRole === 'admin' && (
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={{ ...TYPO.body, color: SHADCN.fg, padding:'8px 12px', border:`1px solid ${SHADCN.border}`, background:SHADCN.bg, borderRadius:R.md }}>
              <option value="membre">Membre (accès café)</option>
              <option value="cafe_admin">Admin Café (accès complet café)</option>
              <option value="cafe_planning">Planning (emploi du temps seulement)</option>
              <option value="comptable">Comptable (lecture rapports uniquement)</option>
              <option value="bureau_membre">Membre Café + Prospection Bureau Sonore</option>
            </select>
          )}
          <button type="submit" disabled={saving} style={{ ...TYPO.body, background:SHADCN.fg, color:SHADCN.bg, border:'none', borderRadius:R.md, padding:'8px 14px', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1, gridColumn: '1 / -1' }}>
            {saving ? 'Création…' : 'Créer le compte'}
          </button>
        </form>
      )}

      {err && <div style={{ ...TYPO.caption, color: SHADCN.error, marginBottom: S[2] }}>{err}</div>}

      {users === null ? (
        <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>Chargement…</div>
      ) : users.length === 0 ? (
        <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>Aucun membre enregistré pour l'instant.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap: 0 }}>
          {users.map((u, i) => (
            <div key={u.id} style={{ display:'flex', alignItems:'center', gap: S[3], padding:'10px 0', borderBottom: i < users.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
              <Avatar prenom={u.prenom} memberId={u.square_member_id} photoUrl={u.photo_url} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...TYPO.body, fontWeight: 500, color: SHADCN.fg }}>{u.prenom}</div>
                <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>@{u.login}{u.role === 'cafe_admin' ? ' · Admin Café' : u.role === 'comptable' ? ' · Comptable' : u.role === 'bureau_membre' ? ' · Café + Bureau' : u.role === 'cafe_planning' ? ' · Planning' : ''}{u.square_member_id ? ' · lié au planning' : ''}</div>
              </div>
              <button onClick={() => handleDelete(u.id, u.prenom)} title="Supprimer" style={{ ...TYPO.caption, background:'transparent', border:`1px solid ${SHADCN.border}`, color: SHADCN.error, borderRadius: R.md, padding:'4px 10px', cursor:'pointer' }}>
                Supprimer
              </button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Helpers purs extraits du render (stable identity, pas de closure) ─────────
const _ht20 = v => v / (1 + TVA_RETAIL);
const GROUP_COLORS = { Café: SHADCN.mutedFg, Disquaire: SHADCN.chart2, Retail: SHADCN.chart1, Services: SHADCN.success };
// Sandwichs préparés chaque jour au Café (production fixe) — base du ratio « vendus / préparés » de l'onglet Aujourd'hui
const SANDWICHS_PAR_JOUR = 4;

function matchCharge(charge, transactions) {
  return transactions.some(tx => {
    if (tx.montant >= 0 || tx.categorie === 'Flux interne') return false;
    const lib = (tx.libelle || '').toUpperCase();
    // URSSAF, Klesia et les 2 prélèvements ALAN partagent la catégorie « Charges sociales » :
    // ces charges se départagent au libellé, sinon le 1er prélèvement ALAN (le 10) cochait l'URSSAF (le 21).
    if (charge.match) return charge.match(lib);
    if (tx.categorie && tx.categorie === charge.categorie) return true;
    const txAmt = Math.abs(tx.montant);
    if (charge.montant === 0) return false;
    // Pas de match au montant seul : n'importe quel achat à ±3 % (40 € pour Orange, 78 € pour Verisure) cochait la charge.
    const ratio = Math.abs(txAmt - charge.montant) / charge.montant;
    return charge.keywords.some(kw => lib.includes(kw)) && ratio <= 0.25;
  });
}

const isFood   = tx => tx.categorie === 'Fournisseurs'
  || (!tx.categorie && KEYWORDS_FOOD.some(kw => (tx.libelle || '').toUpperCase().includes(kw)));
const isRetail = tx => tx.categorie === 'Retail'
  || (!tx.categorie && KEYWORDS_RETAIL.some(kw => (tx.libelle || '').toUpperCase().includes(kw)));

// ── Nouveautés ────────────────────────────────────────────────────────────────
// Popup affichée une seule fois par WHATS_NEW_ID (localStorage sonore_whatsnew_seen).
// ⚠️ Ne bumper WHATS_NEW_ID que pour de VRAIES nouvelles fonctionnalités visibles
// par l'utilisateur — jamais pour un fix, un refactor ou un ajustement de style.
// Sinon la popup perd tout son sens et devient du bruit à chaque deploy.
const WHATS_NEW_ID = '2026-07-23';
const WHATS_NEW = [
  {
    Icon: ListChecks,
    title: 'Entité Perso — vue Focus',
    desc: 'Tes tâches perso ont quitté Notion : une entité dédiée avec la vue Focus du jour.',
    roles: ['admin'],
  },
  {
    Icon: Building2,
    title: 'SCI — Investissement immo',
    desc: 'Nouvel onglet : prêt, equity, loyers et cash-flow de l\'immeuble en un coup d\'œil.',
    roles: ['admin'],
  },
  {
    Icon: Upload,
    title: 'Import BNP par entité',
    desc: 'Chaque entité a son onglet Import — plus besoin de tout passer par le Café.',
    roles: ['admin'],
  },
  {
    Icon: FileText,
    title: 'Rapport mensuel par email',
    desc: 'Le 1er du mois : flash équipe + bilan complet du Café directement dans ta boîte mail.',
    roles: ['admin', 'cafe_admin'],
  },
  {
    Icon: Percent,
    title: 'Rapports — Comptabilité / Monitoring',
    desc: 'Rapports réorganisés en deux volets, avec marge des boissons signatures et suivi de la casse food (3 fournisseurs).',
    roles: ['admin', 'cafe_admin'],
  },
];

function WhatsNewModal({ entries, onClose, isMobile }) {
  const mint = softTone(SHADCN.mint);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: SHADCN.overlay,
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: S[4], animation: 'sonore-fade-in 180ms ease-out',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: SHADCN.cardSolid, border: `1px solid ${SHADCN.border}`,
        borderRadius: R.xl, width: '100%', maxWidth: 460,
        maxHeight: '85vh', overflowY: 'auto',
        boxShadow: SHADCN.shadowXl, animation: 'sk-fade-up 240ms ease-out',
      }}>
        <div style={{ padding: isMobile ? '20px 20px 0' : '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: S[2], marginBottom: 4 }}>
              <Sparkles size={15} strokeWidth={2} style={{ color: SHADCN.mint }} />
              <span style={{ ...TYPO.tag, color: SHADCN.mutedFg }}>Nouveautés</span>
            </div>
            <div style={{ ...TYPO.h2, color: SHADCN.fg }}>Quoi de neuf sur le dashboard</div>
          </div>
          <button onClick={onClose} aria-label="Fermer" style={{
            background: SHADCN.muted, border: `1px solid ${SHADCN.border}`, borderRadius: R.md,
            color: SHADCN.fg, cursor: 'pointer', width: 32, height: 32, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div style={{ padding: isMobile ? '16px 20px' : '20px 28px', display: 'flex', flexDirection: 'column', gap: S[4] }}>
          {entries.map(({ Icon, title, desc }) => (
            <div key={title} style={{ display: 'flex', gap: S[3], alignItems: 'flex-start' }}>
              <div style={{
                width: 34, height: 34, borderRadius: R.md, flexShrink: 0,
                background: mint.bg, border: `1px solid ${mint.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={16} strokeWidth={1.8} style={{ color: SHADCN.mint }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ ...TYPO.titleSm, color: SHADCN.fg }}>{title}</div>
                <div style={{ ...TYPO.body, color: SHADCN.mutedFg }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: isMobile ? '0 20px 20px' : '0 28px 24px' }}>
          <button onClick={onClose} style={{
            ...TYPO.cta, width: '100%', padding: '12px 16px',
            background: SHADCN.primary, color: SHADCN.primaryFg,
            border: 'none', borderRadius: R.md, cursor: 'pointer',
          }}>
            C'est noté
          </button>
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App({ role = 'admin', onLogout, session, onSessionRefresh }) {
  // ── Entités et onglets disponibles selon le rôle ──────────────────────────
  // Rôle inconnu → périmètre membre (jamais admin par défaut).
  // Accès à un module (lib/modules.js) — admin : tout. Garde les rendus d'onglet
  // et les droits d'écriture affichés ; le serveur fait la même vérification.
  const peut = (id) => role === 'admin' || !!session?.modules?.includes(id);
  // Une entité s'affiche dès qu'au moins un de ses onglets est accessible.
  const entities = ENTITIES.filter(e => e.id !== 'outils' && e.id !== 'settings'
    && getAllTabs(e.id, role, session).length > 0);
  // Ids d'entités réellement navigables (persoOnly déjà filtré) — sert à
  // valider ?ent= et le localStorage, pour ne jamais atterrir sur une entité vide.
  const realEntityIds = entities.map(e => e.id);

  // Initial nav — query string > localStorage > défauts. Les query params
  // permettent de partager un lien direct vers une vue précise.
  const initialNav = (() => {
    const out = { entity: entities[0]?.id || 'cafe', tab: null };
    try {
      const qs = new URLSearchParams(window.location.search);
      const qEnt = qs.get('ent');
      const qTab = qs.get('tab');
      if (qEnt && realEntityIds.includes(qEnt)) out.entity = qEnt;
      else {
        const savedEnt = localStorage.getItem('sonore_entity');
        if (savedEnt && realEntityIds.includes(savedEnt)) out.entity = savedEnt;
      }
      const candidateTab = normalizeTabId(qTab || localStorage.getItem('sonore_active_tab') || '');
      // Migration : Focus vivait sous Café — les anciens liens/localStorage
      // (ent=cafe&tab=focus) sont réaiguillés vers l'entité Perso.
      if (candidateTab === 'focus' && realEntityIds.includes('perso')) out.entity = 'perso';
      // Valide l'onglet : doit exister dans l'entité courante OU dans settings.
      const valid = new Set([
        ...getAllTabs(out.entity, role, session).map(t => t.id),
        ...getAllTabs('settings', role, session).map(t => t.id),
      ]);
      if (candidateTab && valid.has(candidateTab)) out.tab = candidateTab;
    } catch (_) {}
    if (!out.tab) out.tab = getAllTabs(out.entity, role, session)[0]?.id || 'today';
    return out;
  })();

  const [activeEntity, setActiveEntityState] = useState(initialNav.entity);
  const [activeTab,    setActiveTabState]    = useState(initialNav.tab);
  // Bureau : période affichée (toggle in-page, partagé par Global/Dépenses/Facturation).
  const [bureauPeriod, setBureauPeriod]      = useState('annee'); // 'mois' | 'annee'
  // Catalogue Square : sous-vue in-page (liste filtrable ⇄ audit TVA, même source tvaAudit).
  const [catalogueView, setCatalogueView]    = useState('catalogue'); // 'catalogue' | 'tva'
  // Navigation "Aujourd'hui" — jour affiché (YYYY-MM-DD) + données Z de caisse pour les jours passés.
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  // Détail Square d'un jour passé (navigation onglet Aujourd'hui) : { jour, j_moins_7 }
  const [jourSelectedDay,       setJourSelectedDay]       = useState(null);

  // Sections visibles pour l'entité active (filtre rôle, retire les sections vides).
  const sections = getSections(activeEntity, role, session);
  // Section courante = celle qui contient l'onglet actif ; fallback = première.
  const activeSection =
    findSectionForTab(activeEntity, role, activeTab, session) || sections[0] || null;

  // ── Persistance nav — URL (?ent&tab) + localStorage ───────────────────────
  // L'URL reflète toujours l'entité/onglet RÉEL sous-jacent — jamais le panneau
  // overlay (Outils/Réglages), qui reste un état d'UI non deep-linkable.
  // push=true crée une entrée d'historique → le bouton « Précédent » fonctionne.
  const isPanelEntity = (e) => e === 'outils' || e === 'settings';
  const writeNav = useCallback((entity, tab, push) => {
    try { localStorage.setItem('sonore_entity', entity); } catch (_) {}
    try { localStorage.setItem('sonore_active_tab', tab); } catch (_) {}
    try {
      const qs = new URLSearchParams(window.location.search);
      qs.set('ent', entity);
      qs.set('tab', tab);
      const url = `${window.location.pathname}?${qs.toString()}`;
      if (push) window.history.pushState(null, '', url);
      else      window.history.replaceState(null, '', url);
    } catch (_) {}
  }, []);

  // Wrapper setActiveTab : applique l'alias + persiste. Un onglet de panneau
  // (Outils/Réglages) ne touche pas l'URL → état d'UI uniquement.
  const setActiveTab = (tabId) => {
    const id = normalizeTabId(tabId);
    setActiveTabState(id);
    if (!isPanelEntity(activeEntity)) writeNav(activeEntity, id, true);
  };

  const setActiveEntity = (entity) => {
    setActiveEntityState(entity);
    const firstTab = getAllTabs(entity, role, session)[0]?.id || '';
    setActiveTabState(firstTab);
    if (!isPanelEntity(entity)) writeNav(entity, firstTab, true);
  };

  // Clic sur une section dans la sidebar : bascule vers son premier onglet.
  const setActiveSection = (sectionId) => {
    const sec = sections.find(s => s.id === sectionId);
    if (!sec) return;
    setActiveTab(sec.tabs[0]?.id);
  };

  // ── Panneau overlay (Outils / Réglages) ──────────────────────────────────
  // Overlay pur : on mémorise la nav réelle (panelPrev), on bascule l'état
  // React SANS écrire l'URL → l'URL reste sur la vue sous-jacente (cohérente,
  // deep-linkable). La fermeture restaure simplement l'état React mémorisé.
  const [panelPrev, setPanelPrev] = useState(null);
  const panelOpen = isPanelEntity(activeEntity) ? activeEntity : null;

  const openPanel = (target) => {
    if (!isPanelEntity(activeEntity)) {
      setPanelPrev({ entity: activeEntity, tab: activeTab });
    }
    setActiveEntity(target); // target = panneau → setActiveEntity n'écrit pas l'URL
  };

  const closePanel = () => {
    if (!panelPrev) {
      setActiveEntity(entities[0]?.id || 'cafe');
      return;
    }
    const prev = panelPrev;
    setActiveEntityState(prev.entity);
    setActiveTabState(prev.tab);
    setPanelPrev(null);
    // L'URL n'a jamais été modifiée à l'ouverture → rien à restaurer.
  };

  // Nav affichée : si un panneau est ouvert, on fige sur l'état précédent.
  const displayEntity  = panelOpen && panelPrev ? panelPrev.entity : activeEntity;
  const displayTab     = panelOpen && panelPrev ? panelPrev.tab    : activeTab;
  const displaySections = getSections(displayEntity, role, session);
  const displayActiveSection =
    findSectionForTab(displayEntity, role, displayTab, session) || displaySections[0] || null;

  // Setters utilisés par la sidebar/topbar : un clic ferme le panneau si ouvert
  // et navigue vers l'entité réelle sous-jacente (écrit l'URL avec la vraie cible).
  const navSetEntity = (e) => { setPanelPrev(null); setActiveEntity(e); };
  const navSetTab    = (t) => {
    const id = normalizeTabId(t);
    const targetEntity = panelOpen ? displayEntity : activeEntity;
    if (panelOpen) { setPanelPrev(null); setActiveEntityState(targetEntity); }
    setActiveTabState(id);
    writeNav(targetEntity, id, true);
  };
  const navSetSection = (sectionId) => {
    const sec = displaySections.find(s => s.id === sectionId);
    if (!sec) return;
    navSetTab(sec.tabs[0]?.id);
  };

  // ── Historique navigateur — Précédent/Suivant resynchronisent ent/tab ─────
  useEffect(() => {
    const onPop = () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const ent = qs.get('ent');
        if (!ent || !realEntityIds.includes(ent)) return;
        const candidate = normalizeTabId(qs.get('tab') || '');
        const valid = new Set(getAllTabs(ent, role, session).map(t => t.id));
        setPanelPrev(null);
        setActiveEntityState(ent);
        setActiveTabState(valid.has(candidate) ? candidate : (getAllTabs(ent, role, session)[0]?.id || ''));
      } catch (_) {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [role, session]);

  // Canonicalise l'URL au montage (sans entrée d'historique) pour que le 1er
  // « Précédent » revienne à une vue valide plutôt que de quitter l'app.
  useEffect(() => {
    if (!isPanelEntity(activeEntity)) writeNav(activeEntity, activeTab, false);
  }, []);

  const [data,           setData]           = useState(null);
  const [supabaseData,   setSupabaseData]   = useState(null);
  const [squareData,     setSquareData]     = useState(null);
  const [squareTeam,     setSquareTeam]     = useState(null);
  const [squareTeamLoading, setSquareTeamLoading] = useState(false);
  const [events,         setEvents]         = useState(null);
  const [eventsLoading,  setEventsLoading]  = useState(false);
  // Index date 'YYYY-MM-DD' → [noms d'événements] — partagé Records + badge today
  const evMap = useMemo(() => {
    const map = {};
    for (const ev of (events || [])) {
      const d = (ev.start || '').slice(0, 10);
      if (d) (map[d] = map[d] || []).push(ev.summary);
    }
    return map;
  }, [events]);
  const [tvaAudit,       setTvaAudit]       = useState(null);
  const [zCaisseHistory,    setZCaisseHistory]    = useState(null);
  const [zCaisseLoading,    setZCaisseLoading]    = useState(false);
  const [zCaisseError,      setZCaisseError]      = useState(null);
  const [recordFilter,     setRecordFilter]      = useState('all'); // 'all' | 'event' | 'no-event'
  const [tvaAuditLoading,setTvaAuditLoading]= useState(false);
  const [tvaToday,       setTvaToday]       = useState(null);
  const [tvaTodayLoading,setTvaTodayLoading]= useState(false);
  const [stockAlerts,        setStockAlerts]        = useState(null);
  const [stockAlertsOpen,    setStockAlertsOpen]    = useState(false);
  const [topProduits,        setTopProduits]        = useState(null);
  const [topProduitsLoading, setTopProduitsLoading] = useState(false);
  const [topProduitsMois,    setTopProduitsMois]    = useState(null);
  const [notionFinances, setNotionFinances] = useState(null);
  const [qontoTreso,     setQontoTreso]     = useState(null);
  const [revolutTreso,   setRevolutTreso]   = useState(null);
  const [stripeData,     setStripeData]     = useState(null);
  const [squareSettled,  setSquareSettled]  = useState(false);
  const [notionSettled,  setNotionSettled]  = useState(false);
  const [qontoSettled,   setQontoSettled]   = useState(false);
  const [facturesMois,   setFacturesMois]   = useState([]);
  // Santé des sources de données — { square: { ok, error, at }, ... }
  const [sourcesHealth,  setSourcesHealth]  = useState({});

  const squareLoading = !squareSettled;
  const [drawerOpen,       setDrawerOpen]       = useState(false);
  const [showFoodDetail,   setShowFoodDetail]   = useState(false);
  const [openSigInfo,      setOpenSigInfo]      = useState(null); // index de la ligne SIG dont l'info est ouverte
  const [openRepli,        setOpenRepli]        = useState({});
  const [detailPanel,      setDetailPanel]      = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sonore_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const [showDebug,        setShowDebug]        = useState(false);
  // ── Nouveautés — une seule fois par WHATS_NEW_ID, filtrées par rôle ────────
  const whatsNewEntries = useMemo(() => WHATS_NEW.filter(e => e.roles.includes(role)), [role]);
  const [showWhatsNew, setShowWhatsNew] = useState(() => {
    try { return whatsNewEntries.length > 0 && localStorage.getItem('sonore_whatsnew_seen') !== WHATS_NEW_ID; }
    catch { return false; }
  });
  const dismissWhatsNew = () => {
    setShowWhatsNew(false);
    try { localStorage.setItem('sonore_whatsnew_seen', WHATS_NEW_ID); } catch (_) {}
  };
  const isMobile = useIsMobile();
  const [selectedPeriode, setSelectedPeriode] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [uploads, setUploads] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sonore_uploads') || '{}'); } catch { return {}; }
  });
  // Couleurs personnalisées du personnel — persistées dans localStorage
  const [memberColors, setMemberColors] = useState(() => getMemberColorOverrides());

  const ENTITE_MAP = { sci: 'SCI Sonore', cafe: 'Café Sonore', bureau: 'Bureau Sonore' };

  const handleSave = async (key, value) => {
    const next = { ...uploads, [key]: value };
    setUploads(next);
    try { localStorage.setItem('sonore_uploads', JSON.stringify(next)); } catch (_) {}
    const entite = ENTITE_MAP[key];
    if (!entite) return { ok: false, error: 'Entité inconnue' };
    const transactions = value.transactions || [];
    if (!transactions.length) return { ok: false, error: 'Aucune transaction à importer' };
    try {
      const res = await fetch('/api/import-transactions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entite, transactions, solde: value.solde, compte: value.compte || '' }),
      });
      if (!res.ok) return { ok: false, error: `Erreur HTTP ${res.status}` };
      const result = await res.json();
      apiFetch('/api/finances').then(r => r.json()).then(d => { if (d?.generatedAt) setNotionFinances(d); }).catch(() => {});
      return result;
    } catch (e) { return { ok: false, error: e.message }; }
  };

  const handleClear = () => { setUploads({}); localStorage.removeItem('sonore_uploads'); };

  // ── Chargement des photos membres (cache global Avatar) au mount ──────────
  useEffect(() => {
    let cancelled = false;
    fetch('/api/users', { credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.users) return;
        const map = {};
        for (const u of data.users) {
          if (u.square_member_id && u.photo_url) map[u.square_member_id] = u.photo_url;
        }
        setMemberPhotos(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // ── Cache version check — invalide les données locales si la version change ──
    try {
      if (localStorage.getItem('sonore_cache_v') !== CACHE_VERSION) {
        localStorage.removeItem('sonore_uploads');
        localStorage.removeItem('sonore_active_tab');
        localStorage.removeItem('sonore_entity');
        localStorage.setItem('sonore_cache_v', CACHE_VERSION);
      }
    } catch (_) {}

    // Keyframe sk-pulse désormais injecté par src/theme.js au chargement du module.
    const canvas = document.createElement('canvas'); canvas.width = 32; canvas.height = 32;
    const ctx = canvas.getContext('2d'); ctx.fillStyle = SHADCN.mint;
    ctx.beginPath(); ctx.arc(16, 16, 16, 0, Math.PI * 2); ctx.fill();
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon'; link.href = canvas.toDataURL(); document.head.appendChild(link);
    // bootFetch : consomme le prefetch lancé au parse du bundle (src/prefetch.js)
    // s'il a abouti, sinon retombe sur le chemin normal apiFetchCached.
    const bootFetch = async (url) => {
      const pre = consumePrefetch(url);
      if (pre) {
        const d = await pre;
        if (d) return d;
      }
      return apiFetchCached(url).then(r => r.json());
    };
    // Supabase — source de vérité principale
    bootFetch('/api/supabase-data').then(d => {
      if (d?.lastUpdate) {
        setSupabaseData(d);
        setData(d);
      }
    }).catch(console.error);
    // reportSource : mémorise l'état de chaque source pour l'affichage santé
    // (Connexions + bandeau panne). Une erreur = source injoignable ou réponse invalide.
    const reportSource = (key, ok, error) =>
      setSourcesHealth(h => ({ ...h, [key]: { ok, error: error || null, at: new Date().toISOString() } }));
    bootFetch('/api/square-data').then(d => {
      if (d?.mois) { setSquareData(d); reportSource('square', true); }
      else reportSource('square', false, d?.error || 'Réponse invalide');
    }).catch(e => { console.error(e); reportSource('square', false, e.message); }).finally(() => setSquareSettled(true));
    apiFetchCached('/api/finances').then(r => r.json()).then(d => {
      if (d?.generatedAt) { setNotionFinances(d); reportSource('finances', true); }
      else reportSource('finances', false, d?.error || 'Réponse invalide');
    }).catch(e => { console.error(e); reportSource('finances', false, e.message); }).finally(() => setNotionSettled(true));
    // Revolut (compte courant du Café) : null = non configuré, { error } = panne
    // (token expiré…), sinon soldes OK.
    apiFetchCached('/api/finances?type=treso').then(r => r.json()).then(d => {
      const rc = d?.revolutCafe;
      if (rc && !rc.error) { setRevolutTreso(rc); reportSource('revolut', true); }
      else if (rc?.error || !d?.generatedAt) reportSource('revolut', false, rc?.error || d?.error || 'Réponse invalide');
    }).catch(e => reportSource('revolut', false, e.message));
    // Qonto = comptes du Bureau : admin seulement (le serveur refuse les autres).
    if (role === 'admin') {
      apiFetchCached('/api/qonto-treso').then(r => r.json()).then(d => {
        if (d?.generatedAt) { setQontoTreso(d); reportSource('qonto', true); }
        else reportSource('qonto', false, d?.error || 'Réponse invalide');
      }).catch(e => reportSource('qonto', false, e.message)).finally(() => setQontoSettled(true));
    } else setQontoSettled(true);
    apiFetchCached('/api/stripe-data').then(r => r.json()).then(d => {
      if (d?.ok) { setStripeData(d); reportSource('stripe', true); }
      else reportSource('stripe', false, d?.error || 'Réponse invalide');
    }).catch(e => reportSource('stripe', false, e.message));
    // Factures Supabase — mois en cours (Marge Brute). Pas de cache local
    // car les factures peuvent être ajoutées/payées en cours de session.
    const periodeNow = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    apiFetch(`/api/factures?mois=${periodeNow}`).then(r => r.json()).then(d => { if (d?.factures) setFacturesMois(d.factures); }).catch(() => {});
  }, []);

  // ── Fetch données équipe — chargement paresseux à la 1ère visite des onglets
  // qui en ont besoin (Aujourd'hui pour la liste du jour, Équipe pour la grille).
  useEffect(() => {
    const needsTeam = activeTab === 'planning' || activeTab === 'today' || activeTab === 'todos' || activeTab === 'focus';
    if (!needsTeam || squareTeam || squareTeamLoading) return;
    setSquareTeamLoading(true);
    apiFetch('/api/square-team').then(r => r.json()).then(d => {
      if (d?.semaines) setSquareTeam(d);
    }).catch(() => {}).finally(() => setSquareTeamLoading(false));
  }, [activeTab, squareTeam, squareTeamLoading]);

  // Refetch forcé après création/édition/suppression d'un shift dans EquipeTab.
  const refetchSquareTeam = useCallback(() => {
    setSquareTeamLoading(true);
    apiFetch('/api/square-team').then(r => r.json()).then(d => {
      if (d?.semaines) setSquareTeam(d);
    }).catch(() => {}).finally(() => setSquareTeamLoading(false));
  }, []);

  // ── Fermeture du soir ─────────────────────────────────────────────────────
  // Le panneau de checklist s'ouvre pour qui ferme : la personne dont le
  // créneau du jour finit le plus tard, à partir de 45 min avant cette fin.
  // « Plus tard » le replie 20 min ; « Fermer » (checklist bouclée) jusqu'au
  // lendemain. Rien pour un admin qui ne travaille pas ce soir-là. Le planning
  // vient de squareTeam, chargé par l'onglet Aujourd'hui où l'équipe arrive.
  const [horloge, setHorloge] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setHorloge(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  const [fermetureVue, setFermetureVue] = useState(0);
  const rappelFermeture = useMemo(() => {
    if (!peut('fermeture') || !squareTeam?.semaines) return false;
    const d = new Date(horloge);
    const iso = d.toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
    const jour = squareTeam.semaines.flatMap(s => s.jours || []).find(j => j.date === iso);
    const shifts = jour?.shifts || [];
    if (!shifts.length) return false;
    const finMax = Math.max(...shifts.map(sh => sh.finMin));
    const [h, m] = d.toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).split(':').map(Number);
    if (h * 60 + m < finMax - 45) return false;
    const prenom = (session?.prenom || '').trim().toLowerCase();
    const ferme = shifts.filter(sh => sh.finMin === finMax).some(sh =>
      (session?.square_member_id && sh.memberId === session.square_member_id)
      || (prenom && (sh.prenom || '').trim().toLowerCase() === prenom));
    if (!ferme) return false;
    try {
      if (localStorage.getItem('sonore_fermeture_close') === iso) return false;
      if (Number(localStorage.getItem('sonore_fermeture_report') || 0) > horloge) return false;
    } catch (_) {}
    return true;
  }, [squareTeam, horloge, session, role, fermetureVue]); // fermetureVue : relit localStorage après « Plus tard »
  const reporterFermeture = useCallback(() => {
    try { localStorage.setItem('sonore_fermeture_report', String(Date.now() + 20 * 60000)); } catch (_) {}
    setFermetureVue(v => v + 1);
  }, []);
  const cloreFermeture = useCallback(() => {
    try { localStorage.setItem('sonore_fermeture_close', new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' })); } catch (_) {}
    setFermetureVue(v => v + 1);
  }, []);

  // Refetch events après CRUD GCal.
  const refetchEvents = useCallback(() => {
    setEventsLoading(true);
    apiFetch('/api/square-data?type=events').then(r => r.json()).then(d => {
      if (Array.isArray(d?.events)) setEvents(d.events);
    }).catch(() => {}).finally(() => setEventsLoading(false));
  }, []);

  // ── Fetch events Google Calendar — Today + Planning RH
  useEffect(() => {
    const needsEvents = activeTab === 'today' || activeTab === 'planning' || activeTab === 'records' || activeTab === 'focus';
    if (!needsEvents || events || eventsLoading) return;
    setEventsLoading(true);
    apiFetch('/api/square-data?type=events').then(r => r.json()).then(d => {
      if (Array.isArray(d?.events)) setEvents(d.events);
    }).catch(() => {}).finally(() => setEventsLoading(false));
  }, [activeTab, events, eventsLoading]);

  // ── Fetch audit TVA — chargement paresseux à la 1ère visite de l'onglet Café
  useEffect(() => {
    if (activeTab !== 'articles_liste' || tvaAudit || tvaAuditLoading) return;
    setTvaAuditLoading(true);
    apiFetchCached('/api/square-data?type=tva-audit').then(r => r.json()).then(d => {
      if (d?.summary) setTvaAudit(d);
    }).catch(() => {}).finally(() => setTvaAuditLoading(false));
  }, [activeTab, tvaAudit, tvaAuditLoading]);

  // ── Z de caisse — historique chargé à la 1ère visite de l'onglet
  useEffect(() => {
    if (activeTab !== 'rapports' && activeTab !== 'records' && activeTab !== 'today') return;
    if (zCaisseHistory || zCaisseLoading) return;
    setZCaisseLoading(true);
    setZCaisseError(null);
    apiFetch('/api/square-data?type=zcaisse-history')
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d)) setZCaisseHistory(d);
        else setZCaisseError(d?.error || 'Erreur inconnue');
      })
      .catch(e => setZCaisseError(e.message))
      .finally(() => setZCaisseLoading(false));
  }, [activeTab, zCaisseHistory, zCaisseLoading]);

  // ── Alerte TVA ventes du jour — fetch à l'entrée de l'onglet today
  const fetchTvaToday = useCallback(() => {
    setTvaTodayLoading(true);
    apiFetch('/api/square-data?type=tva-today').then(r => r.json()).then(d => {
      if (d && typeof d.nbAnomalies === 'number') setTvaToday(d);
    }).catch(() => {}).finally(() => setTvaTodayLoading(false));
  }, []);
  useEffect(() => {
    if (activeTab !== 'today' || tvaToday || tvaTodayLoading) return;
    fetchTvaToday();
  }, [activeTab, tvaToday, tvaTodayLoading, fetchTvaToday]);

  // ── Détail d'un jour passé (navigation "Aujourd'hui") ─────────────────────────
  // Lecture seule (?type=jour) : CA, groupes, catégories, disquaire, sandwichs,
  // retail, services, cowork + J-7. On vide d'abord l'état pour ne jamais
  // afficher les blocs du jour précédemment consulté, et on ignore une réponse
  // arrivée après un nouveau changement de jour.
  useEffect(() => {
    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    setJourSelectedDay(null);
    if (selectedDay === todayStr) return;
    let cancelled = false;
    apiFetch(`/api/square-data?type=jour&date=${selectedDay}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setJourSelectedDay(d?.jour ? { ...d, date: selectedDay } : null); })
      .catch(() => { if (!cancelled) setJourSelectedDay(null); });
    return () => { cancelled = true; };
  }, [selectedDay]);

  useEffect(() => {
    if (activeTab !== 'today' || stockAlerts) return;
    apiFetch('/api/square-data?type=stock-alerts&seuil=20&cats=Retail,Merch,Céramique,Matériel')
      .then(r => r.json())
      .then(d => { if (Array.isArray(d?.alerts)) setStockAlerts(d.alerts); })
      .catch(() => {});
  }, [activeTab, stockAlerts]);

  useEffect(() => {
    if (activeTab !== 'articles_ventes') return;
    setTopProduitsLoading(true);
    const qs = topProduitsMois ? `&mois=${topProduitsMois}` : '';
    apiFetch(`/api/square-data?type=top-produits${qs}`)
      .then(r => r.json())
      .then(d => { if (d?.top) setTopProduits(d); })
      .catch(() => {})
      .finally(() => setTopProduitsLoading(false));
  }, [activeTab, topProduitsMois]);

  // Gate minimal : on attend que Supabase (supabase-data) ait répondu.
  // Les API Square/Stripe/Qonto se résolvent ensuite en parallèle.
  if (!data) return <PageLoader />;

  // ── Données de base ───────────────────────────────────────────────────────────
  const now            = new Date();
  const currentPeriode = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const dayOfMonth     = now.getDate();
  const daysInMonth    = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysLeft       = daysInMonth - dayOfMonth;

  const latestNotion = (em) => em ? Object.entries(em).sort((a,b) => b[0].localeCompare(a[0])).map(([,v])=>v).find(v=>v.solde!==null)||null : null;

  // Priorité : import manuel → Notion → Qonto live → Supabase (mergé dans data) → data.json (fallback)
  // Priorité LIVE d'abord (dynamique), puis solde localStorage (uploads) et statique en secours.
  // SCI → live = Supabase (BNP) ; Bureau → live = Qonto (son vrai compte).
  const tresoSci    = latestNotion(notionFinances?.sci)?.solde    ?? qontoTreso?.sci?.solde    ?? uploads.sci?.solde    ?? data.tresorerie.sci ?? 0;
  // Café — compte principal futur : trésorerie consolidée BNP (Supabase) + Revolut.
  // BNP Café = LIVE Supabase en priorité (dernière entrée BDD, dynamique). Le solde
  // d'import figé dans le localStorage (uploads) et le fallback statique ne servent
  // plus que de filet de secours si la BDD ne renvoie rien.
  const tresoCafeBNP     = latestNotion(notionFinances?.cafe)?.solde ?? uploads.cafe?.solde ?? data.tresorerie.cafe;
  const tresoCafeRevolut = revolutTreso?.solde ?? null;
  const tresoCafe        = tresoCafeBNP + (tresoCafeRevolut ?? 0);
  const qontoLive   = !!qontoTreso?.generatedAt;

  // ── Paramètres métier depuis dashboard_config (Supabase) ─────────────────
  const appCfg = data.config || {};
  const SQUARE_FEE_RATE      = appCfg.square_fee_rate      ?? 0.0175;
  const FOOD_COST_CIBLE      = appCfg.food_cost_cible      ?? 0.30;
  const FOOD_COST_ALERTE     = appCfg.food_cost_alerte     ?? 0.35;
  const RATIO_FOOD_VS_ACHATS = appCfg.ratio_food_vs_achats ?? 0.65;

  // Charges fixes = moyennes des 3 derniers mois bancaires complets (BNP + Revolut),
  // calculées côté serveur par lib/charges-fixes.js. Aucun montant saisi ni en dur.
  const cfRef = data.cafe.chargesFixes;
  const cfMoisLabel = (cfRef.mois || []).map(p => MOIS_FR[Number(p.slice(5, 7)) - 1]).join(' · ');
  const totalChargesCafe = cfRef.total || 0;

  // ── Coût salarial réel (banque) ──────────────────────────────────────────
  // Virements de salaires + URSSAF + Klesia + ALAN réellement décaissés, moins la
  // refacturation du salaire de Léo encaissée de l'EURL Fardeau (moyenne 3 mois).
  const coutSalarialCharge = Math.round(cfRef.coutSalarial || 0);
  const chargesFixesHT = Math.round(
    _ht20(cfRef.loyerSCI)
    + coutSalarialCharge   // Alan inclus dedans (coût du personnel complet)
    + cfRef.pretsBNP
    + _ht20(cfRef.syla)
    + cfRef.assuranceAXA
    + _ht20(cfRef.verisure)
    + _ht20(cfRef.orange)
  );

  const availableMonths = notionFinances?.cafe
    ? [...new Set([...Object.keys(notionFinances.cafe), currentPeriode])].sort()
    : [currentPeriode];
  const isCurrentMonth = selectedPeriode === currentPeriode;

  const cafeSelectedMonth = notionFinances?.cafe?.[selectedPeriode] ?? null;
  const cafeUploadsMonth  = uploads.cafe?.months?.[selectedPeriode] ?? null;
  const cafeMonthData     = cafeSelectedMonth || cafeUploadsMonth;
  const chargesVarMois    = cafeMonthData?.chargesVariable ?? (cafeMonthData ? Math.max(0, Math.abs(cafeMonthData.depenses) - totalChargesCafe) : null);
  // Loyer SCI est tagué flux_interne dans finances.js → absent de chargesFixed.
  // totalChargesCafe (moyenne banque) l'inclut déjà — on le réintègre manuellement.
  const chargesFixesMois  = cafeMonthData
    ? (cafeMonthData.chargesFixed + (cfRef.loyerSCI ?? 0))
    : totalChargesCafe;
  // Décaissements du mois (runway, atterrissage) — pas le seuil de rentabilité.
  const seuilDynamique    = chargesVarMois !== null ? Math.round(chargesFixesMois + chargesVarMois) : Math.round(cfRef.decaissements || totalChargesCafe);

  const squareMoisEnCours   = squareData?.mois?.find(m => m.enCours);
  const caSquareMoisEnCours = squareMoisEnCours?.grossTTC ?? squareMoisEnCours?.totalTTC ?? null;
  const tvaCollectee        = squareMoisEnCours?.tvaCollectee ?? 0;

  // ── CA HT — ventilation granulaire avec fallback automatique ───────────────
  // Priorité 1 : catégories Square fines (à configurer dans le POS)
  // Priorité 2 : catégories legacy Square (Café / Retail / Services)
  // Priorité 3 : TTC − TVA collectée (ultime filet de sécurité)
  const sqCats = squareMoisEnCours?.groups ?? {};

  // Tentative granulaire (objectif après reconfiguration Square POS)
  const _foodFine = Math.round(
    (sqCats['Sur place']   ?? 0) / (1 + TVA_SUR_PLACE)  +
    (sqCats['À emporter']  ?? 0) / (1 + TVA_A_EMPORTER)
  );
  // Disquaire = vinyles + presse + affiches (sortis du Retail)
  const _disquaireFine = Math.round(
    ((sqCats['Vinyles'] ?? 0) + (sqCats['Affiches'] ?? 0)) / (1 + TVA_RETAIL) +
    (sqCats['Magazines'] ?? 0) / (1 + TVA_PRESSE)
  );
  const _retailFine = Math.round(
    (sqCats['Merch'] ?? 0) / (1 + TVA_RETAIL) +
    (sqCats['Café Grain/Retail'] ?? 0) / (1 + TVA_A_EMPORTER)
  );
  const _svcFine = Math.round((sqCats['Services'] ?? 0) / (1 + TVA_RETAIL));
  const hasFineGrain = _foodFine + _retailFine + _disquaireFine > 0;

  // Fallback legacy (catégories Square actuelles : Café / Retail / Services)
  const _foodLegacy   = Math.round((sqCats['Café']    ?? 0) / (1 + TVA_CAFE));
  const _retailLegacy = Math.round((sqCats['Retail']  ?? 0) / (1 + TVA_RETAIL));
  const _svcLegacy    = Math.round((sqCats['Services'] ?? 0) / (1 + TVA_RETAIL));
  // Le serveur range vinyles, magazines, affiches et livres dans un groupe
  // « Disquaire » à part. Sans cette ligne, le pôle entier valait 0 : absent du
  // CA HT du mois comme du budget de réassort. Le groupe arrive sans détail par
  // taux : tout est ramené en HT à 20 % (vinyles), ce qui minore un peu la presse.
  const _disquaireLegacy = Math.round((sqCats['Disquaire'] ?? 0) / (1 + TVA_RETAIL));
  const hasLegacyData = _foodLegacy + _retailLegacy + _disquaireLegacy + _svcLegacy > 0;

  // Sélection de la couche active
  const FOOD_BEV_HT  = hasFineGrain ? _foodFine   : _foodLegacy;
  const RETAIL_HT    = hasFineGrain ? _retailFine : _retailLegacy;
  const DISQUAIRE_HT = hasFineGrain ? _disquaireFine : _disquaireLegacy;
  const SERVICES_HT  = hasFineGrain ? _svcFine    : _svcLegacy;

  // Stripe — CA services mois en cours (cowork + privatisation, TVA 20%)
  const stripeCurrentMonth = stripeData?.parMois?.[currentPeriode]?.montant ?? 0;
  const STRIPE_SERVICES_HT = Math.round(stripeCurrentMonth / 1.20);

  // CA total HT — avec ultime fallback TTC − TVA si aucune catégorie exploitable
  const _sumCats = FOOD_BEV_HT + RETAIL_HT + DISQUAIRE_HT + SERVICES_HT + STRIPE_SERVICES_HT;
  const TOTAL_CA_HT = _sumCats > 0
    ? _sumCats
    : Math.max(0, (caSquareMoisEnCours ?? 0) + stripeCurrentMonth - tvaCollectee);

  // Aliases rétrocompatibles utilisés dans la suite (food cost, invest retail)
  const caCafeGroupHT   = FOOD_BEV_HT;
  // BFR « Invest Retail » : les achats vinyles/disques restent classés en achatsRetail,
  // donc le dénominateur du ratio inclut le pôle Disquaire (vinyles) en plus du Retail.
  const caRetailGroupHT = RETAIL_HT + DISQUAIRE_HT;


  // Montants dynamiques depuis byCategory Notion (courant → précédent → fallback statique)
  const byCatCourant = (isCurrentMonth ? cafeMonthData?.byCategory : null) ?? {};

  // Jour d'échéance : celui réellement observé en banque (cfRef.detail[].jour,
  // médiane des 3 dernières occurrences) ; les jours écrits ici ne servent plus
  // que de secours quand la ligne n'est jamais tombée. Les jours saisis étaient
  // faux — loyer annoncé le 28 alors qu'il part le 8, URSSAF le 21 pour le 16.
  const jourDe = (cle, secours) => cfRef.detail?.[cle]?.jour ?? secours;
  const CHARGES_FIXES_REF = [
    { label: 'Loyer SCI',       cle: 'loyerSCI',     jourMois: jourDe('loyerSCI', 8),      categorie: 'Loyer',            montant: cfRef.loyerSCI,                        keywords: ['SCI','LOYER','BAIL '] },
    { label: 'Prêts BNP',       cle: 'pretsBNP',     jourMois: jourDe('pretsBNP', 7),      categorie: 'Prêts',            montant: cfRef.pretsBNP,                        keywords: ['PRET','EMPRUNT','REMBT','ECHEANCE','MENSUALITE','AMORT'] },
    { label: 'Mutuelle ALAN',   cle: 'alanSante',    jourMois: jourDe('alanSante', 10),    categorie: 'Charges sociales', montant: cfRef.alanSante,                       match: lib => lib.includes('ALAN') && (lib.includes('SANTE') || lib.includes('INSURANCE')) && !lib.includes('PREV') },
    { label: 'Assurance AXA',   cle: 'assuranceAXA', jourMois: jourDe('assuranceAXA', 10), categorie: 'Assurance',        montant: cfRef.assuranceAXA,                    keywords: ['AXA','ALLIANZ','ASSURANCE'] },
    { label: 'Verisure',        cle: 'verisure',     jourMois: jourDe('verisure', 10),     categorie: 'Sécurité',         montant: cfRef.verisure,                        keywords: ['VERISURE','SECURITAS'] },
    { label: 'Comptable Syla',  cle: 'syla',         jourMois: jourDe('syla', 15),         categorie: 'Comptable',        montant: cfRef.syla,                            keywords: ['SYLA','COMPTABLE','EXPERTISE'] },
    { label: 'Télécom Orange',  cle: 'orange',       jourMois: jourDe('orange', 20),       categorie: 'Télécom',          montant: cfRef.orange,                          keywords: ['ORANGE','SFR ','BOUYGUES','FREE ','TELECOM'] },
    { label: 'URSSAF',          cle: 'urssaf',       jourMois: jourDe('urssaf', 21),       categorie: 'Charges sociales', montant: cfRef.urssaf,                          match: lib => lib.includes('URSSAF') },
    { label: 'Retraite Klesia', cle: 'klesia',       jourMois: jourDe('klesia', 21),       categorie: 'Charges sociales', montant: cfRef.klesia,                          match: lib => lib.includes('KLESIA') || lib.includes('MALAKOFF') },
    { label: 'Prévoyance ALAN', cle: 'alanPrev',     jourMois: jourDe('alanPrev', 21),     categorie: 'Charges sociales', montant: cfRef.alanPrev,                        match: lib => lib.includes('ALAN') && lib.includes('PREV') },
    { label: 'Salaires',        cle: 'salaires',     jourMois: jourDe('salaires', 1),      categorie: 'Salaires',         montant: cfRef.salaires,                        keywords: ['SALAIRE','VIR SAL','PAIE ','REMUNERATION'] },
  ];

  // Supabase en priorité (BNP importé + Revolut synchronisé chaque nuit) : même état sur tous
  // les appareils. L'import CSV gardé dans le localStorage ne sert que si /api/finances n'a pas
  // répondu — avant, il passait devant et figeait la checklist sur le dernier import de ce navigateur.
  const cafeTxCourant = (() => {
    if (notionFinances?.cafe) {
      return (notionFinances.cafe[currentPeriode]?.transactions || []).filter(t => t.montant < 0);
    }
    if (isCurrentMonth && uploads.cafe?.transactions) {
      return uploads.cafe.transactions.filter(t => t.periode === currentPeriode && t.montant < 0);
    }
    return [];
  })();

  const hasTxDetail = cafeTxCourant.length > 0;
  const chargesATomber = CHARGES_FIXES_REF.filter(c => {
    if (hasTxDetail) return !matchCharge(c, cafeTxCourant);
    return !(byCatCourant[c.categorie] > 0);
  });

  const montantATomber = chargesATomber.reduce((s, c) => s + c.montant, 0);
  const hasTresoReelle = isCurrentMonth && (latestNotion(notionFinances?.cafe) !== null || uploads.cafe?.solde != null);
  const tresoNetteCafe = tresoCafe - (isCurrentMonth ? montantATomber : 0) - (isCurrentMonth ? tvaCollectee : 0);
  const runwayNetMois  = seuilDynamique > 0 ? Math.floor(tresoNetteCafe / seuilDynamique) : null;

  // ── calculateOperatingHealth() ────────────────────────────────────────────────
  // Moyenne glissante des charges variables sur 3 derniers mois (Notion)
  const moyenneVariables = (() => {
    if (!notionFinances?.cafe) return 0;
    const entries = Object.entries(notionFinances.cafe)
      .filter(([p]) => p < currentPeriode)
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 3);
    if (!entries.length) return 0;
    // chargesVariable (finances.js) exclut les flux internes — `depenses` les inclut
    // (virements tréso BNP→Revolut, écritures techniques banque) et gonflerait le seuil.
    const sums = entries.map(([, d]) => d.chargesVariable ?? Math.max(0, Math.abs(d.depenses ?? 0) - totalChargesCafe));
    return Math.round(sums.reduce((a, b) => a + b, 0) / sums.length);
  })();

  // ── Scission achats Food Cost vs Invest Retail ───────────────────────────────
  const splitFromTx = (() => {
    if (!cafeTxCourant.length) return null;
    const food   = cafeTxCourant.filter(isFood).reduce((s, tx) => s + Math.abs(tx.montant), 0);
    const retail = cafeTxCourant.filter(isRetail).reduce((s, tx) => s + Math.abs(tx.montant), 0);
    return { food: Math.round(food), retail: Math.round(retail) };
  })();

  const foodTxLines = splitFromTx ? cafeTxCourant.filter(isFood) : null;

  // ── Top 3 fournisseurs Food — mois courant vs moyenne 2 mois précédents ──────
  const top3Fournisseurs = (() => {
    // Extrait le nom du bénéficiaire depuis un libellé BNP
    const extractNom = libelle => {
      const l = libelle || '';
      const ben = l.match(/\/BEN\s+([^/]+)/i);
      if (ben) return ben[1].replace(/\s+/g, ' ').trim().substring(0, 28);
      const eme = l.match(/EMETTEUR\s+([^/\s][^/]*)/i);
      if (eme) return eme[1].replace(/\s+/g, ' ').trim().substring(0, 28);
      return l.replace(/VIR (SEPA|SCT INST) (EMIS|RECU)\s*/i, '').replace(/PRLV SEPA\s*/i, '').trim().substring(0, 28);
    };

    // 2 mois précédents disponibles dans notionFinances.cafe
    const prevPeriodes = Object.keys(notionFinances?.cafe || {})
      .filter(p => p < currentPeriode)
      .sort()
      .slice(-2);

    // Agréger par nom de fournisseur
    const map = {};
    // Mois courant
    cafeTxCourant.filter(isFood).forEach(tx => {
      const nom = extractNom(tx.libelle);
      if (!map[nom]) map[nom] = { nom, current: 0, prevTotals: [] };
      map[nom].current += Math.abs(tx.montant);
    });
    // Mois précédents (on cumule par période pour faire une moyenne)
    prevPeriodes.forEach(p => {
      const txs = (notionFinances?.cafe?.[p]?.transactions || []).filter(isFood);
      const perFourn = {};
      txs.forEach(tx => {
        const nom = extractNom(tx.libelle);
        perFourn[nom] = (perFourn[nom] || 0) + Math.abs(tx.montant);
      });
      Object.entries(perFourn).forEach(([nom, total]) => {
        if (!map[nom]) map[nom] = { nom, current: 0, prevTotals: [] };
        map[nom].prevTotals.push(total);
      });
    });

    return Object.values(map)
      .filter(f => f.current > 0)
      .sort((a, b) => b.current - a.current)
      .slice(0, 3)
      .map(f => ({
        ...f,
        current: Math.round(f.current),
        avg: f.prevTotals.length > 0
          ? Math.round(f.prevTotals.reduce((s, v) => s + v, 0) / prevPeriodes.length)
          : null,
        nbPrevMois: prevPeriodes.length,
      }));
  })();

  const totalVarBrut   = chargesVarMois ?? moyenneVariables;
  const achatsFoodHT   = splitFromTx ? splitFromTx.food   : Math.round(totalVarBrut * RATIO_FOOD_VS_ACHATS);
  const achatsRetailHT = splitFromTx ? splitFromTx.retail : Math.round(totalVarBrut * (1 - RATIO_FOOD_VS_ACHATS));

  // ── Seuil HT — point mort basé sur coût réel employeur + variables glissantes ─
  // Variables = décaissements achats (food/retail) ramenés en HT via leurs taux de TVA déductible.
  const moyenneVariablesHT = Math.round(
    moyenneVariables * RATIO_FOOD_VS_ACHATS / (1 + TVA_A_EMPORTER)
    + moyenneVariables * (1 - RATIO_FOOD_VS_ACHATS) / (1 + TVA_RETAIL)
  );
  // Le seuil affiché est celui de lib/seuils.js (20 000 € HT, recalculé sur le
  // bilan au 30/06/2026), servi par le payload. Le calcul banque ne reste qu'en
  // secours : il additionnait fixes et achats du moment, donc montait avec le CA.
  const seuilHT = data.cafe.seuilRentabilite > 0
    ? data.cafe.seuilRentabilite
    : Math.round(chargesFixesHT + moyenneVariablesHT);

  // Cockpit metrics — Square TTC + Stripe (cowork + privatisation)
  const caActuel       = (caSquareMoisEnCours ?? 0) + stripeCurrentMonth;
  const caJournalier   = dayOfMonth > 0 && caActuel > 0 ? Math.round(caActuel / dayOfMonth) : 0;
  const projectionFin  = Math.round(caJournalier * daysInMonth);
  // Rentabilité → 100 % HT : on compare le CA HT (Square HT + Stripe HT) au seuil HT par poste.
  const caActuelHT     = TOTAL_CA_HT + Math.round(stripeCurrentMonth / (1 + TVA_RETAIL));
  const manqueCA       = Math.max(0, seuilHT - caActuelHT);
  const couverturePct  = seuilHT > 0 && caActuelHT > 0 ? Math.round(caActuelHT / seuilHT * 100) : 0;
  const estRentable    = caActuelHT >= seuilHT;
  const atterColor     = tresoNetteCafe >= seuilDynamique ? SHADCN.success : tresoNetteCafe > 0 ? SHADCN.warning : SHADCN.error;

  // Données graphiques
  const squareGroupsRaw  = squareMoisEnCours?.groups;
  const ventilationData  =
    ['Café', 'Disquaire', 'Retail', 'Services']
      .map(name => ({ name, value: squareGroupsRaw?.[name] ?? 0, color: GROUP_COLORS[name] }))
      .filter(d => d.value > 0);
  const ventilationTotal = ventilationData.reduce((s, d) => s + d.value, 0);

  const cashFlowData = notionFinances?.cafe
    ? Object.entries(notionFinances.cafe)
        .filter(([, d]) => d.recettes || d.depenses)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-6)
        .map(([periode, d]) => ({
          mois: MOIS_LABELS[parseInt(periode.split('-')[1], 10) - 1],
          recettes: Math.round(d.recettes ?? 0),
          depenses: Math.round(Math.abs(d.depenses ?? 0)),
        }))
    : [];

  // Prêts BNP — calcul de l'impact passage différé → amortissement
  const pretsBNP         = data.cafe.pretsBNP || [];
  const totalDiffere     = pretsBNP.reduce((s, p) => s + p.mensualiteDiffere, 0);
  const totalAmort       = pretsBNP.reduce((s, p) => s + p.mensualiteAmort, 0);
  const surplusAmort     = totalAmort - totalDiffere;


  // ── Résultat Net Café — 3 vues (projeté / glissant 3M / YTD) ─────────────────
  const _chargesAnnuData  = data.cafe?.chargesAnnuelles || {};
  const _chargesAnnuMens  = Object.values(_chargesAnnuData).reduce((s, c) => s + (c.lisseMensuel || 0), 0);
  const _chargesAnnuToVerify = Object.values(_chargesAnnuData).some(c => c._status === 'TO_VERIFY');

  const _cogsMoy3M = (() => {
    if (!notionFinances?.cafe) return 0;
    const ps = Object.keys(notionFinances.cafe).filter(p => p < currentPeriode).sort().slice(-3);
    if (!ps.length) return 0;
    const tots = ps.map(p =>
      (notionFinances.cafe[p]?.transactions || [])
        .filter(tx => tx.categorie === 'Fournisseurs')
        .reduce((s, tx) => s + Math.abs(tx.montant || 0), 0)
    );
    return Math.round(tots.reduce((a, b) => a + b, 0) / ps.length);
  })();

  const _raiNotion = (periode) => {
    const d = notionFinances?.cafe?.[periode];
    if (!d) return null;
    const caHT  = Math.round((d.recettes || 0) / 1.10);
    const frais = Math.round(caHT * SQUARE_FEE_RATE);
    const cogsM = Math.round((d.transactions || []).filter(tx => tx.categorie === 'Fournisseurs').reduce((s, tx) => s + Math.abs(tx.montant || 0), 0));
    const cogs  = cogsM > 0 ? cogsM : _cogsMoy3M;
    // Mois clos : charges fixes réellement décaissées ce mois-là (le loyer SCI, tagué
    // flux interne, est absent de chargesFixed → réintégré depuis les transactions).
    const loyer = (d.transactions || []).filter(tx => tx.categorie === 'Loyer' && tx.montant < 0).reduce((s, tx) => s + Math.abs(tx.montant), 0);
    const cf    = Math.round((d.chargesFixed || 0) + loyer);
    return { rai: caHT - frais - cogs - cf - _chargesAnnuMens };
  };

  const _cfCourant    = coutSalarialCharge
    + cfRef.loyerSCI + cfRef.pretsBNP + cfRef.syla
    + cfRef.assuranceAXA + cfRef.verisure + cfRef.orange;
  const _fraisCourant = Math.round((FOOD_BEV_HT + RETAIL_HT + DISQUAIRE_HT + SERVICES_HT) * SQUARE_FEE_RATE);
  const _margeCourant = TOTAL_CA_HT - _fraisCourant - _cogsMoy3M;
  const _raiCourant   = _margeCourant - _cfCourant - _chargesAnnuMens;

  const _annee    = currentPeriode.split('-')[0];
  const _moisClos = Object.keys(notionFinances?.cafe || {}).filter(p => p.startsWith(_annee) && p < currentPeriode).sort();
  let _raiYTD = 0, _isAccum = 0;

  const _raisAnnee = {};
  for (const p of _moisClos) {
    const r = _raiNotion(p);
    if (!r) continue;
    _raiYTD += r.rai;
    const isDu = Math.max(0, Math.min(_raiYTD, IS_SEUIL) * IS_RATE_1 + Math.max(0, _raiYTD - IS_SEUIL) * IS_RATE_2);
    const prov = isDu - _isAccum;
    _isAccum  += prov;
    _raisAnnee[p] = { net: Math.round(r.rai - prov) };
  }

  const _factProj   = dayOfMonth > 0 ? daysInMonth / dayOfMonth : 0;
  // Extrapoler uniquement CA et frais variables (Square fees), pas les COGS ni charges fixes mensuelles
  // Projection fiable uniquement à partir de J5 (1 semaine de travail représentative)
  const MIN_PROJ_DAYS = 5;
  const _caProj     = Math.round(TOTAL_CA_HT * _factProj);
  const _fraisProj  = Math.round(_fraisCourant * _factProj);
  const _raiProj    = dayOfMonth >= MIN_PROJ_DAYS ? Math.round(_caProj - _fraisProj - _cogsMoy3M - _cfCourant - _chargesAnnuMens) : null;
  const _isDuProj   = _raiProj !== null
    ? Math.max(0, Math.min(_raiYTD + _raiProj, IS_SEUIL) * IS_RATE_1 + Math.max(0, _raiYTD + _raiProj - IS_SEUIL) * IS_RATE_2)
    : null;
  const _provISProj = _isDuProj !== null ? Math.round(_isDuProj - _isAccum) : null;
  const _netProj    = _raiProj !== null ? _raiProj - (_provISProj ?? 0) : null;

  const _last3   = Object.keys(notionFinances?.cafe || {}).filter(p => p < currentPeriode).sort().slice(-3);
  const _gliss3M = _last3.length > 0 ? (() => {
    const nets = _last3.map(p => {
      if (_raisAnnee[p]) return _raisAnnee[p].net;
      const r = _raiNotion(p);
      if (!r) return 0;
      return Math.round(r.rai - Math.max(0, r.rai) * IS_RATE_1);
    });
    return { avg: Math.round(nets.reduce((a, b) => a + b, 0) / nets.length), nb: _last3.length };
  })() : null;

  const _ytdNet = _moisClos.length > 0 ? _moisClos.reduce((s, p) => s + (_raisAnnee[p]?.net ?? 0), 0) : null;

  // ── Helper formatting pour DetailPanel ───────────────────────────────────────
  const _F = (v, d = 0) => new Intl.NumberFormat('fr-FR', { style:'currency', currency:'EUR', maximumFractionDigits:d, minimumFractionDigits:d }).format(v);
  const _signed = v => v >= 0 ? `+${_F(v)}` : `−${_F(Math.abs(v))}`;

  // ── Panels de détail — données construites au niveau App ─────────────────────
  // (les panels café IIFE utilisent setDetailPanel directement avec ces builders)

  const _panelNetProj = () => {
    const caSquareProj  = Math.round(FOOD_BEV_HT + RETAIL_HT + DISQUAIRE_HT + SERVICES_HT) * (dayOfMonth > 0 ? daysInMonth / dayOfMonth : 1);
    const caStripeProj  = Math.round(stripeCurrentMonth * (dayOfMonth > 0 ? daysInMonth / dayOfMonth : 1));
    const fraisProj     = Math.round(_fraisCourant * (dayOfMonth > 0 ? daysInMonth / dayOfMonth : 1));
    const cogsLbl       = `Moyenne 3 derniers mois`;
    const CF = cfRef;
    return {
      title: 'Résultat net — détail',
      subtitle: `Café · Projeté J${dayOfMonth}→J${daysInMonth}`,
      rows: [
        { type:'sep', label:'CHIFFRE D\'AFFAIRES PROJETÉ' },
        { type:'item', label:'CA Square HT projeté', value:_signed(Math.round(TOTAL_CA_HT * (dayOfMonth > 0 ? daysInMonth / dayOfMonth : 1))), note:`Extrapolé depuis J${dayOfMonth} sur ${daysInMonth} jours` },
        { type:'item', label:'CA Stripe HT projeté', value:_signed(Math.round(stripeCurrentMonth / 1.20 * (dayOfMonth > 0 ? daysInMonth / dayOfMonth : 1))), indent:true, dim:true },
        { type:'item', label:`Frais Square (${SQUARE_FEE_RATE*100}%)`, value:`−${_F(fraisProj)}`, indent:true, dim:true },
        { type:'item', label:`COGS — ${cogsLbl}`, value:`−${_F(_cogsMoy3M)}`, indent:true, dim:true, note:'Achats fournisseurs cat. Notion' },
        { type:'result', label:'Marge brute projetée', value:_signed(_raiProj !== null ? _raiProj + _cfCourant + _chargesAnnuMens : 0), color: (_raiProj ?? 0) + _cfCourant + _chargesAnnuMens >= 0 ? SHADCN.success : SHADCN.error },
        { type:'sep', label:'CHARGES FIXES (MOYENNE 3 MOIS BANQUE)' },
        { type:'item', label:'Coût salarial', value:`−${_F(coutSalarialCharge)}`, note:'Salaires + URSSAF + Klesia + ALAN décaissés − refacturation Léo' },
        { type:'item', label:'Loyer SCI', value:`−${_F(CF.loyerSCI)}` },
        { type:'item', label:'Prêts BNP', value:`−${_F(CF.pretsBNP)}` },
        { type:'item', label:'Comptable Syla', value:`−${_F(CF.syla)}` },
        { type:'item', label:'Assurance AXA', value:`−${_F(CF.assuranceAXA)}`, dim:true },
        { type:'item', label:'Verisure', value:`−${_F(CF.verisure)}`, dim:true },
        { type:'item', label:'Télécom Orange', value:`−${_F(CF.orange)}`, dim:true },
        { type:'item', label:'Charges annuelles lissées', value:`−${_F(_chargesAnnuMens)}`, dim:true, note:'CFE + SACEM + maintenance / 12' },
        { type:'result', label:'RAI projeté', value:_raiProj !== null ? _signed(_raiProj) : '—', color: (_raiProj ?? 0) >= 0 ? SHADCN.success : SHADCN.error },
        { type:'item', label:'Provision IS', value:_provISProj !== null ? `−${_F(Math.max(0, _provISProj))}` : '—', indent:true, dim:true, note:`IS 15% jusqu'à ${_F(IS_SEUIL)} · 25% au-delà` },
        { type:'final', label:'Résultat net projeté', value:_netProj !== null ? _signed(_netProj) : '—', color: (_netProj ?? 0) >= 200 ? SHADCN.success : (_netProj ?? 0) > -200 ? SHADCN.warning : SHADCN.error },
        { type:'note', text:`Seul le CA est projeté au prorata. Les charges fixes sont comptées en totalité (elles tombent quoi qu'il arrive).` },
      ],
    };
  };

  const _panelGliss3M = () => {
    const MOIS_FR_COURT = ['jan','fév','mar','avr','mai','jun','jul','aoû','sep','oct','nov','déc'];
    const rows = [{ type:'sep', label:'3 DERNIERS MOIS COMPLETS' }];
    const nets = _last3.map(p => {
      const net = _raisAnnee[p]?.net ?? Math.round((_raiNotion(p)?.rai ?? 0) * (1 - IS_RATE_1));
      const [, m] = p.split('-');
      return { label: MOIS_FR_COURT[parseInt(m,10)-1] + ' ' + p.split('-')[0], value: net };
    });
    nets.forEach(({ label, value }) => {
      rows.push({ type:'item', label, value: _signed(value), color: value >= 0 ? SHADCN.success : SHADCN.error });
    });
    if (nets.length > 0) {
      const avg = Math.round(nets.reduce((s, n) => s + n.value, 0) / nets.length);
      rows.push({ type:'final', label:`Moyenne sur ${nets.length} mois`, value:_signed(avg), color: avg >= 0 ? SHADCN.success : SHADCN.error });
      rows.push({ type:'note', text:'Résultat net après IS — mois complets uniquement, hors mois en cours.' });
    }
    return { title:'Glissant 3 mois', subtitle:'Café Sonore', rows };
  };

  const _panelYTD = () => {
    const MOIS_FR_LONG = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
    const rows = [{ type:'sep', label:`MOIS COMPLETS ${_annee}` }];
    _moisClos.forEach(p => {
      const net = _raisAnnee[p]?.net ?? 0;
      const [, m] = p.split('-');
      rows.push({ type:'item', label:MOIS_FR_LONG[parseInt(m,10)-1], value:_signed(net), color: net >= 0 ? SHADCN.success : SHADCN.error });
    });
    if (_ytdNet !== null) {
      rows.push({ type:'final', label:`Total ${_annee}`, value:_signed(_ytdNet), color: _ytdNet >= 0 ? SHADCN.success : SHADCN.error });
      rows.push({ type:'note', text:'Hors mois en cours. Provision IS appliquée mois par mois (méthode progressive).' });
    }
    return { title:`Cumul YTD ${_annee}`, subtitle:'Café Sonore', rows };
  };

  const _panelCharges = () => ({
    title: 'Charges à venir',
    subtitle: `Café · ${daysLeft} jours restants`,
    rows: [
      { type:'sep', label:'CHARGES EN ATTENTE' },
      ...[...chargesATomber].sort((a,b) => (a.jourMois||99) - (b.jourMois||99)).map(c => ({
        type:'item', label:c.label,
        value:`−${_F(c.montant)}`,
        note: c.jourMois ? `Vers le ${c.jourMois} du mois` : undefined,
      })),
      { type:'final', label:`Total à sortir`, value:`−${_F(montantATomber)}`, color:SHADCN.error },
      { type:'note', text:'Estimations basées sur historique — à confirmer via la banque.' },
    ],
  });

  // Bureau Sonore — pilotage
  const bPilotage        = data.bureau?.pilotage || {};
  const bCF              = bPilotage.chargesFixes || {};
  const bCV              = bPilotage.chargesVariables || {};
  const bSeuilRenta      = bPilotage.seuilRenta;
  // Préfère les données live Qonto si disponibles, sinon data.json
  // Qonto retourne 6 mois glissants — on filtre pour ne garder que l'année en cours
  const bQontoLive       = !!(qontoTreso?.historique?.length);
  const bAnnee           = now.getFullYear();
  // ── CA COMPTABLE ──────────────────────────────────────────────────────────
  // Source de vérité = factures clients émises (Qonto Facturation), HT, au mois
  // d'émission. C'est le CA du bilan : comparable au seuil de renta, à l'objectif
  // et aux CA 2024/2025 de data.json (eux aussi HT).
  // Les crédits Qonto (`recettes`) ne sont PAS du CA : ils incluent la TVA, les
  // apports en CCA et les virements entre comptes. Ils restent la source de
  // secours si /client_invoices n'a rien renvoyé.
  const bCaFacture       = qontoTreso?.caFacture?.mois ? qontoTreso.caFacture : null;
  const bCaSource        = bCaFacture ? 'facture' : 'encaissement';
  // Dépenses / flux internes : uniquement dispo sur les 6 mois glissants Qonto
  const bTxParPeriode    = {};
  if (bQontoLive) {
    for (const m of qontoTreso.historique) {
      bTxParPeriode[m.periode] = {
        depenses: m.depenses || 0,
        // Virements Qonto ↔ BNP exclus des dépenses par l'API — montant tracé pour affichage
        fluxInternes: m.fluxInternes || 0,
        // /api/qonto-treso renvoie montant en négatif — on normalise au positif (montant absolu de la dépense)
        topDepenses: (m.topDepenses || []).map(t => ({ label: t.label, montant: Math.abs(t.montant || 0) })),
      };
    }
  }
  const bCaHistoRaw      = bCaFacture
    // Janvier → mois en cours : la série couvre toute l'année, pas seulement les
    // 6 mois glissants remontés par les transactions Qonto.
    ? Array.from({ length: now.getMonth() + 1 }, (_, i) => {
        const periode = `${bAnnee}-${String(i + 1).padStart(2, '0')}`;
        const fact    = bCaFacture.mois[periode] || {};
        const tx      = bTxParPeriode[periode];
        return {
          mois: periode, label: MOIS_ABBR[i], enCours: i === now.getMonth(),
          ca: fact.ht || 0, nbFactures: fact.nb || 0,
          depenses: tx?.depenses || 0,
          depensesDispo: !!tx,
          fluxInternes: tx?.fluxInternes || 0,
          topDepenses: tx?.topDepenses || [],
        };
      })
    : bQontoLive
      ? qontoTreso.historique
          .filter(m => m.periode >= `${bAnnee}-01`)
          .map(m => ({
            mois: m.periode, label: m.label, ca: m.recettes, enCours: m.enCours,
            depenses: m.depenses, depensesDispo: true,
            fluxInternes: m.fluxInternes || 0,
            topDepenses: (m.topDepenses || []).map(t => ({ label: t.label, montant: Math.abs(t.montant || 0) })),
          }))
      : (bPilotage.caHistorique || []);
  const bCaHisto         = bCaHistoRaw;
  const bMoisEnCours     = bCaHisto.find(m => m.enCours) || bCaHisto[bCaHisto.length - 1] || {};
  const bCaMois          = bMoisEnCours.ca || 0;
  const bCouverturePct   = bSeuilRenta ? Math.round(Math.min(bCaMois / bSeuilRenta, 1) * 100) : 0;
  const bManqueSeuil     = Math.max(0, (bSeuilRenta || 0) - bCaMois);
  const bCaYTD           = Math.round(bCaHisto.reduce((s, m) => s + (m.ca || 0), 0));
  const bNbFacturesYTD   = bCaHisto.reduce((s, m) => s + (m.nbFactures || 0), 0);
  const bMoisEcoules     = Math.max(1, bCaHisto.filter(m => !m.enCours).length);
  const bCaMoyenMois     = Math.round(bCaYTD / bMoisEcoules);
  const bChargesTotal    = bCF.total || 0;
  const bRemuTotal       = bPilotage.remuneration?.total || 0;
  const bObjectifAnnuel  = bPilotage.objectifAnnuel || (bSeuilRenta ? bSeuilRenta * 12 : 0);
  const bCouvAnnuelPct   = bObjectifAnnuel ? Math.round(bCaYTD / bObjectifAnnuel * 100) : 0;
  const bManqueAnnuel    = Math.max(0, bObjectifAnnuel - bCaYTD);
  const bCa2024          = data.bureau?.ca2024 || 0;
  const bCa2025          = data.bureau?.ca2025 || 0;
  const bCvTauxMoyen     = bCV.tauxMoyen || 0;
  const bCvPostes        = bCV.postes || [];
  // ── Dépenses réelles depuis Qonto live (pas de cat. côté Qonto) ───────────
  // bCaHisto contient déjà depenses + topDepenses par mois.
  // Variables = Qonto − fixes RÉCURRENTS MENSUELS (loyer, assurance, abonnements…).
  // L'URSSAF (trimestrielle) reste dans les variables les mois où elle tombe.
  //
  // ⚠️ Fenêtres différentes : le CA couvre janvier → mois en cours (factures), les
  // dépenses seulement les 6 mois glissants remontés par Qonto. Les marges sont donc
  // calculées sur la FENÊTRE COMMUNE (mois où les deux existent), sinon on
  // soustrairait 6 mois de dépenses d'un CA de 7 mois.
  const bMoisDep           = bCaHisto.filter(m => m.depensesDispo !== false);
  const bMoisEcoulesDep    = Math.max(1, bMoisDep.filter(m => !m.enCours).length);
  const bFenetreDep        = bMoisDep.length && bMoisDep.length < bCaHisto.length
    ? `${bMoisDep[0].label} → ${bMoisDep[bMoisDep.length - 1].label}`
    : null;
  const bCaFenetreDep      = Math.round(bMoisDep.reduce((s, m) => s + (m.ca || 0), 0));
  const bDepensesYTD       = Math.round(bMoisDep.reduce((s, m) => s + (m.depenses || 0), 0));
  const bDepensesMois      = Math.round(bMoisEnCours.depenses || 0);
  const bTopDepensesMois   = bMoisEnCours.topDepenses || [];
  const bChargesFixMensuel = bChargesTotal;
  const bChargesFixProrata = Math.round(bChargesFixMensuel * bMoisEcoulesDep);
  const bChargesFixesAn    = bChargesFixMensuel * 12 + bRemuTotal * 12;
  const bChargesVarReel    = Math.max(0, bDepensesYTD  - bChargesFixProrata);
  const bChargesVarMois    = Math.max(0, bDepensesMois - bChargesFixMensuel);
  const bTauxVarReel       = bCaFenetreDep > 0 ? Math.round(bChargesVarReel / bCaFenetreDep * 100) : 0;
  const bTauxVarMois       = bCaMois > 0 ? Math.round(bChargesVarMois / bCaMois * 100) : 0;
  const bMargeBrute        = bCaFenetreDep - bChargesVarReel;
  const bMargeNette        = bMargeBrute - bChargesFixProrata - Math.round(bRemuTotal * bMoisEcoulesDep);
  // Couverture attendue à la date du jour (ex: fin mai = 5/12 = 41%)
  const bRythmeAttendu   = Math.round((bMoisEcoules / 12) * 100);
  const bSeuilColor      = bCouverturePct >= 100 ? SHADCN.success : bCouverturePct >= 70 ? SHADCN.warning : SHADCN.error;
  const bAnnuelColor     = bCouvAnnuelPct >= bRythmeAttendu ? SHADCN.success : bCouvAnnuelPct >= bRythmeAttendu * 0.7 ? SHADCN.warning : SHADCN.error;

  const statusColor = (val, good, warn) => val >= good ? SHADCN.success : val >= warn ? SHADCN.warning : SHADCN.error;

  // ── Render ────────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    // Cleanup legacy keys — la vraie session vit dans le cookie httpOnly,
    // que /api/logout (appelé par index.js → onLogout) se chargera d'effacer.
    try {
      sessionStorage.removeItem('sonore_auth');
      sessionStorage.removeItem('sonore_auth_ts');
      sessionStorage.removeItem('sonore_pwd');
    } catch (_) {}
    if (onLogout) onLogout();
    else window.location.reload();
  };

  const handleSync = () => window.location.reload(true);

  const handleSidebarToggle = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try { localStorage.setItem('sonore_sidebar_collapsed', next ? '1' : '0'); } catch (_) {}
  };

  const sidebarW   = isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_W_CLOSE : SIDEBAR_W_OPEN);
  const debugData  = { version: APP_VERSION, data, squareData, notionFinances, qontoTreso, uploads, generatedAt: new Date().toISOString() };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: SHADCN.bg, color: SHADCN.fg, fontFamily: SHADCN.font }}>

      {/* ── Popup Nouveautés ─────────────────────────────────────────────── */}
      {rappelFermeture && (
        <Suspense fallback={null}>
          <FermetureRappel open={rappelFermeture} onLater={reporterFermeture} onClose={cloreFermeture} />
        </Suspense>
      )}
      {showWhatsNew && <WhatsNewModal entries={whatsNewEntries} onClose={dismissWhatsNew} isMobile={isMobile} />}

      {/* ── Modal Debug API ──────────────────────────────────────────────── */}
      {showDebug && (
        <div style={{ position:'fixed', inset:0, background:SHADCN.overlayStrong, zIndex:9999, display:'flex', flexDirection:'column' }}>
          <div style={{ background:SHADCN.bg, margin: S[5], borderRadius: R.md, flex:1, overflow:'hidden', display:'flex', flexDirection:'column', border:`1px solid ${SHADCN.border}` }}>
            <div style={{ padding:'16px 20px', borderBottom:`1px solid ${SHADCN.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap: S[3] }}>
                <span style={{ ...TYPO.h2, color:SHADCN.fg }}>Debug — Données API</span>
                <span style={{ ...TYPO.eyebrow, ...TYPO.num, color:SHADCN.mutedFg }}>v{APP_VERSION}</span>
              </div>
              <button onClick={() => setShowDebug(false)} style={{ background:SHADCN.muted, border:`1px solid ${SHADCN.border}`, borderRadius: R.md, color:SHADCN.fg, cursor:'pointer', width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <X size={16} strokeWidth={2} />
              </button>
            </div>
            <pre style={{ flex:1, overflow:'auto', margin:0, padding:'20px', fontFamily:'monospace', fontSize:11, color:SHADCN.fg, lineHeight:1.5, background:SHADCN.bg }}>
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {!isMobile && <Sidebar activeTab={displayTab} activeSection={displayActiveSection} sections={displaySections} activeEntity={displayEntity} entities={entities} setActiveTab={navSetTab} setActiveSection={navSetSection} setActiveEntity={navSetEntity} onOpenPanel={openPanel} panelOpen={panelOpen} onLogout={handleLogout} onSync={handleSync} collapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />}
      {isMobile && <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} activeTab={displayTab} activeSection={displayActiveSection} sections={displaySections} activeEntity={displayEntity} entities={entities} setActiveTab={navSetTab} setActiveSection={navSetSection} setActiveEntity={navSetEntity} onOpenPanel={openPanel} panelOpen={panelOpen} onLogout={handleLogout} onSync={handleSync} />}

      <div style={{ flex: 1, marginLeft: sidebarW, minWidth: 0, display: 'flex', flexDirection: 'column', transition: 'margin-left 0.22s ease', position: 'relative' }}>
        <TopBar isMobile={isMobile} onMenuClick={() => setDrawerOpen(true)} onClose={panelOpen ? closePanel : null} activeTab={displayTab} activeSection={displayActiveSection} sections={displaySections} setActiveTab={navSetTab} setActiveSection={navSetSection} role={role} />

        {/* ── Panneau overlay (Outils / Réglages) ───────────────────────── */}
        {panelOpen && (
          <>
            <div onClick={closePanel} style={{
              position: 'fixed', inset: 0, background: SHADCN.overlay,
              zIndex: 250, animation: 'sonore-fade-in 180ms ease-out',
            }} />
            <button onClick={closePanel} aria-label="Fermer" style={{
              position: 'fixed', top: 14, right: 18, zIndex: 252,
              width: 36, height: 36, borderRadius: R.md,
              background: SHADCN.cardSolid, border: `1px solid ${SHADCN.border}`,
              color: SHADCN.fg, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: SHADCN.shadowMd,
            }}>
              <X size={16} strokeWidth={2} />
            </button>
          </>
        )}

        <div style={panelOpen ? {
          position: 'fixed',
          top: isMobile ? 8 : 28,
          bottom: isMobile ? 8 : 28,
          left: isMobile ? 8 : (sidebarW + 28),
          right: isMobile ? 8 : 28,
          zIndex: 251,
          background: SHADCN.bg,
          border: `1px solid ${SHADCN.border}`,
          borderRadius: R.lg,
          boxShadow: SHADCN.shadowXl,
          overflowY: 'auto',
          padding: 0,
        } : { flex: 1, maxWidth: 1100, margin: '0 auto', width: '100%', padding: isMobile ? '16px 14px' : '28px 32px' }}>

          {panelOpen && (() => {
            const panelTabs = getAllTabs(activeEntity, role, session);
            return (
              <div style={{
                position: 'sticky', top: 0, zIndex: 2,
                background: SHADCN.bg,
                borderBottom: `1px solid ${SHADCN.border}`,
                padding: isMobile ? '14px 16px 0' : '18px 32px 0',
                display: 'flex', gap: 2, overflowX: 'auto',
              }}>
                {panelTabs.map(t => {
                  const active = activeTab === t.id;
                  return (
                    <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                      ...TYPO.body, fontWeight: active ? 600 : 400,
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      background: 'transparent', border: 'none',
                      borderBottom: `2px solid ${active ? SHADCN.fg : 'transparent'}`,
                      color: active ? SHADCN.fg : SHADCN.mutedFg,
                      padding: '10px 14px 12px', cursor: 'pointer', whiteSpace: 'nowrap',
                    }}>
                      {t.Icon && <t.Icon size={14} strokeWidth={active ? 2 : 1.7} />}
                      {t.label}
                    </button>
                  );
                })}
              </div>
            );
          })()}

          <div style={panelOpen ? { padding: isMobile ? '20px 16px' : '28px 36px' } : undefined}>

          {/* ═════════════ BANDEAU PANNE SOURCES — visible seulement si problème ═════════════ */}
          {['today','cafe','bureau_global','sci'].includes(activeTab) && (() => {
            const SOURCE_LABELS = { square:'Square (caisse Café)', finances:'Supabase (transactions)', qonto:'Qonto (Bureau)', revolut:'Revolut (soldes Café)', stripe:'Stripe (cowork)' };
            const down = Object.entries(sourcesHealth).filter(([, s]) => s && s.ok === false);
            if (!down.length) return null;
            return (
              <div style={{ display:'flex', alignItems:'flex-start', gap:S[3], padding:`${S[3]}px ${S[4]}px`, marginBottom:S[4], borderRadius:R[2], border:`1px solid ${SHADCN.error}`, background:SHADCN.card }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:SHADCN.error, flexShrink:0, marginTop:5 }} />
                <div>
                  <div style={{ ...TYPO.body, fontWeight:500, color:SHADCN.fg }}>
                    {down.length === 1 ? 'Une source de données ne répond plus' : `${down.length} sources de données ne répondent plus`} — chiffres possiblement incomplets
                  </div>
                  <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop:2 }}>
                    {down.map(([k, s]) => `${SOURCE_LABELS[k] || k} : ${s.error || 'erreur'}`).join(' · ')}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ══════════════════════════ AUJOURD'HUI ════════════════════════ */}
          {activeTab === 'today' && (() => {
            const JOURS_FR = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
            const todayISO = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
            const isToday  = selectedDay === todayISO;
            const selDate  = isToday ? now : new Date(selectedDay + 'T12:00:00');
            const changeDay = (delta) => {
              const d = new Date(selectedDay + 'T12:00:00');
              d.setDate(d.getDate() + delta);
              const next = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              if (next <= todayISO) setSelectedDay(next);
            };
            const jourSemaine = JOURS_FR[selDate.getDay()];
            const hr = isToday ? now.getHours() : 23;
            const momentJour = hr < 6 ? 'nuit' : hr < 12 ? 'matin' : hr < 14 ? 'midi' : hr < 18 ? 'après-midi' : hr < 22 ? 'soir' : 'nuit';
            const caJourNecessaire = daysLeft > 0 ? Math.round(manqueCA / daysLeft) : 0;

            // Ventes du jour — Square live (aujourd'hui) ou détail Square du jour consulté (passé)
            const pastDay   = !isToday && jourSelectedDay?.date === selectedDay ? jourSelectedDay : null;
            const todayData = isToday ? squareData?.aujourd_hui : pastDay?.jour ?? null;
            // Libellés : « aujourd'hui » seulement pour le jour courant
            const leJour    = isToday ? 'aujourd\'hui' : 'ce jour-là';
            // Cumuls « Ce mois » servis par la route par défaut = mois courant uniquement
            const memeMois  = selectedDay.slice(0, 7) === todayISO.slice(0, 7);
            const caTodayTTC  = todayData?.totalTTC  ?? 0;
            const caTodayHT   = todayData?.totalHT   ?? 0;
            const todayGroups = todayData?.groups     ?? {};
            const todayNb     = todayData?.nbCommandes ?? 0;
            const todayGroupsTotal = Object.values(todayGroups).reduce((s, v) => s + v, 0);
            const todayCategories  = todayData?.categories ?? [];

            // Même jour la semaine dernière (jusqu'à la même heure) — pour deltas comparatifs
            const j7Data = isToday ? squareData?.j_moins_7 : pastDay?.j_moins_7;
            const j7Label = isToday ? 'J-7 même heure' : 'J-7';
            const j7Nb   = j7Data?.nbCommandes ?? null;
            const buildDeltaJ7 = (todayVal, j7Val, suffix = 'vs même jour sem. dernière') => {
              if (j7Val == null || j7Val === 0 || todayVal == null) return null;
              const pct = Math.round((todayVal - j7Val) / j7Val * 100);
              return { value: Math.abs(pct), direction: pct >= 0 ? 'up' : 'down', suffix };
            };
            // Palette chart shadcn : Café=jaune (chart4), Retail=cyan (chart1), Services=mint (chart3).
            // SHADCN.chart4 et SHADCN.warning pointaient tous deux sur chart4 → indistinguables.
            const GROUP_COLORS = { Café: SHADCN.chart4, Disquaire: SHADCN.chart2, Retail: SHADCN.chart1, Services: SHADCN.success };

            // ── Équipe au café — uniquement pour le jour courant
            const nowMin = isToday ? (now.getHours()*60 + now.getMinutes()) : 0;
            const jourCourant = isToday ? squareTeam?.semaines?.[0]?.jours?.find(j => j.date === todayISO) : null;
            const shiftsAuj = (jourCourant?.shifts ?? []).slice().sort((a,b) => a.debutMin - b.debutMin);
            const enServiceMaintenant = shiftsAuj.filter(sh => sh.debutMin <= nowMin && nowMin < sh.finMin);

            // ── Statut ouverture café : fusionne shifts qui se chevauchent en plages d'ouverture
            const plagesOuverture = [];
            shiftsAuj.forEach(sh => {
              const last = plagesOuverture[plagesOuverture.length - 1];
              if (last && sh.debutMin <= last.end) last.end = Math.max(last.end, sh.finMin);
              else plagesOuverture.push({ start: sh.debutMin, end: sh.finMin });
            });
            const plageEnCours = plagesOuverture.find(p => p.start <= nowMin && nowMin < p.end);
            const plageSuivante = plagesOuverture.find(p => p.start > nowMin);
            const fmtH = (m) => { const h = Math.floor(m/60), mn = m%60; return mn === 0 ? `${h}h` : `${h}h${String(mn).padStart(2,'0')}`; };
            let cafeStatut;
            if (plageEnCours) cafeStatut = { variant: 'success', label: 'ouvert' };
            else if (plageSuivante) cafeStatut = { variant: 'warning', label: `Café fermé · ouvre à ${fmtH(plageSuivante.start)}` };
            else if (shiftsAuj.length > 0) cafeStatut = { variant: 'neutral', label: 'Café fermé · journée terminée' };
            else cafeStatut = { variant: 'neutral', label: 'Café fermé aujourd\'hui' };

            const hasObjectif = caJourNecessaire > 0;
            const deltaJour   = caTodayHT - caJourNecessaire;
            const onTrack     = hasObjectif && caTodayHT >= caJourNecessaire;

            return (
              <>
                {/* ── En-tête harmonisé ────────────────────────────────────── */}
                <PageHeader
                  eyebrow={`${jourSemaine}${isToday ? ` · ${momentJour}` : ''}`}
                  title={<TodayDateTitle title={`${selDate.getDate()} ${MOIS_FR[selDate.getMonth()]} ${selDate.getFullYear()}`} now={isToday ? now : null} />}
                  actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button onClick={() => changeDay(-1)} aria-label="Jour précédent" style={{ background: 'none', border: 'none', padding: '4px 6px', borderRadius: R.sm, cursor: 'pointer', color: SHADCN.mutedFg, display: 'flex', alignItems: 'center' }}>
                        <ChevronLeft size={18} />
                      </button>
                      <button onClick={() => changeDay(1)} aria-label="Jour suivant" disabled={isToday} style={{ background: 'none', border: 'none', padding: '4px 6px', borderRadius: R.sm, cursor: isToday ? 'default' : 'pointer', color: isToday ? SHADCN.border : SHADCN.mutedFg, display: 'flex', alignItems: 'center' }}>
                        <ChevronRight size={18} />
                      </button>
                      {/* Sauter à une date : les flèches seules demandaient vingt clics
                          pour remonter à un samedi d'il y a trois semaines. */}
                      <DayPicker value={selectedDay} max={todayISO} onChange={setSelectedDay} label="Choisir un jour" />
                      {isToday && squareTeam ? (cafeStatut.variant === 'success'
                        ? <LiveIndicator variant="success">{cafeStatut.label}</LiveIndicator>
                        : <StatusPill dot variant={cafeStatut.variant}>{cafeStatut.label}</StatusPill>) : null}
                    </div>
                  }
                />

                {/* ── Ticket résolu aujourd'hui (annonce au demandeur) ─────── */}
                <Suspense fallback={null}>
                  <TicketsResolusBanner userId={session?.id} />
                </Suspense>

                {/* ── Mes tâches assignées (todos) ─────────────────────────── */}
                <Suspense fallback={null}>
                  <MesTodos
                    squareMemberId={session?.square_member_id}
                    prenom={session?.prenom}
                    userId={session?.id}
                    membres={squareTeam?.membres ?? []}
                  />
                </Suspense>

                {/* ── Données Square indisponibles ─────────────────────────── */}
                {squareSettled && !squareData && (
                  <Alert variant="warning" style={{ marginBottom: S[4] }}>
                    Square indisponible. Les ventes du jour ne sont pas affichées ; recharge la page pour réessayer.
                  </Alert>
                )}

                {/* ── Alerte TVA : ventes du jour avec taux illégal ─────────── */}
                {tvaToday?.nbAnomalies > 0 && (() => {
                  const list = tvaToday.anomalies || [];
                  const fmtPct = (p) => Number.isFinite(p) ? `${(+p).toFixed(p % 1 === 0 ? 0 : 2)} %` : '?';
                  return (
                    <Card tone="danger" style={{ marginBottom: S[4], borderColor: softTone(SHADCN.error).border }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap: S[3] }}>
                        <AlertTriangle size={22} color={SHADCN.error} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...TYPO.h3, color: SHADCN.error, marginBottom: 4 }}>
                            TVA suspecte sur {tvaToday.nbAnomalies} vente{tvaToday.nbAnomalies > 1 ? 's' : ''} aujourd'hui
                          </div>
                          <div style={{ ...TYPO.body, color: SHADCN.fg, marginBottom: S[3] }}>
                            Taux appliqué hors {tvaToday.tauxLegaux?.map(fmtPct).join(' / ')}. À corriger dans Square (catalogue → article → TVA).
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap: 6 }}>
                            {list.slice(0, 12).map((a, i) => (
                              <div key={a.orderId + '-' + i} style={{
                                display:'grid',
                                gridTemplateColumns: isMobile ? '52px 1fr auto' : '52px 1fr 140px 80px',
                                gap: S[2], alignItems:'center',
                                padding: '6px 10px', borderRadius: R.md,
                                background: 'oklch(0.577 0.245 27.325 / 0.06)',
                                fontVariantNumeric: 'tabular-nums', fontSize: 13,
                              }}>
                                <span style={{ color: SHADCN.mutedFg, fontFamily: SHADCN.font }}>{a.heure}</span>
                                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {a.quantite}× {a.article}
                                  {a.categorie && <span style={{ color: SHADCN.mutedFg }}> · {a.categorie}</span>}
                                </span>
                                {!isMobile && (
                                  <span style={{ color: SHADCN.error, fontWeight: 600 }}>
                                    TVA {a.tauxIllegaux.map(fmtPct).join(' + ')}
                                  </span>
                                )}
                                <span style={{ textAlign:'right', color: SHADCN.fg }}>{fmtFull(a.montantTTC)}</span>
                              </div>
                            ))}
                            {list.length > 12 && (
                              <div style={{ ...TYPO.small, color: SHADCN.mutedFg, marginTop: 4 }}>
                                …et {list.length - 12} autre{list.length - 12 > 1 ? 's' : ''}.
                              </div>
                            )}
                          </div>
                          <div style={{ marginTop: S[3] }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={fetchTvaToday}
                              disabled={tvaTodayLoading}
                              loading={tvaTodayLoading}
                              loadingText="Vérification…"
                            >
                              Revérifier
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })()}

                {/* ── Alerte stocks bas — rouge = réassort urgent (seuils par
                       catégorie côté API), jaune = à surveiller ─────────────── */}
                {stockAlerts?.length > 0 && (() => {
                  const nbRouges = stockAlerts.filter(a => a.niveau === 'rouge').length;
                  const toneCol  = nbRouges > 0 ? SHADCN.error : SHADCN.warning;
                  return (
                  <Card tone={nbRouges > 0 ? 'error' : 'warning'} style={{ marginBottom: S[4], borderColor: softTone(toneCol).border }}>
                    <button
                      onClick={() => setStockAlertsOpen(o => !o)}
                      style={{ display:'flex', alignItems:'center', gap: S[3], width:'100%', background:'none', border:'none', padding:0, cursor:'pointer', textAlign:'left' }}
                    >
                      <Package size={22} color={toneCol} strokeWidth={2} style={{ flexShrink: 0 }} />
                      <span style={{ ...TYPO.h3, color: toneCol, flex: 1 }}>
                        {stockAlerts.length} article{stockAlerts.length > 1 ? 's' : ''} en stock bas
                        {nbRouges > 0 ? ` — ${nbRouges} réassort${nbRouges > 1 ? 's' : ''} urgent${nbRouges > 1 ? 's' : ''}` : ''}
                      </span>
                      <ChevronDown size={16} color={toneCol} style={{ flexShrink: 0, transition: 'transform 0.2s', transform: stockAlertsOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                    </button>
                    <div style={{ overflow: 'hidden', maxHeight: stockAlertsOpen ? 1000 : 0, transition: 'max-height 0.25s ease' }}>
                      <div style={{ display:'flex', flexDirection:'column', gap: 4, marginTop: S[2] }}>
                        {stockAlerts.slice(0, 10).map(a => {
                          const rouge = a.niveau === 'rouge';
                          const col   = rouge ? SHADCN.error : SHADCN.warning;
                          return (
                          <div key={a.id} style={{
                            display:'flex', justifyContent:'space-between', alignItems:'center',
                            padding: '4px 10px', borderRadius: R.md,
                            background: softTone(col).bg,
                          }}>
                            <span style={{ ...TYPO.body, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color: rouge ? SHADCN.error : undefined }}>
                              {a.name}
                              {a.category && <span style={{ color: SHADCN.mutedFg }}> · {a.category}</span>}
                            </span>
                            <span style={{ ...TYPO.caption, fontWeight: 600, flexShrink: 0, marginLeft: S[3], color: col }}>
                              {a.stock === 0 ? 'Épuisé' : `${a.stock} restant${a.stock > 1 ? 's' : ''}`}
                            </span>
                          </div>
                          );
                        })}
                        {stockAlerts.length > 10 && (
                          <div style={{ ...TYPO.small, color: SHADCN.mutedFg, marginTop: 2 }}>
                            …et {stockAlerts.length - 10} autre{stockAlerts.length - 10 > 1 ? 's' : ''}.
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                  );
                })()}

                {/* ── Hero du jour : CA dominant à gauche, secondaires à droite ── */}
                {(() => {
                  const SPACE_SECTION = S[6]; // 32 — rythme entre sections distinctes
                  const EYEBROW = { fontFamily: SHADCN.font, fontWeight: 500, fontSize: 11, lineHeight: 1.4, letterSpacing: '0.08em', textTransform: 'uppercase', color: SHADCN.mutedFg };

                  if (squareLoading) {
                    return (
                      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '7fr 5fr', gap: S[5], marginBottom: SPACE_SECTION }}>
                        <Skeleton h={140} />
                        <Skeleton h={140} />
                      </div>
                    );
                  }

                  const deltaPct = hasObjectif && caTodayHT > 0 && !estRentable
                    ? Math.abs(Math.round(deltaJour / caJourNecessaire * 100)) : null;
                  const deltaPillCA = deltaPct != null ? (
                    <DeltaPill direction={onTrack ? 'up' : 'down'} value={deltaPct} suffix="vs objectif" size="md" />
                  ) : null;

                  const deltaTickets = buildDeltaJ7(todayNb, j7Nb);
                  const deltaPillTk = deltaTickets ? (
                    <DeltaPill direction={deltaTickets.direction} value={deltaTickets.value} size="sm" />
                  ) : null;

                  return (
                    <div style={{
                      display:'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '7fr 5fr',
                      gap: isMobile ? S[5] : S[6],
                      marginBottom: SPACE_SECTION,
                      alignItems: 'stretch',
                    }}>
                      {/* Hero : CA du jour — sans Card, typo dominante */}
                      <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', minWidth: 0, paddingRight: isMobile ? 0 : S[3] }}>
                        <div style={{ ...EYEBROW, marginBottom: S[4] }}>Chiffre d'affaires du jour</div>
                        <div>
                          <div style={{ display:'flex', alignItems:'baseline', gap: S[3], flexWrap:'wrap', marginBottom: S[3] }}>
                            <div style={{
                              fontFamily: SHADCN.font, fontWeight: 600,
                              fontSize: isMobile ? 48 : 'clamp(56px, 6.5vw, 84px)',
                              letterSpacing: '-0.035em', lineHeight: 0.95,
                              fontVariantNumeric: 'tabular-nums', color: SHADCN.fg,
                            }}>
                              <AnimatedAmount value={caTodayHT} format={fmtFull} />
                            </div>
                            {deltaPillCA}
                          </div>
                          {hasObjectif && !estRentable ? (
                            <>
                              <div style={{ ...TYPO.body, color: SHADCN.mutedFg }}>
                                Objectif {fmtFull(caJourNecessaire)} · reste {fmtFull(Math.max(0, caJourNecessaire - caTodayHT))} · HT
                              </div>
                              <div style={{ marginTop: S[3], height: 4, background: SHADCN.border, borderRadius: R.pill, overflow:'hidden', maxWidth: 420 }}>
                                <div style={{
                                  height:'100%', width:'100%',
                                  transform: `scaleX(${Math.min(1, Math.max(0, caTodayHT / caJourNecessaire))})`,
                                  transformOrigin:'left',
                                  background: onTrack ? SHADCN.success : SHADCN.warning,
                                  transition:'transform 280ms cubic-bezier(0.23, 1, 0.32, 1)',
                                }} />
                              </div>
                            </>
                          ) : (
                            <div style={{ ...TYPO.body, color: SHADCN.mutedFg }}>
                              {estRentable ? 'Objectif du mois déjà couvert' : 'Hors taxes'}
                            </div>
                          )}
                          {(() => {
                            const todayISO = new Date().toISOString().slice(0, 10);
                            const histRows = (zCaisseHistory || []).filter(r => r.date !== todayISO);
                            if (!histRows.length || caTodayTTC <= 0) return null;
                            const todayDOW      = new Date(todayISO).getDay();
                            const recordAllTime = Math.max(...histRows.map(r => Number(r.total_ttc)));
                            const sameWeekday   = histRows.filter(r => new Date(r.date).getDay() === todayDOW);
                            const recordJour    = sameWeekday.length ? Math.max(...sameWeekday.map(r => Number(r.total_ttc))) : null;
                            const isRecordAbs   = caTodayTTC >= recordAllTime;
                            const isRecordJour  = !isRecordAbs && recordJour != null && caTodayTTC >= recordJour;
                            const JOURS         = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
                            const pct           = caTodayTTC / recordAllTime;
                            if (!isRecordAbs && !isRecordJour && pct < 0.90) return null;
                            const todayEv = evMap?.[todayISO]?.[0] ?? null;
                            const msg = isRecordAbs
                              ? `Record all-time battu !${todayEv ? ' · ' + todayEv : ''}`
                              : isRecordJour
                              ? `Record ${JOURS[todayDOW]} battu !${todayEv ? ' · ' + todayEv : ''}`
                              : `Proche du record (${Math.round(pct * 100)} %).`;
                            return (
                              <div style={{ ...TYPO.caption, color: SHADCN.success, fontWeight: 500, marginTop: S[2] }}>
                                {msg}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Secondaire : Tickets / Reste à prélever — divider vertical */}
                      <div style={{
                        display:'flex', flexDirection:'column',
                        borderLeft: isMobile ? 'none' : `1px solid ${SHADCN.border}`,
                        borderTop:  isMobile ? `1px solid ${SHADCN.border}` : 'none',
                        paddingLeft: isMobile ? 0 : S[5],
                        paddingTop:  isMobile ? S[5] : 0,
                      }}>
                        <div style={{ flex: 1, paddingBottom: S[4] }}>
                          <div style={{ ...EYEBROW, marginBottom: S[2] }}>Tickets</div>
                          <div style={{ display:'flex', alignItems:'baseline', gap: S[2], flexWrap:'wrap' }}>
                            <div style={{
                              fontFamily: SHADCN.font, fontWeight: 600, fontSize: 32,
                              letterSpacing: '-0.02em', lineHeight: 1,
                              fontVariantNumeric: 'tabular-nums', color: SHADCN.fg,
                            }}>{todayNb}</div>
                            {deltaPillTk}
                          </div>
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[2] }}>
                            {todayNb > 0 ? `Panier moyen ${fmtFull(caTodayHT / todayNb)} HT` : 'Aucun ticket'}
                          </div>
                        </div>
                        <div style={{ flex: 1, paddingTop: S[4], borderTop: `1px solid ${SHADCN.border}` }}>
                          <div style={{ ...EYEBROW, marginBottom: S[2] }}>Charges à venir</div>
                          <div style={{
                            fontFamily: SHADCN.font, fontWeight: 600, fontSize: 32,
                            letterSpacing: '-0.02em', lineHeight: 1,
                            fontVariantNumeric: 'tabular-nums',
                            color: chargesATomber.length === 0 ? SHADCN.success : SHADCN.fg,
                          }}>
                            {chargesATomber.length === 0 ? '0 €' : fmtFull(montantATomber)}
                          </div>
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[2] }}>
                            {chargesATomber.length === 0
                              ? 'Tout est passé ce mois-ci'
                              : `${chargesATomber.length} prélèvement${chargesATomber.length > 1 ? 's' : ''} à venir`}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Bloc 2 colonnes : [Ventes par groupe + Vinyles vendus] | Ventes Café ── */}
                {(() => {
                  // Filtre uniquement les catégories du groupe Café (exclut Vinyles, Retail, Services).
                  // Fallback : si `group` absent (API pas encore redéployée), on garde tout.
                  const cafeCategories = todayCategories.filter(c => !c.group || c.group === 'Café');
                  const totalCat = cafeCategories.reduce((s, c) => s + c.value, 0);
                  // Top 6 + 'Autre' agrégé. Une seule barre accentuée (la plus haute) en chart4 ;
                  // les autres en mutedFg — la hiérarchie se lit en hauteur, pas en couleur.
                  const sortedCats = [...cafeCategories].sort((a, b) => b.value - a.value);
                  const topCats = sortedCats.slice(0, 6);
                  const restCats = sortedCats.slice(6);
                  const aggregated = restCats.length > 0
                    ? [...topCats, { name: 'Autre', value: restCats.reduce((s, c) => s + c.value, 0) }]
                    : topCats;
                  const maxCatValue = aggregated[0]?.value ?? 0;
                  const pieData = aggregated.map(c => ({
                    ...c,
                    color: c.value === maxCatValue && maxCatValue > 0 ? SHADCN.chart4 : SHADCN.mutedFg,
                  }));
                  const groupsArr = Object.entries(todayGroups).filter(([,v]) => v > 0);
                  // Tout le pôle Disquaire (vinyles + magazines + affiches + livres)
                  const vinylesVendus = todayData?.vinylesVendus ?? [];
                  const totalArticles = vinylesVendus.reduce((s, v) => s + v.qty, 0);
                  // Sandwichs (catégorie Square « Sandwich ») — null = détail pas encore chargé
                  const sandwichsVendus = todayData?.sandwichsVendus ?? null;
                  const sumQty = arr => (arr || []).reduce((s, v) => s + (v.qty || 0), 0);
                  const sumCa  = arr => (arr || []).reduce((s, v) => s + (v.grossEur || 0), 0);
                  const totalSandwichs  = sumQty(sandwichsVendus);
                  const sandwichsRestants = Math.max(SANDWICHS_PAR_JOUR - totalSandwichs, 0);
                  const sandwichsToutVendu = totalSandwichs >= SANDWICHS_PAR_JOUR;
                  const j7Sandwichs     = sumQty(j7Data?.sandwichsVendus);
                  const moisSandwichsArr = memeMois ? (squareData?.mois?.find(m => m.enCours)?.sandwichsVendus || []) : [];
                  const moisSandwichs   = sumQty(moisSandwichsArr);
                  const moisSandwichsCa = sumCa(moisSandwichsArr);
                  // Retail (merch, céramique, matériel) et Services : détail des articles
                  // qui composent les montants de « Ventes par groupe ». Carte affichée
                  // seulement quand le pôle a vendu quelque chose.
                  const autresPoles = [
                    { key: 'Retail',   label: `Retail vendu ${leJour}`,   items: todayData?.retailVendus   ?? [], Icon: ShoppingBag },
                    { key: 'Services', label: `Services vendus ${leJour}`, items: todayData?.servicesVendus ?? [], Icon: Briefcase },
                  ].filter(p => p.items.length > 0);
                  // Cowork : les baristas ajoutent l'article Square « Coworking » (0 €) au ticket
                  // quand le client s'installe pour travailler. Marqueur agrégé côté API
                  // (aggregateCowork) — jour courant ou jour passé consulté.
                  const cowork      = todayData?.cowork ?? null;
                  const j7Cowork    = j7Data?.cowork?.nbPersonnes ?? null;
                  const moisCowork  = memeMois ? (squareData?.mois?.find(m => m.enCours)?.cowork ?? null) : null;
                  const showCowork  = !!cowork && (cowork.nbPersonnes > 0 || (isToday && (moisCowork?.nbPersonnes ?? 0) > 0));
                  const coworkPctJour = cowork && caTodayTTC > 0 ? Math.round(cowork.caTTC / caTodayTTC * 100) : 0;
                  const coworkHeures  = cowork
                    ? Object.entries(cowork.parHeure || {}).map(([h, n]) => [parseInt(h, 10), n]).sort((a, b) => a[0] - b[0])
                    : [];
                  const coworkArticles = (cowork?.articles ?? []).slice(0, 6);
                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '7fr 5fr',
                      gap: isMobile ? S[4] : S[5],
                      alignItems: 'stretch',
                      marginBottom: S[6],
                    }}>
                      {/* ── Colonne gauche : Ventes par groupe + Vinyles vendus stackés ── */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: S[4], minWidth: 0 }}>
                      {todayGroupsTotal > 0 && (
                        <Card style={{ marginBottom: 0 }}>
                          <SectionTitle>Ventes par groupe</SectionTitle>
                          <div style={{ display:'grid', gridTemplateColumns: `repeat(${groupsArr.length}, minmax(0, 1fr))`, gap: S[4] }}>
                            {groupsArr.map(([k, v], i, arr) => (
                              <div key={k} style={{
                                paddingRight: i < arr.length-1 ? S[4] : 0,
                                borderRight: i < arr.length-1 ? `1px solid ${SHADCN.border}` : 'none',
                              }}>
                                <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 8 }}>
                                  <div style={{ width:8, height:8, borderRadius: R.pill, background: GROUP_COLORS[k] || SHADCN.mutedFg }} />
                                  <div style={{ ...TYPO.body, fontWeight: 500, color: SHADCN.fg }}>{k}</div>
                                </div>
                                <div style={{ ...TYPO.h2, ...TYPO.num, color: SHADCN.fg }}>{fmtFull(v)}</div>
                                <div style={{ ...TYPO.body, color: SHADCN.mutedFg, marginTop: 4 }}>
                                  {Math.round(v / todayGroupsTotal * 100)}% du jour
                                </div>
                              </div>
                            ))}
                          </div>
                        </Card>
                      )}
                      <Card style={{ marginBottom: 0, flex: 1 }}>
                        <SectionTitle right={vinylesVendus.length > 0 ? <StatusPill variant="info">{totalArticles} article{totalArticles > 1 ? 's' : ''}</StatusPill> : null}>
                          Disquaire vendu {leJour}
                        </SectionTitle>
                        {vinylesVendus.length === 0 ? (
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, padding: '14px 4px' }}>
                            Aucune vente disquaire {leJour}.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {vinylesVendus.map((v, i) => (
                              <div key={v.variationId || i} style={{
                                display:'flex', alignItems:'center', gap: S[3],
                                padding: '12px 0',
                                borderTop: i > 0 ? `1px solid ${SHADCN.border}` : 'none',
                              }}>
                                <div style={{
                                  width: 44, height: 44, borderRadius: R.md, background: SHADCN.muted, flexShrink: 0,
                                  position: 'relative', overflow: 'hidden',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <Disc3 size={20} color={SHADCN.mutedFg} />
                                  {v.image && (
                                    <img
                                      src={v.image}
                                      alt=""
                                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                  )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ ...TYPO.body, color: SHADCN.fg, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {v.title || v.name || 'Vinyle'}
                                  </div>
                                  {(v.artist || (v.categorie && v.categorie !== 'Vinyles')) && (
                                    <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {v.artist || v.categorie}
                                    </div>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <span style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{fmtFull(v.grossEur)}</span>
                                  {v.qty > 1 && (
                                    <span style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>×{v.qty}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                      {showCowork && (
                        <Card style={{ marginBottom: 0 }}>
                          <SectionTitle right={
                            <StatusPill variant={cowork.nbPersonnes > 0 ? 'success' : 'neutral'}>
                              {cowork.nbPersonnes} coworker{cowork.nbPersonnes > 1 ? 's' : ''}
                            </StatusPill>
                          }>
                            Cowork {leJour}
                          </SectionTitle>
                          {/* 3 repères : coworkers (vs J-7), panier moyen (vs hors cowork), CA généré (% du jour) */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: S[4], marginBottom: S[3] }}>
                            {[
                              {
                                label: 'Coworkers',
                                value: String(cowork.nbPersonnes),
                                sub:   j7Cowork != null ? `${j7Label} : ${j7Cowork}` : null,
                              },
                              {
                                label: 'Panier moyen',
                                value: cowork.panierMoyen != null ? fmtDec(cowork.panierMoyen) : '—',
                                sub:   cowork.panierMoyenAutres != null ? `${fmtDec(cowork.panierMoyenAutres)} hors cowork` : null,
                              },
                              {
                                label: 'CA généré',
                                value: fmtDec(cowork.caTTC),
                                sub:   cowork.caTTC > 0 ? `${coworkPctJour}% du jour` : null,
                              },
                            ].map((k, i, arr) => (
                              <div key={k.label} style={{
                                minWidth: 0,
                                paddingRight: i < arr.length - 1 ? S[4] : 0,
                                borderRight: i < arr.length - 1 ? `1px solid ${SHADCN.border}` : 'none',
                              }}>
                                <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginBottom: 4 }}>{k.label}</div>
                                <div style={{ ...TYPO.h2, ...TYPO.num, color: SHADCN.fg, whiteSpace: 'nowrap' }}>{k.value}</div>
                                {k.sub && (
                                  <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.sub}</div>
                                )}
                              </div>
                            ))}
                          </div>
                          {/* Heures d'arrivée (heure d'encaissement du ticket marqué) */}
                          {coworkHeures.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: S[2], marginBottom: S[3] }}>
                              <span style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>Arrivées</span>
                              {coworkHeures.map(([h, n]) => (
                                <span key={h} style={{
                                  ...TYPO.caption, ...TYPO.num, color: SHADCN.fg,
                                  background: SHADCN.muted, borderRadius: R.pill, padding: '2px 8px',
                                }}>
                                  {h}h{n > 1 ? ` ×${n}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                          {cowork.nbPersonnes === 0 ? (
                            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, padding: '14px 4px' }}>
                              {isToday ? 'Personne au cowork pour l\'instant.' : 'Personne au cowork ce jour-là.'}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {coworkArticles.map((v, i) => (
                                <div key={v.variationId || `${v.name}-${i}`} style={{
                                  display:'flex', alignItems:'center', gap: S[3],
                                  padding: '12px 0',
                                  borderTop: i > 0 ? `1px solid ${SHADCN.border}` : 'none',
                                }}>
                                  <div style={{
                                    width: 36, height: 36, borderRadius: R.md, background: SHADCN.muted, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <Laptop size={18} color={GROUP_COLORS.Services} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0, ...TYPO.body, color: SHADCN.fg, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {v.name}
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <span style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>×{v.qty}</span>
                                    <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg }}>{fmtDec(v.grossEur)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Repère mensuel : cumul coworkers + panier moyen cowork vs hors cowork */}
                          {moisCowork && (
                            <div style={{
                              display: 'flex', justifyContent: 'space-between', gap: S[3],
                              marginTop: S[3], paddingTop: S[3], borderTop: `1px solid ${SHADCN.border}`,
                              ...TYPO.caption, color: SHADCN.mutedFg,
                            }}>
                              <span>
                                Ce mois : <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{moisCowork.nbPersonnes}</span> coworker{moisCowork.nbPersonnes > 1 ? 's' : ''}
                                {moisCowork.caTTC > 0 ? ` · ${fmtDec(moisCowork.caTTC)}` : ''}
                              </span>
                              {moisCowork.panierMoyen != null && moisCowork.panierMoyenAutres != null && (
                                <span>
                                  Panier <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{fmtDec(moisCowork.panierMoyen)}</span> vs {fmtDec(moisCowork.panierMoyenAutres)}
                                </span>
                              )}
                            </div>
                          )}
                        </Card>
                      )}
                      {autresPoles.map(({ key, label, items, Icon }) => {
                        const nb = items.reduce((s, v) => s + v.qty, 0);
                        return (
                          <Card key={key} style={{ marginBottom: 0 }}>
                            <SectionTitle right={<StatusPill variant="info">{nb} article{nb > 1 ? 's' : ''}</StatusPill>}>
                              {label}
                            </SectionTitle>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {items.map((v, i) => (
                                <div key={v.variationId || `${v.name}-${i}`} style={{
                                  display:'flex', alignItems:'center', gap: S[3],
                                  padding: '12px 0',
                                  borderTop: i > 0 ? `1px solid ${SHADCN.border}` : 'none',
                                }}>
                                  <div style={{
                                    width: 44, height: 44, borderRadius: R.md, background: SHADCN.muted, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <Icon size={20} color={GROUP_COLORS[key] || SHADCN.mutedFg} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ ...TYPO.body, color: SHADCN.fg, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {v.name || key}{v.variation ? ` · ${v.variation}` : ''}
                                    </div>
                                    {v.categorie && (
                                      <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {v.categorie}
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <span style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{fmtFull(v.grossEur)}</span>
                                    {v.qty > 1 && (
                                      <span style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>×{v.qty}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Card>
                        );
                      })}
                      </div>
                      {/* ── Colonne droite : Ventes Café (barres) + Sandwichs vendus ── */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: S[4], minWidth: 0 }}>
                      {cafeCategories.length > 0 && (() => {
                        const maxCat = Math.max(...pieData.map(d => d.value), 0);
                        return (
                          <Card style={{ marginBottom: 0 }}>
                            <SectionTitle>Ventes Café</SectionTitle>
                            <div style={{ display:'flex', flexDirection:'column' }}>
                              {/* Zone barres */}
                              <div style={{
                                display:'grid',
                                gridTemplateColumns: `repeat(${pieData.length}, minmax(0, 1fr))`,
                                gap: S[2],
                                alignItems:'end',
                                height: 180,
                                paddingTop: S[3],
                              }}>
                                {pieData.map(({ name, value, color }) => {
                                  const barH = maxCat > 0 ? (value / maxCat) * 100 : 0;
                                  return (
                                    <div key={name} style={{
                                      display:'flex', flexDirection:'column', alignItems:'center',
                                      height: '100%', justifyContent:'flex-end', gap: 6, minWidth: 0,
                                    }}>
                                      <div style={{ ...TYPO.caption, ...TYPO.num, fontWeight: 600, color: SHADCN.fg, whiteSpace:'nowrap' }}>
                                        {fmtFull(value)}
                                      </div>
                                      <div style={{
                                        width: '100%',
                                        maxWidth: 28,
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                      }}>
                                        <div style={{
                                          width: '100%',
                                          height: '100%',
                                          background: color,
                                          borderRadius: `${R.sm}px ${R.sm}px 0 0`,
                                          transform: `scaleY(${Math.max(barH / 100, 0.012)})`,
                                          transformOrigin: 'bottom',
                                          transition: 'transform 280ms cubic-bezier(0.23, 1, 0.32, 1)',
                                        }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Labels axe X — angle 45°, hauteur réduite */}
                              <div style={{
                                display:'grid',
                                gridTemplateColumns: `repeat(${pieData.length}, minmax(0, 1fr))`,
                                gap: S[2],
                                marginTop: S[3],
                                paddingTop: S[3],
                                borderTop: `1px solid ${SHADCN.border}`,
                                height: 56,
                              }}>
                                {pieData.map(({ name }) => (
                                  <div key={name} style={{
                                    display:'flex', justifyContent:'center', alignItems:'flex-start',
                                    minWidth: 0, overflow: 'visible',
                                  }} title={name}>
                                    <div style={{
                                      ...TYPO.caption,
                                      color: SHADCN.mutedFg,
                                      transform: 'rotate(-35deg)',
                                      transformOrigin: 'top center',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 80,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}>{name}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Card>
                        );
                      })()}
                      {sandwichsVendus && (
                        <Card style={{ marginBottom: 0, flex: 1 }}>
                          <SectionTitle right={
                            <StatusPill variant={sandwichsToutVendu ? 'success' : 'info'}>
                              {totalSandwichs} / {SANDWICHS_PAR_JOUR} vendu{totalSandwichs > 1 ? 's' : ''}
                            </StatusPill>
                          }>
                            Sandwichs vendus {leJour}
                          </SectionTitle>
                          {/* Jauge : une case par sandwich préparé, remplie quand vendu */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: S[3], marginBottom: S[2] }}>
                            <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                              {Array.from({ length: SANDWICHS_PAR_JOUR }, (_, i) => (
                                <div key={i} style={{
                                  flex: 1, height: 8, borderRadius: R.pill,
                                  background: i < totalSandwichs ? SHADCN.fg : SHADCN.muted,
                                  transition: 'background 200ms ease',
                                }} />
                              ))}
                            </div>
                            <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg, whiteSpace: 'nowrap' }}>
                              {sandwichsToutVendu
                                ? (totalSandwichs > SANDWICHS_PAR_JOUR ? `tout vendu, +${totalSandwichs - SANDWICHS_PAR_JOUR}` : 'tout vendu')
                                : `reste ${sandwichsRestants}`}
                            </span>
                          </div>
                          {sandwichsVendus.length === 0 ? (
                            <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, padding: '14px 4px' }}>
                              Aucun sandwich vendu {isToday ? 'pour l\'instant' : 'ce jour-là'} · {SANDWICHS_PAR_JOUR} préparés.
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {sandwichsVendus.map((v, i) => (
                                <div key={v.variationId || i} style={{
                                  display:'flex', alignItems:'center', gap: S[3],
                                  padding: '12px 0',
                                  borderTop: i > 0 ? `1px solid ${SHADCN.border}` : 'none',
                                }}>
                                  <div style={{
                                    width: 36, height: 36, borderRadius: R.md, background: SHADCN.muted, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}>
                                    <Sandwich size={18} color={SHADCN.mutedFg} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0, ...TYPO.body, color: SHADCN.fg, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {v.name}
                                  </div>
                                  <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    <span style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>×{v.qty}</span>
                                    <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg }}>{fmtFull(v.grossEur)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Repères : même jour la semaine dernière (jusqu'à la même heure) + cumul du mois */}
                          <div style={{
                            display: 'flex', justifyContent: 'space-between', gap: S[3],
                            marginTop: S[3], paddingTop: S[3], borderTop: `1px solid ${SHADCN.border}`,
                            ...TYPO.caption, color: SHADCN.mutedFg,
                          }}>
                            <span>{j7Label} : <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{j7Sandwichs}</span></span>
                            {memeMois && (
                              <span>
                                Ce mois : <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>{moisSandwichs}</span>
                                {moisSandwichs > 0 ? ` · ${fmtFull(moisSandwichsCa)}` : ''}
                              </span>
                            )}
                          </div>
                        </Card>
                      )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Détail des charges à tomber (liste) ──────────────────── */}
                {chargesATomber.length > 0 && (
                  <Card>
                    <SectionTitle>Charges à venir ce mois</SectionTitle>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {chargesATomber.map((c, i) => (
                        <div key={i} style={{
                          display:'flex', justifyContent:'space-between', alignItems:'center',
                          padding: '12px 0',
                          borderTop: i > 0 ? `1px solid ${SHADCN.border}` : 'none',
                        }}>
                          <span style={{ ...TYPO.body, color: SHADCN.mutedFg }}>{c.label}</span>
                          <span style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.fg, fontWeight: 600 }}>−{fmtFull(c.montant)}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}


                {/* ── Équipe au café aujourd'hui ────────────────────────────── */}
                {squareTeamLoading && <Card><Skeleton h={120} /></Card>}
                {squareTeam && shiftsAuj.length > 0 && (() => {
                  // Plage horaire de la timeline (arrondi à l'heure)
                  const minStart = Math.min(...shiftsAuj.map(s => s.debutMin));
                  const maxEnd   = Math.max(...shiftsAuj.map(s => s.finMin));
                  const startH = Math.floor(minStart / 60);
                  const endH   = Math.ceil(maxEnd / 60);
                  const totalMin = (endH - startH) * 60;
                  const pct = (m) => `${((m - startH * 60) / totalMin) * 100}%`;
                  const inRange = nowMin >= startH*60 && nowMin <= endH*60;

                  // Membres uniques — en service d'abord, puis ordre chronologique
                  const seen = new Set();
                  const uniques = [];
                  [...enServiceMaintenant, ...shiftsAuj].forEach(sh => {
                    if (!seen.has(sh.memberId)) {
                      seen.add(sh.memberId);
                      uniques.push({
                        memberId: sh.memberId,
                        prenom: sh.prenom,
                        shifts: shiftsAuj.filter(s => s.memberId === sh.memberId),
                      });
                    }
                  });

                  // Graduations heures (toutes les heures, jusqu'à 8 maxi → sinon 2h)
                  const stepH = (endH - startH) > 8 ? 2 : 1;
                  const hourMarks = [];
                  for (let h = startH; h <= endH; h += stepH) hourMarks.push(h);

                  return (
                    <Card>
                      <SectionTitle right={enServiceMaintenant.length > 0 ? (
                        <LiveIndicator variant="success">
                          {enServiceMaintenant.length === 1
                            ? `${enServiceMaintenant[0].prenom} en service`
                            : `${enServiceMaintenant.length} en service`}
                        </LiveIndicator>
                      ) : null}>
                        Au café aujourd'hui
                      </SectionTitle>

                      {/* Groupe d'avatars empilés */}
                      <div style={{ display:'flex', alignItems:'center', gap: S[3], marginBottom: S[5] }}>
                        <div style={{ display:'flex' }}>
                          {uniques.slice(0, 5).map((u, i) => (
                            <div key={u.memberId} style={{ marginLeft: i === 0 ? 0 : -10, borderRadius: '50%', boxShadow: `0 0 0 2px ${SHADCN.bg}` }}>
                              <Avatar prenom={u.prenom} memberId={u.memberId} size={36} overrideColor={memberColors[u.memberId]} />
                            </div>
                          ))}
                          {uniques.length > 5 && (
                            <div style={{
                              marginLeft: -10,
                              width: 36, height: 36, borderRadius: '50%',
                              background: SHADCN.sidebarAccent, color: SHADCN.fg,
                              boxShadow: `0 0 0 2px ${SHADCN.bg}`,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: SHADCN.font, fontWeight: 600, fontSize: FS.caption,
                            }}>+{uniques.length - 5}</div>
                          )}
                        </div>
                        <div style={{ display:'flex', flexDirection:'column' }}>
                          <span style={{ ...TYPO.body, fontWeight: 600, color: SHADCN.fg }}>
                            {uniques.length} personne{uniques.length > 1 ? 's' : ''}
                          </span>
                          <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg }}>
                            {shiftsAuj.length} shift{shiftsAuj.length > 1 ? 's' : ''} · {shiftsAuj.reduce((s,sh) => s + sh.dureeH, 0).toFixed(1)}h cumulées
                          </span>
                        </div>
                      </div>

                      {/* Timeline Gantt par membre */}
                      <div>
                        {uniques.map((u) => (
                          <div key={u.memberId} style={{ display:'flex', alignItems:'center', gap: S[3], padding: '6px 0' }}>
                            <div style={{ width: 96, flexShrink: 0, display:'flex', alignItems:'center', gap: S[2], minWidth: 0 }}>
                              <Avatar prenom={u.prenom} memberId={u.memberId} size={20} overrideColor={memberColors[u.memberId]} />
                              <span style={{ ...TYPO.caption, fontWeight: 600, color: SHADCN.fg, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.prenom}</span>
                            </div>
                            <div style={{ position:'relative', flex: 1, height: 28, background: SHADCN.muted, borderRadius: R.sm }}>
                              {u.shifts.map((sh, i) => {
                                const color = memberColors[sh.memberId] ?? couleurMembre(sh.memberId);
                                const enCours = sh.debutMin <= nowMin && nowMin < sh.finMin;
                                const passe   = sh.finMin <= nowMin;
                                const soft = softTone(color);
                                return (
                                  <div key={i} style={{
                                    position:'absolute',
                                    left: pct(sh.debutMin),
                                    right: `calc(100% - ${pct(sh.finMin)})`,
                                    top: 0, bottom: 0,
                                    background: enCours ? color : soft.bg,
                                    border: `1px solid ${enCours ? color : soft.border}`,
                                    borderRadius: R.sm,
                                    opacity: passe ? 0.55 : 1,
                                    display:'flex', alignItems:'center', padding:'0 8px',
                                    color: enCours ? texteSurFond(color) : soft.text,
                                    ...TYPO.caption, ...TYPO.num, fontWeight: 600, lineHeight: 1,
                                    whiteSpace:'nowrap', overflow:'hidden',
                                  }}>
                                    {sh.debut}–{sh.fin} · {sh.dureeH}h
                                  </div>
                                );
                              })}
                              {inRange && (
                                <div style={{
                                  position:'absolute', left: pct(nowMin), top: -2, bottom: -2,
                                  width: 2, background: SHADCN.fg, borderRadius: 1, pointerEvents:'none',
                                }} />
                              )}
                            </div>
                          </div>
                        ))}
                        {/* Échelle heures */}
                        <div style={{ display:'flex', alignItems:'center', gap: S[3], marginTop: S[2] }}>
                          <div style={{ width: 96, flexShrink: 0 }} />
                          <div style={{ position:'relative', flex: 1, height: 14 }}>
                            {hourMarks.map(h => (
                              <div key={h} style={{
                                position:'absolute',
                                left: pct(h*60),
                                transform: h === startH ? 'translateX(0)' : h === endH ? 'translateX(-100%)' : 'translateX(-50%)',
                                ...TYPO.caption, color: SHADCN.mutedFg,
                                fontFamily: SHADCN.font, fontSize: FS.meta, fontVariantNumeric: 'tabular-nums',
                              }}>{h}h</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })()}

                {/* ── Alerte couverture events — extras à prévoir ─────────── */}
                {events && squareTeam && (() => {
                  // Helpers locaux (dupliqués depuis Equipe.js volontairement)
                  const isoMins = (iso) => {
                    if (!iso) return null;
                    const s = String(iso);
                    if (s.endsWith('Z')) { const d = new Date(s); return d.getHours()*60 + d.getMinutes(); }
                    const m = s.match(/T(\d{2}):(\d{2})/); return m ? (+m[1])*60 + (+m[2]) : null;
                  };
                  const evMins = (e) => {
                    if (e.allDay) return null;
                    const d = isoMins(e.start);
                    let f = isoMins(e.end);
                    if (d == null) return null;
                    if (f == null || f <= d) f = Math.min(24*60, d + 60);
                    return { debut: d, fin: f };
                  };
                  const uncov = (eD, eF, shifts) => {
                    const inter = shifts.map(s => ({ debut: Math.max(s.debutMin, eD), fin: Math.min(s.finMin ?? (s.debutMin + s.dureeMin), eF) }))
                      .filter(s => s.fin > s.debut).sort((a,b) => a.debut - b.debut);
                    const merged = [];
                    for (const s of inter) {
                      const last = merged[merged.length - 1];
                      if (last && last.fin >= s.debut) last.fin = Math.max(last.fin, s.fin);
                      else merged.push({ ...s });
                    }
                    const gaps = []; let cur = eD;
                    for (const s of merged) { if (s.debut > cur) gaps.push({ debut: cur, fin: s.debut }); cur = Math.max(cur, s.fin); }
                    if (cur < eF) gaps.push({ debut: cur, fin: eF });
                    return gaps;
                  };
                  const fmtMin = (mins) => {
                    const h = Math.floor(mins/60), m = mins%60;
                    return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2,'0')}`;
                  };
                  const joursByDate = new Map();
                  for (const sem of (squareTeam.semaines || [])) {
                    for (const j of (sem.jours || [])) joursByDate.set(j.date, j);
                  }
                  const startTs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                  const endTs   = startTs + 7 * 24 * 3600 * 1000;
                  const issues = [];
                  for (const e of events) {
                    const t = new Date(e.start).getTime();
                    if (isNaN(t) || t < startTs || t >= endTs) continue;
                    const dayKey = String(e.start).slice(0, 10);
                    const jour = joursByDate.get(dayKey);
                    if (!jour) continue;
                    const m = evMins(e);
                    if (!m) continue;
                    const gaps = uncov(m.debut, m.fin, jour.shifts || []);
                    if (gaps.length === 0) continue;
                    issues.push({ jour, event: e, gaps, ts: t });
                  }
                  if (issues.length === 0) return null;
                  issues.sort((a, b) => a.ts - b.ts);
                  const fmtDayShort = (iso) => {
                    const [y, mo, d] = iso.split('-').map(Number);
                    const dt = new Date(y, mo - 1, d);
                    const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
                    if (dt.getTime() === today0) return 'Aujourd’hui';
                    if (dt.getTime() === today0 + 86400000) return 'Demain';
                    return dt.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                  };
                  return (
                    <Alert variant="error" style={{ marginBottom: S[4] }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>
                          {issues.length} event{issues.length > 1 ? 's' : ''} sans couverture
                        </strong> dans les 7 jours — prévoir un extra.
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {issues.slice(0, 4).map((ci, i) => (
                          <li key={`ci-today-${i}`} style={{ ...TYPO.caption, marginTop: 2 }}>
                            <strong style={{ textTransform: 'capitalize' }}>{fmtDayShort(ci.jour.date)}</strong>
                            {' · '}{ci.event.summary}
                            {' · trou '}
                            {ci.gaps.map((g, gi) => (
                              <span key={gi}>{gi > 0 ? ', ' : ''}{fmtMin(g.debut)}–{fmtMin(g.fin)}</span>
                            ))}
                          </li>
                        ))}
                        {issues.length > 4 && (
                          <li style={{ ...TYPO.caption, marginTop: 2, opacity: 0.8 }}>
                            + {issues.length - 4} autre{issues.length - 4 > 1 ? 's' : ''}…
                          </li>
                        )}
                      </ul>
                    </Alert>
                  );
                })()}

                {/* ── Semaine — lundi → dimanche ────────────────────────────── */}
                {eventsLoading && !events && <Card><Skeleton h={140} /></Card>}
                {events && (() => {
                  const now = new Date();
                  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const dow = (startOfToday.getDay() + 6) % 7; // 0 = lundi
                  const monday = new Date(startOfToday.getTime() - dow * 86400000);
                  const sunday = new Date(monday.getTime() + 7 * 86400000);
                  const weekEvents = events.filter(e => {
                    const t = new Date(e.start).getTime();
                    return t >= monday.getTime() && t < sunday.getTime();
                  });
                  const byDay = new Map();
                  for (const e of weekEvents) {
                    const key = String(e.start).slice(0, 10);
                    if (!byDay.has(key)) byDay.set(key, []);
                    byDay.get(key).push(e);
                  }
                  const fmtTime = (iso, allDay) => {
                    if (allDay) return 'journée';
                    const m = String(iso).match(/T(\d{2}):(\d{2})/);
                    return m ? `${m[1]}h${m[2] === '00' ? '' : m[2]}` : '';
                  };
                  const days = [];
                  for (let i = 0; i < 7; i++) {
                    const dt = new Date(monday.getTime() + i * 86400000);
                    const y = dt.getFullYear();
                    const m = String(dt.getMonth() + 1).padStart(2, '0');
                    const d = String(dt.getDate()).padStart(2, '0');
                    const key = `${y}-${m}-${d}`;
                    days.push({
                      key, dt,
                      isToday: dt.getTime() === startOfToday.getTime(),
                      isPast: dt.getTime() < startOfToday.getTime(),
                      events: (byDay.get(key) || []).sort((a,b) => String(a.start).localeCompare(String(b.start))),
                    });
                  }
                  const wkLabels = ['lun','mar','mer','jeu','ven','sam','dim'];
                  const fmtRange = (a, b) => {
                    const lastDay = new Date(b.getTime() - 86400000);
                    const sameMonth = a.getMonth() === lastDay.getMonth();
                    const left = a.toLocaleDateString('fr-FR', { day: 'numeric', month: sameMonth ? undefined : 'short' });
                    const right = lastDay.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                    return `${left} → ${right}`;
                  };
                  const monthShort = (dt) => dt.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '');
                  const cols = `repeat(7, minmax(0, 1fr))`;
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: S[3] }}>
                        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>Cette semaine</div>
                        <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{fmtRange(monday, sunday)} · {weekEvents.length} event{weekEvents.length > 1 ? 's' : ''}</div>
                      </div>
                      <Card style={{ padding: 0, overflow: 'hidden' }}>
                        {/* En-tête jours */}
                        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: `1px solid ${SHADCN.border}`, background: SHADCN.bg }}>
                          {days.map((day, idx) => (
                            <div key={`hd-${day.key}`} style={{
                              padding: '12px 10px',
                              borderLeft: idx === 0 ? 'none' : `1px solid ${SHADCN.border}`,
                              background: day.isToday ? softTone(SHADCN.success).bg : 'transparent',
                              opacity: day.isPast ? 0.55 : 1,
                              textAlign: 'center',
                              minWidth: 0,
                            }}>
                              <div style={{ ...TYPO.eyebrow, color: day.isToday ? SHADCN.success : SHADCN.mutedFg }}>
                                {wkLabels[idx]}
                              </div>
                              <div style={{ ...TYPO.body, ...TYPO.num, fontWeight: 600, color: day.isToday ? SHADCN.success : SHADCN.fg, marginTop: 3 }}>
                                {day.dt.getDate()} {monthShort(day.dt)}
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Corps — colonnes events */}
                        <div style={{ display: 'grid', gridTemplateColumns: cols, minHeight: 160 }}>
                          {days.map((day, idx) => (
                            <div key={day.key} style={{
                              borderLeft: idx === 0 ? 'none' : `1px solid ${SHADCN.border}`,
                              background: day.isToday ? softTone(SHADCN.success).bg : 'transparent',
                              opacity: day.isPast ? 0.6 : 1,
                              padding: 8,
                              display: 'flex', flexDirection: 'column', gap: 4,
                              minWidth: 0,
                            }}>
                              {day.isToday && day.events.length === 0 && (
                                <div style={{ flex: 1, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                  <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, fontSize: FS.meta, fontStyle:'italic' }}>Aucun event</span>
                                </div>
                              )}
                              {day.events.slice(0, 6).map((e, i) => {
                                const key = e.uid ? `${e.uid}-${i}` : `${day.key}-${i}`;
                                const title = `${fmtTime(e.start, e.allDay)} · ${e.summary}${e.location ? ' · ' + e.location : ''}`;
                                if (e.allDay) {
                                  return (
                                    <div key={key} title={title} style={{
                                      background: SHADCN.mintSoft,
                                      border: `1px dashed ${SHADCN.mintBorder}`,
                                      color: SHADCN.mint,
                                      borderRadius: R.xs,
                                      padding: '1px 6px',
                                      ...TYPO.caption,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      lineHeight: 1.35,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}>{e.summary}</div>
                                  );
                                }
                                return (
                                  <div key={key} title={title} style={{
                                    background: SHADCN.mint,
                                    color: SHADCN.bg,
                                    borderRadius: R.xs,
                                    padding: '2px 6px',
                                    ...TYPO.caption,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}>
                                    <span style={{ ...TYPO.num, marginRight: 4, opacity: 0.85 }}>{fmtTime(e.start, e.allDay)}</span>{e.summary}
                                  </div>
                                );
                              })}
                              {day.events.length > 6 && (
                                <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, fontSize: FS.meta, marginTop: 2 }}>+{day.events.length - 6}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    </>
                  );
                })()}

              </>
            );
          })()}

          {/* ═══════════════════ COCKPIT ÉQUIPE V4 — HT · Charges réelles ══ */}
          {activeTab === 'cafe' && (
          <EntityAccentContext.Provider value={SHADCN.mutedFg}>
          {(() => {
            // ── Navigation mois ─────────────────────────────────────────────
            const changeMois = (delta) => {
              const [y, m] = selectedPeriode.split('-').map(Number);
              const d = new Date(y, m - 1 + delta, 1);
              const next = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
              if (next <= currentPeriode) setSelectedPeriode(next);
            };
            // Shadow global dayOfMonth/daysInMonth/daysLeft/prorataFact pour le mois sélectionné
            const [_selY, _selM] = selectedPeriode.split('-').map(Number);
            const daysInMonth  = new Date(_selY, _selM, 0).getDate();
            const dayOfMonth   = isCurrentMonth ? now.getDate() : daysInMonth;
            const daysLeft     = isCurrentMonth ? (daysInMonth - now.getDate()) : 0;

            // ── Constantes de session ───────────────────────────────────────
            const prorataFact = dayOfMonth / daysInMonth;

            // ── Seuil HT (point mort réel employeur) ───────────────────────
            // seuilHT et chargesFixesHT définis en amont dans le corps de App
            const flag1 = cfRef.loyerSCI + coutSalarialCharge * prorataFact; // Survie (HT, proraté)
            const flag2 = chargesFixesHT;  // Sécurité = toutes charges fixes HT

            // ── CA HT — ventilation granulaire (calculée dans le corps de App) ─
            // TOTAL_CA_HT = FOOD_BEV_HT + RETAIL_HT + SERVICES_HT
            // Chaque pôle divisé par son taux TVA exact (10%, 5,5%, 2,1%, 20%)
            const caActuelCockpit  = (caSquareMoisEnCours ?? 0) + stripeCurrentMonth; // TTC — Square + Stripe
            const caNetHT          = TOTAL_CA_HT;                // HT granulaire — référence pilotage

            // ── Indicateurs HT cohérents ────────────────────────────────────
            const resteAVendre     = Math.max(0, seuilHT - caNetHT);        // HT vs HT
            const nbCmds           = squareMoisEnCours?.nbCommandes ?? 0;
            const panierMoyen      = nbCmds > 0 ? caActuelCockpit / nbCmds : null;
            const caJourCockpit    = dayOfMonth > 0 ? Math.round(caNetHT / dayOfMonth) : 0;
            const caJourNecessaire = daysLeft > 0 ? Math.round(resteAVendre / daysLeft) : 0;
            const caAttenduAuj     = Math.round(seuilHT * prorataFact);     // objectif HT du jour
            const enAvance         = caNetHT >= caAttenduAuj;
            const zoneColor        = caNetHT >= seuilHT ? SHADCN.success : caNetHT >= flag2 ? SHADCN.warning : SHADCN.error;

            // ── Labor Cost — coût employeur réel proratisé ────────────────
            const salaireChargeProratatise = Math.round(coutSalarialCharge * prorataFact);
            // Garde-fou : évite ratios absurdes si CA HT < 200€ (données manquantes)
            const ratioLabor   = caNetHT >= 200 ? Math.round(salaireChargeProratatise / caNetHT * 100) : null;
            // Seuil unique : ≤ 42 % OK, au-delà trop de personnel
            const laborStatus  = ratioLabor === null ? null
              : ratioLabor <= 42 ? 'OK'
              : 'TROP DE PERSONNEL';
            const laborBgColor = ratioLabor !== null && ratioLabor > 42 ? SHADCN.error : SHADCN.success;

            // ── Food Cost Consommables ─────────────────────────────────────
            // Proratisé uniquement si pas de données TX réelles (sinon montant exact)
            const achatsFoodSuivi   = splitFromTx ? achatsFoodHT   : Math.round(achatsFoodHT   * prorataFact);
            const achatsRetailSuivi = splitFromTx ? achatsRetailHT : Math.round(achatsRetailHT * prorataFact);
            // Base de calcul food cost = CA pôle Food HT ; garde-fou < 200€
            const baseFC      = caCafeGroupHT > 0 ? caCafeGroupHT : caNetHT;
            const foodCostPct = baseFC >= 200 ? Math.round(achatsFoodSuivi / baseFC * 100) : null;
            const foodBgColor = foodCostPct === null ? SHADCN.success
              : foodCostPct < FOOD_COST_CIBLE  * 100 ? SHADCN.success
              : foodCostPct < FOOD_COST_ALERTE * 100 ? SHADCN.warning
              : SHADCN.error;
            const foodStatus  = foodCostPct === null ? null
              : foodCostPct < FOOD_COST_CIBLE  * 100 ? 'BON'
              : foodCostPct < FOOD_COST_ALERTE * 100 ? 'À SURVEILLER'
              : 'TROP ÉLEVÉ';

            // ── Prime Cost — indicateur maître (labor + food) ─────────────
            // Standard contrôle de gestion restauration
            const primeCost = (ratioLabor !== null && foodCostPct !== null)
              ? ratioLabor + foodCostPct : null;
            // Écart à la cible 66 % (négatif = sous la cible = bon)
            const primeCostStatus = primeCost === null ? null
              : `${primeCost - 66 >= 0 ? '+' : '−'}${Math.abs(primeCost - 66)} pts`;
            const primeCostBg = primeCost === null ? SHADCN.fg
              : primeCost < 60 ? SHADCN.success
              : primeCost < 66 ? SHADCN.success
              : primeCost < 71 ? SHADCN.warning
              : SHADCN.error;

            // ── Invest Retail — BFR ────────────────────────────────────────
            const retailRatio  = caRetailGroupHT > 0
              ? Math.round(achatsRetailSuivi / caRetailGroupHT * 100) : null;
            const retailStatus = retailRatio === null ? null
              : retailRatio < 60 ? 'BON'
              : retailRatio < 90 ? 'ATTENTION'
              : 'TROP DE STOCK';
            const retailRatioColor = retailRatio === null ? SHADCN.fg
              : retailRatio < 60 ? SHADCN.success
              : retailRatio < 90 ? SHADCN.warning
              : SHADCN.error;

            // ── Mention prorata ─────────────────────────────────────────────
            const MOIS_LONG = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
            const prorataLabel = `CALCULÉ AU ${dayOfMonth} ${MOIS_LONG[now.getMonth()].toUpperCase()} ${now.getFullYear()}`;

            const F    = (v, d=0) => new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',maximumFractionDigits:d,minimumFractionDigits:d}).format(v);
            const MONO  = TYPO.num;
            // Tokens locaux — pointent sur le système global pour rester cohérents
            const LABEL = { ...TYPO.eyebrow, color: SHADCN.mutedFg };
            const SUB   = { ...TYPO.caption, color: SHADCN.mutedFg };
            const TICK  = { ...TYPO.eyebrow, color: SHADCN.mutedFg };
            const BODY  = { ...TYPO.body, ...TYPO.num, color: SHADCN.mutedFg };
            const DIV  = { borderBottom:`1px solid ${SHADCN.border}`, marginBottom:44, paddingBottom:44 };

            // ── SolidBlock — aplat plein, texte toujours noir ─────────────
            const SolidBlock = ({ bg, label, value, status, prorata, sublabel }) => (
              <div style={{ background:bg, padding: isMobile ? '32px 28px' : '44px 44px' }}>
                <div style={{ ...TYPO.eyebrow, color:'rgba(0,0,0,0.45)', marginBottom:20 }}>{label}</div>
                <div style={{ ...TYPO.display, fontSize:FS.h1, lineHeight:0.88, color:'rgba(0,0,0,0.88)', marginBottom: S[4] }}>{value}</div>
                {prorata && (
                  <div style={{ ...TYPO.eyebrow, fontWeight:700, color:'rgba(0,0,0,0.60)', marginBottom: S[3], background:'rgba(0,0,0,0.10)', display:'inline-block', padding:'3px 8px', borderRadius:R.xs }}>{prorata}</div>
                )}
                {status && (
                  <div style={{ ...TYPO.eyebrow, fontWeight:700, color:'rgba(0,0,0,0.80)', marginBottom:14, marginTop: prorata ? 8 : 0 }}>{status}</div>
                )}
                <div style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.overlay }}>{sublabel}</div>
              </div>
            );

            if (squareLoading) return (
              <>
                <PageHeader title={`${MOIS_LONG[now.getMonth()].charAt(0).toUpperCase() + MOIS_LONG[now.getMonth()].slice(1)} ${now.getFullYear()}`} />
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:28 }}>
                  <Card><Skeleton h={200} /></Card>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <Card><Skeleton h={120} /></Card>
                    <Card><Skeleton h={120} /></Card>
                    <Card><Skeleton h={120} /></Card>
                    <Card><Skeleton h={120} /></Card>
                  </div>
                </div>
                <Card style={{ marginBottom:14 }}><Skeleton h={280} /></Card>
                <Card style={{ marginBottom:14 }}><Skeleton h={200} /></Card>
              </>
            );

            const dotsBtn = (onDetail) => onDetail ? (
              <button
                onClick={(e) => { e.stopPropagation(); onDetail(); }}
                style={{
                  background: 'none', border: 'none', padding: '2px 4px',
                  borderRadius: R.sm, cursor: 'pointer', color: SHADCN.mutedFg,
                  display: 'flex', alignItems: 'center', lineHeight: 1,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = SHADCN.fg}
                onMouseLeave={e => e.currentTarget.style.color = SHADCN.mutedFg}
              >
                <MoreHorizontal size={15} />
              </button>
            ) : null;

            return (
              <>
                {/* ── 1. En-tête ─────────────────────────────────────── */}
                <PageHeader
                  eyebrow={isCurrentMonth ? `Jour ${dayOfMonth} sur ${daysInMonth} · ${Math.round(prorataFact * 100)}% du mois` : `${daysInMonth} jours · mois clôturé`}
                  title={<MonthTitle title={`${MOIS_LONG[_selM - 1].charAt(0).toUpperCase() + MOIS_LONG[_selM - 1].slice(1)} ${_selY}`} now={isCurrentMonth ? now : null} />}
                  actions={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button onClick={() => changeMois(-1)} aria-label="Mois précédent" style={{ background: 'none', border: 'none', padding: '4px 6px', borderRadius: R.sm, cursor: 'pointer', color: SHADCN.mutedFg, display: 'flex', alignItems: 'center' }}>
                        <ChevronLeft size={18} />
                      </button>
                      <button onClick={() => changeMois(1)} aria-label="Mois suivant" disabled={isCurrentMonth} style={{ background: 'none', border: 'none', padding: '4px 6px', borderRadius: R.sm, cursor: isCurrentMonth ? 'default' : 'pointer', color: isCurrentMonth ? SHADCN.border : SHADCN.mutedFg, display: 'flex', alignItems: 'center' }}>
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  }
                />

                {/* ── HERO — Résultat net projeté, pleine largeur, primauté visuelle ── */}
                <div style={{ marginBottom: S[5] }}>
                {/* ── HERO — Résultat net projeté, borderless, typo dominante ── */}
                {(() => {
                  const verdict = _netProj === null ? '—'
                    : _netProj >= 200 ? 'Bénéficiaire'
                    : _netProj > -200 ? "À l'équilibre"
                    : 'Déficitaire';
                  const verdictColor = _netProj === null ? SHADCN.mutedFg
                    : _netProj >= 200 ? SHADCN.success
                    : _netProj > -200 ? SHADCN.warning
                    : SHADCN.error;
                  const verdictBg = _netProj === null ? 'transparent'
                    : _netProj >= 200 ? softTone(SHADCN.success).bg
                    : _netProj > -200 ? softTone(SHADCN.warning).bg
                    : softTone(SHADCN.error).bg;
                  const verdictBorder = _netProj === null ? SHADCN.border
                    : _netProj >= 200 ? softTone(SHADCN.success).border
                    : _netProj > -200 ? softTone(SHADCN.warning).border
                    : softTone(SHADCN.error).border;
                  const numColor = _netProj === null || _netProj >= 0 ? SHADCN.fg : SHADCN.error;

                  // 5 derniers mois clos + la projection du mois en cours.
                  const netSeries = [
                    ..._moisClos.slice(-5)
                      .filter(m => _raisAnnee[m]?.net != null)
                      .map(m => ({ periode: m, net: _raisAnnee[m].net })),
                    // _netProj projette toujours le mois EN COURS, même si l'on
                    // navigue sur un mois clos : la barre porte donc currentPeriode.
                    ...(_netProj !== null ? [{ periode: currentPeriode, net: _netProj, projete: true }] : []),
                  ];

                  const EYEBROW = { fontFamily: SHADCN.font, fontWeight: 500, fontSize: 11, lineHeight: 1.4, letterSpacing: '0.08em', textTransform: 'uppercase', color: SHADCN.mutedFg };

                  return (
                    <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', minWidth: 0, paddingRight: isMobile ? 0 : S[3] }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: S[3], marginBottom: S[4] }}>
                        <div style={{ ...EYEBROW }}>Résultat net projeté</div>
                        {_netProj !== null && dotsBtn(() => setDetailPanel(_panelNetProj()))}
                      </div>
                      <div>
                        <div style={{ display:'flex', alignItems:'baseline', gap: S[3], flexWrap:'wrap', marginBottom: S[3] }}>
                          <div style={{
                            fontFamily: SHADCN.font, fontWeight: 600,
                            fontSize: isMobile ? 48 : 'clamp(56px, 6.5vw, 84px)',
                            letterSpacing: '-0.035em', lineHeight: 0.95,
                            fontVariantNumeric: 'tabular-nums', color: numColor,
                          }}>
                            {_netProj === null ? '—' : <AnimatedAmount value={_netProj} format={v => (v >= 0 ? '+' : '−') + F(Math.abs(v))} />}
                          </div>
                          {_netProj !== null && (
                            <StatusPill
                              size="md"
                              tone={{ bg: verdictBg, border: verdictBorder, color: verdictColor }}
                              style={{ fontWeight: 600 }}
                            >
                              {verdict}
                            </StatusPill>
                          )}
                        </div>
                        {_netProj === null && dayOfMonth < MIN_PROJ_DAYS && (
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginBottom: S[2] }}>
                            Projection disponible à partir de J{MIN_PROJ_DAYS} — données insuffisantes (J{dayOfMonth} sur {daysInMonth})
                          </div>
                        )}
                        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap: S[4], flexWrap: 'wrap' }}>
                          <div style={{ ...TYPO.body, color: SHADCN.mutedFg, display:'flex', flexWrap:'wrap', gap:'0 6px' }}>
                            <span>Seuil de rentabilité</span>
                            <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 500 }}>{F(seuilHT)}</span>
                          </div>
                          {netSeries.length >= 2 && !isMobile && (
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap: 2, flexShrink: 0 }}>
                              <NetMiniChart series={netSeries} />
                              <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, fontSize: 9 }}>
                                Résultat net · dernier mois projeté
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                </div>

                {/* ── 6 KPI : 2 rangées de 3, grille homogène ── */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: S[3], marginBottom: S[7] }}>
                {(() => {
                  // Marge brute : même calcul que SIG cascade ci-dessous
                  const factAchatsKpi = facturesMois.filter(f =>
                    f.categorie === 'Fournisseurs — Food & Bev' ||
                    f.categorie === 'Fournisseurs — Retail' ||
                    f.categorie === 'Fournisseurs — Stock revente'
                  );
                  const achatsKpi = factAchatsKpi.reduce((s, f) => s + Math.abs(f.montant_ht ?? 0), 0);
                  const totalAchatsKpi = achatsKpi > 0 ? achatsKpi : (achatsFoodSuivi + achatsRetailSuivi);
                  const margePct = caNetHT >= 200 ? Math.round((caNetHT - totalAchatsKpi) / caNetHT * 100) : null;
                  const margeColor = margePct === null ? SHADCN.mutedFg
                    : margePct >= 65 ? SHADCN.success
                    : margePct >= 50 ? SHADCN.warning
                    : SHADCN.error;
                  const margeSub = margePct === null ? 'données insuffisantes'
                    : margePct >= 65 ? 'cible ≥ 65%'
                    : margePct >= 50 ? 'à surveiller'
                    : "trop d'achats";

                  const seuilRef    = seuilHT;
                  const seuilPct    = seuilRef > 0 ? Math.round(caNetHT / seuilRef * 100) : 0;
                  const seuilCapped = Math.min(100, seuilPct);
                  const seuilColor  = caNetHT >= seuilRef ? SHADCN.success
                    : seuilPct >= Math.round(prorataFact * 100) ? SHADCN.warning
                    : SHADCN.error;

                  const laborColor = ratioLabor === null ? SHADCN.mutedFg
                    : ratioLabor <= 38 ? SHADCN.success
                    : ratioLabor <= 42 ? SHADCN.warning
                    : SHADCN.error;
                  const laborSub = ratioLabor === null ? '—'
                    : ratioLabor <= 38 ? 'Marge recrutement OK'
                    : ratioLabor <= 42 ? 'À étudier'
                    : 'Sur-effectif';

                  const primeColor = primeCost === null ? SHADCN.mutedFg
                    : primeCost < 66 ? SHADCN.success
                    : primeCost < 71 ? SHADCN.warning
                    : SHADCN.error;

                  const MiniSpark = ({ data, color: c, w = 80, h = 32 }) => {
                    if (!data || data.length < 2) return null;
                    const min = Math.min(...data), max = Math.max(...data);
                    const range = max - min || 1;
                    const pts = data.map((v, i) => [
                      (i / (data.length - 1)) * w,
                      h - ((v - min) / range) * (h - 4) - 2,
                    ]);
                    const dPath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
                    const areaPath = `${dPath} L${w},${h} L0,${h} Z`;
                    const gid = `sg${Math.random().toString(36).slice(2, 7)}`;
                    return (
                      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ flexShrink: 0, display: 'block' }}>
                        <defs>
                          <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor={c} stopOpacity="0.2" />
                            <stop offset="100%" stopColor={c} stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d={areaPath} fill={`url(#${gid})`} />
                        <path d={dPath} fill="none" stroke={c} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    );
                  };

                  const KpiTile = ({ label, value, sub, color, progress, progressColor, onDetail, badge, spark }) => {
                    const s = softTone(color || SHADCN.primary);
                    return (
                      <div style={{
                        background: SHADCN.cardSolid, border: `1px solid ${SHADCN.border}`, borderRadius: R.xl,
                        padding: S[5], display: 'flex', flexDirection: 'column', gap: S[2],
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: S[2] }}>
                          <div style={{ fontSize: 13, fontWeight: 400, color: SHADCN.mutedFg }}>{label}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: S[2] }}>
                            {badge && (
                              <StatusPill
                                size="sm"
                                tone={{ bg: s.bg, border: s.border, color }}
                                style={{ fontWeight: 600 }}
                              >{badge}</StatusPill>
                            )}
                            {dotsBtn(onDetail)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: S[3] }}>
                          <div style={{ fontSize: 40, fontWeight: 600, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1, color: SHADCN.fg }}>
                            {value}
                          </div>
                          {spark && <MiniSpark data={spark} color={color || SHADCN.primary} />}
                        </div>
                        {progress != null && (
                          <div style={{ height: 4, background: SHADCN.border, borderRadius: R.pill, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: '100%', transform: `scaleX(${progress / 100})`, transformOrigin: 'left', background: progressColor || color || SHADCN.fg, borderRadius: R.pill, transition: 'transform .5s cubic-bezier(0.23, 1, 0.32, 1)' }} />
                          </div>
                        )}
                        {sub && <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{sub}</div>}
                      </div>
                    );
                  };

                  const sparkCA = [
                    ..._moisClos.slice(-5).map(p => Math.round((notionFinances?.cafe?.[p]?.recettes || 0) / 1.10)),
                    caNetHT,
                  ].filter(v => v > 0);

                  return (
                    <>
                      <KpiTile
                        label="Ventes du mois (HT)"
                        value={<AnimatedAmount value={caNetHT} format={F} />}
                        color={SHADCN.fg}
                        progress={seuilCapped}
                        progressColor={seuilColor}
                        sub={`${seuilPct}% du seuil ${F(seuilRef)}`}
                      />
                      <KpiTile
                        label="Marge brute"
                        value={margePct !== null ? `${margePct}%` : '—'}
                        color={margeColor}
                        sub={margeSub}
                      />
                      <KpiTile
                        label="Part salaires"
                        value={ratioLabor !== null ? `${ratioLabor}%` : 'N/A'}
                        color={laborColor}
                        sub={laborSub}
                        onDetail={() => ratioLabor !== null && setDetailPanel({
                          title: 'Labor Cost — détail',
                          subtitle: `Café · au ${dayOfMonth} du mois`,
                          rows: [
                            { type: 'sep', label: 'CALCUL DU RATIO' },
                            { type: 'item', label: 'Salaires versés (moy. 3 mois)', value: _F(cfRef.salaires) },
                            { type: 'item', label: '+ URSSAF, Klesia, ALAN − refacturation Léo', value: `= ${_F(coutSalarialCharge)}`, note: `Décaissements bancaires réels · ${cfMoisLabel}` },
                            { type: 'item', label: `Prorata J${dayOfMonth} / ${daysInMonth} jours`, value: _F(salaireChargeProratatise), bold: true },
                            { type: 'sep', label: 'RAPPORT AU CA HT' },
                            { type: 'item', label: `CA HT au ${dayOfMonth} du mois`, value: _F(caNetHT) },
                            { type: 'final', label: 'Ratio Labor Cost', value: `${ratioLabor}%`, color: ratioLabor <= 38 ? SHADCN.success : ratioLabor <= 42 ? SHADCN.warning : SHADCN.error },
                            { type: 'note', text: 'Cible : 25–38 % · Acceptable : 38–42 % · Sur-effectif : > 42 %' },
                          ],
                        })}
                      />
                      <KpiTile
                        label="Prime cost"
                        value={primeCost !== null ? `${primeCost}%` : '—'}
                        color={primeColor}
                        badge={primeCostStatus}
                        onDetail={() => primeCost !== null && setDetailPanel({
                          title: 'Prime cost — détail',
                          subtitle: `Café · au ${dayOfMonth} du mois`,
                          rows: [
                            { type: 'sep', label: 'COMPOSITION' },
                            { type: 'item', label: 'Part salaires (labor cost)', value: `${ratioLabor}%`, note: 'Salaires + charges patronales / CA HT' },
                            { type: 'item', label: 'Coût matières (food cost)', value: `${foodCostPct}%`, note: 'Achats Food & Bev + Retail / CA HT' },
                            { type: 'final', label: 'Prime cost', value: `${primeCost}%`, color: primeColor },
                            { type: 'sep', label: 'LECTURE' },
                            { type: 'note', text: 'Standard contrôle de gestion restauration : salaires + matières rapportés au CA HT.' },
                            { type: 'note', text: 'Cible : < 66 % · À surveiller : 66–71 % · Danger : > 71 %' },
                          ],
                        })}
                      />
                      <KpiTile
                        label="Ventes / jour (HT)"
                        value={caJourCockpit > 0 ? F(caJourCockpit) : '—'}
                        color={SHADCN.fg}
                        sub={`moyenne sur ${dayOfMonth} j`}
                      />
                      <KpiTile
                        label="Panier moyen"
                        value={panierMoyen !== null ? F(panierMoyen, 2) : '—'}
                        color={SHADCN.fg}
                        sub={nbCmds > 0 ? `${nbCmds} transactions` : 'en attente Square'}
                      />
                    </>
                  );
                })()}
                </div>

                {/* ── Charges à venir (pleine largeur, si non vide) ── */}
                {chargesATomber.length > 0 && (
                  <Card tone="default" style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: S[4] }}>
                      <div style={{ fontSize: 13, fontWeight: 400, color: SHADCN.mutedFg }}>Charges à venir ce mois</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: S[3] }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: S[2] }}>
                          <span style={{ fontSize: FS.xl, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: SHADCN.fg }}>−{F(montantATomber)}</span>
                          <span style={{ fontSize: FS.xs, color: SHADCN.mutedFg }}>sur {daysLeft} jours</span>
                        </div>
                        {dotsBtn(() => setDetailPanel(_panelCharges()))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: S[2] }}>
                      {[...chargesATomber].sort((a, b) => (a.jourMois || 99) - (b.jourMois || 99)).map((c) => (
                        <div key={c.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${S[2]}px ${S[3]}px`, borderRadius: R.md, background: SHADCN.muted }}>
                          <div style={{ fontSize: 13, color: SHADCN.fg }}>{c.label}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: SHADCN.fg }}>−{F(c.montant)}</span>
                            {c.jourMois && <span style={{ fontSize: 11, color: SHADCN.mutedFg }}>~{c.jourMois}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* ── 5. SIG cascade — visible par défaut ── */}
                <SectionTitle style={{ marginTop: S[7] }}>Construction du bénéfice</SectionTitle>
                <Card padded={false} style={{ overflow: 'hidden' }}>
                  {(() => {
                    const factAchats = facturesMois.filter(f =>
                      f.categorie === 'Fournisseurs — Food & Bev' ||
                      f.categorie === 'Fournisseurs — Retail' ||
                      f.categorie === 'Fournisseurs — Stock revente'
                    );
                    const achatsSupabase = factAchats.reduce((s, f) => s + Math.abs(f.montant_ht ?? 0), 0);
                    const totalAchatsHT  = achatsSupabase > 0 ? achatsSupabase : (achatsFoodSuivi + achatsRetailSuivi);
                    const srcAchats      = achatsSupabase > 0
                      ? `${factAchats.length} facture(s) enregistrée(s)`
                      : `Estimation (Notion/banque) · ajoutez vos factures pour le chiffre réel`;
                    const margeBrute = caNetHT - totalAchatsHT;
                    const tauxMarge  = caNetHT >= 200 ? Math.round(margeBrute / caNetHT * 100) : null;
                    const factChargesExtBrut = facturesMois.filter(f =>
                      f.categorie === 'Fournisseurs — Services' ||
                      f.categorie === 'Fournisseurs — Équipement' ||
                      f.categorie === 'Fournisseurs — Divers'
                    );
                    // Garde-fou : un seul loyer SCI par mois. Si plusieurs factures de loyer partagent la
                    // période (rattrapage de mois antérieurs émis le même jour), on garde celle dont le
                    // libellé nomme le mois courant, sinon la première — les autres ne pèsent pas sur le SIG.
                    const isLoyerSci = f => /sci/i.test(f.sous_categorie || '') || /loyer/i.test(f.libelle || '');
                    const loyersSci = factChargesExtBrut.filter(isLoyerSci);
                    const moisCourantRe = new RegExp(MOIS_FR[new Date().getMonth()], 'i');
                    const loyerRetenu = loyersSci.find(f => moisCourantRe.test(f.libelle || '')) || loyersSci[0] || null;
                    const factChargesExt = factChargesExtBrut.filter(f => !isLoyerSci(f) || f === loyerRetenu);
                    const chargesExtSupabase = factChargesExt.reduce((s, f) => s + Math.abs(f.montant_ht ?? 0), 0);
                    // La facture de loyer SCI est déjà dans factChargesExt → ne pas réajouter le loyer proratisé (sinon compté 2×)
                    const aFactureLoyer = loyerRetenu !== null;
                    const loyerProrata = aFactureLoyer ? 0 : Math.round(cfRef.loyerSCI * prorataFact);
                    const totalChargesExt = chargesExtSupabase + loyerProrata;
                    const srcChargesExt = chargesExtSupabase > 0
                      ? `${factChargesExt.length} facture(s)${aFactureLoyer ? ' (loyer SCI inclus)' : ' + loyer SCI (calculé au jour)'}${loyersSci.length > 1 ? ` · ${loyersSci.length - 1} loyer(s) d'autres mois ignoré(s)` : ''}`
                      : `Loyer SCI (calculé au jour) · importez les factures services/équipement pour affiner`;
                    const valeurAjoutee = margeBrute - totalChargesExt;
                    const tauxVA = caNetHT >= 200 ? Math.round(valeurAjoutee / caNetHT * 100) : null;
                    const totalPersonnel = salaireChargeProratatise; // ALAN déjà inclus dans coutSalarialCharge
                    const ebe    = valeurAjoutee - totalPersonnel;
                    const tauxEBE = caNetHT >= 200 ? Math.round(ebe / caNetHT * 100) : null;
                    const ebeBg  = tauxEBE === null ? SHADCN.fg : tauxEBE >= 15 ? SHADCN.success : tauxEBE >= 5 ? SHADCN.warning : SHADCN.error;
                    const ebeStatus = tauxEBE === null ? null : tauxEBE >= 15 ? 'TRÈS BON' : tauxEBE >= 5 ? 'CORRECT' : ebe >= 0 ? 'FAIBLE' : 'NÉGATIF';
                    const pct = v => caNetHT >= 200 ? `${Math.round(v / caNetHT * 100)}%` : '—';
                    const sigRows = [
                      { prefix:'', label:'Ventes (hors TVA)', value:F(caNetHT), vpct:'100%', isResult:false, indent:false, sub:null },
                      { prefix:'−', label:'Coût des marchandises vendues', value:`− ${F(totalAchatsHT)}`, vpct:pct(totalAchatsHT), isResult:false, indent:true, sub:srcAchats },
                      { prefix:'', label:'Marge brute (ce qu\'il reste après les achats)', value:F(margeBrute), vpct:tauxMarge !== null ? `${tauxMarge}%` : '—', isResult:true, color:tauxMarge === null ? SHADCN.fg : tauxMarge >= 65 ? SHADCN.success : tauxMarge >= 50 ? SHADCN.warning : SHADCN.error, indent:false, sub:null },
                      { prefix:'−', label:'Loyer, factures, services', value:`− ${F(totalChargesExt)}`, vpct:pct(totalChargesExt), isResult:false, indent:true, sub:srcChargesExt },
                      { prefix:'', label:'Marge après loyer & factures', value:F(valeurAjoutee), vpct:tauxVA !== null ? `${tauxVA}%` : '—', isResult:true, color:tauxVA === null ? SHADCN.fg : tauxVA >= 40 ? SHADCN.success : tauxVA >= 25 ? SHADCN.warning : SHADCN.error, indent:false, sub:null },
                      { prefix:'−', label:'Salaires + cotisations + mutuelle', value:`− ${F(totalPersonnel)}`, vpct:pct(totalPersonnel), isResult:false, indent:true, sub:`Salaires + cotisations + mutuelle décaissés (moy. 3 mois banque), calculé au ${dayOfMonth}` },
                      { prefix:'', label:"Bénéfice opérationnel du mois", value:F(ebe), vpct:tauxEBE !== null ? `${tauxEBE}%` : '—', isResult:true, isFinal:true, color:ebeBg, indent:false, sub:ebeStatus ? `${ebeStatus} · objectif ≥ 15 % des ventes · correct 5–14 % · faible < 5 %` : null },
                    ];
                    return (
                      <div>
                        {sigRows.map((row, i) => {
                          const infoOpen = openSigInfo === i;
                          const hasInfo  = row.sub && !row.isFinal;
                          const finalFg = row.isFinal ? row.color : null;
                          return (
                            <React.Fragment key={i}>
                              <div style={{
                                display:'flex', alignItems:'center', justifyContent:'space-between',
                                padding:row.isFinal ? '18px 14px' : '12px 14px',
                                paddingLeft:row.indent ? (isMobile ? 24 : 36) : 14,
                                borderTop: row.isResult && !row.isFinal ? `1px solid ${SHADCN.border}` : (i === 0 ? 'none' : `1px solid ${SHADCN.border}`),
                                background: row.isResult ? SHADCN.muted : 'transparent',
                              }}>
                                <div style={{ flex:1, minWidth:0, display:'flex', alignItems:'center', gap: S[2] }}>
                                  {row.prefix && <span style={{ ...TYPO.body, color:SHADCN.mutedFg, fontWeight:700, width:12, flexShrink:0, textAlign:'center' }}>{row.prefix}</span>}
                                  <span style={{ ...TYPO.body, color:row.isResult ? SHADCN.fg : SHADCN.mutedFg, fontWeight:row.isResult ? 700 : 400, fontSize:row.isFinal ? 14 : 13 }}>{row.label}</span>
                                  {hasInfo && (
                                    <button onClick={() => setOpenSigInfo(infoOpen ? null : i)} style={{ background:'none', border:'none', padding:2, cursor:'pointer', display:'inline-flex', alignItems:'center', color:infoOpen ? SHADCN.fg : SHADCN.mutedFg, flexShrink:0 }}>
                                      <Info size={13} strokeWidth={2} />
                                    </button>
                                  )}
                                </div>
                                <div style={{ textAlign:'right', paddingLeft:20, flexShrink:0, display:'flex', alignItems:'baseline', gap: S[3] }}>
                                  <span style={{ ...TYPO.eyebrow, color:row.isFinal ? finalFg : SHADCN.mutedFg, fontWeight:row.isFinal ? 700 : 500, minWidth: 44, textAlign:'right', fontVariantNumeric:'tabular-nums' }}>{row.vpct}</span>
                                  <span style={{ ...TYPO.num, fontFamily: SHADCN.font, fontSize:row.isFinal ? 22 : row.isResult ? 17 : 14, fontWeight:row.isFinal || row.isResult ? 700 : 400, lineHeight:1, color:row.isFinal ? finalFg : row.isResult ? (row.color || SHADCN.fg) : SHADCN.fg }}>{row.value}</span>
                                </div>
                              </div>
                              {row.sub && row.isFinal && (
                                <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, background: SHADCN.muted, padding:'0 14px 12px 14px' }}>{row.sub}</div>
                              )}
                              {hasInfo && infoOpen && (
                                <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, background:SHADCN.muted, borderTop:`1px solid ${SHADCN.border}`, padding:`8px 14px 10px ${row.indent ? (isMobile ? 36 : 48) : 14}px` }}>{row.sub}</div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    );
                  })()}
                </Card>

                {/* ── 5 ter. En banque — répartition BNP / Revolut (compte principal futur) ── */}
                {tresoCafeRevolut != null && (() => {
                  const partBNP = Math.abs(tresoCafeBNP);
                  const partRev = Math.abs(tresoCafeRevolut);
                  const totAbs  = partBNP + partRev;
                  const bnpPct  = totAbs > 0 ? Math.round(partBNP / totAbs * 100) : 100;
                  return (
                    <>
                      <SectionTitle style={{ marginTop: S[7] }}>En banque — Café</SectionTitle>
                      <Card style={{ marginBottom: S[5] }}>
                        <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[1] }}>Trésorerie consolidée · BNP + Revolut</div>
                        <div style={{ ...TYPO.num, fontSize: 28, fontWeight: 700, lineHeight: 1, marginBottom: S[3] }}>{F(tresoCafe)}</div>
                        <div style={{ display: 'flex', height: 6, borderRadius: R.xs, overflow: 'hidden', background: SHADCN.muted, marginBottom: S[3] }}>
                          <div style={{ width: `${bnpPct}%`,       background: SHADCN.mutedFg }} />
                          <div style={{ width: `${100 - bnpPct}%`, background: SHADCN.fg }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {[
                            { label: `BNP · ${bnpPct}%`,           value: F(tresoCafeBNP) },
                            { label: `Revolut · ${100 - bnpPct}%`, value: F(tresoCafeRevolut) },
                          ].map((row, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: S[3] }}>
                              <span style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{row.label}</span>
                              <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.fg, flexShrink: 0 }}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </>
                  );
                })()}

                {/* ── 5 quater. Virement BNP → Revolut conseillé ── */}
                {/* Revolut n'encaisse rien : sans virement, les salaires du 3 ne
                    partent pas. Sa place est ici, sous la répartition des deux
                    comptes. Admin et admin Café seulement — c'est de la trésorerie,
                    pas une info d'équipe — et sur le mois courant : conseiller
                    un virement en consultant août n'a pas de sens. */}
                {(role === 'admin' || role === 'cafe_admin') && isCurrentMonth && tresoCafeRevolut != null && (
                  <>
                    <SectionTitle style={{ marginTop: S[7] }}>Virement BNP → Revolut</SectionTitle>
                    <VirementConseille
                      soldeBNP={tresoCafeBNP}
                      soldeRevolut={tresoCafeRevolut}
                      jour={now.getDate()}
                      dateSoldeBNP={data?.lastImportBNP}
                      style={{ marginBottom: S[5] }}
                      echeances={CHARGES_FIXES_REF
                        .filter(ch => ch.montant > 0)
                        .map(ch => ({
                          jour:    ch.jourMois,
                          label:   ch.label,
                          montant: ch.montant,
                          // Les salaires partent de Revolut depuis juillet 2026 :
                          // ils ne pèsent pas sur ce que BNP doit garder.
                          compte:  ch.cle === 'salaires' ? 'Revolut' : 'BNP',
                          // « Payé » = constaté en banque ce mois-ci, via la même
                          // checklist que l'état du mois — pas une date atteinte.
                          paye:    !chargesATomber.find(x => x.label === ch.label),
                        }))}
                    />
                  </>
                )}

                {/* ── 5 bis. Budget stock disponible ── */}
                {/* Le réassort se finance sur ce que le retail a vendu, pas sur la
                    trésorerie. L'ancien calcul (tréso nette − un mois entier de
                    dépenses) rendait 0 en permanence : le Café tourne avec une
                    semaine de tréso, il ne détiendra jamais 18 000 € d'avance —
                    et le seuil incluait les achats de stock eux-mêmes, donc on
                    réservait les achats de stock pour calculer le budget d'achat
                    de stock. La tréso ne sert plus que de plafond. */}
                {hasTresoReelle && (() => {
                  // Part du CA HT qui repart en stock : le Café marge à 30 % sur le retail,
                  // donc 70 % du prix de vente HT est le coût de rachat de l'article vendu.
                  const TAUX_REINVEST = 0.7;
                  // Plancher volontairement bas : tresoNetteCafe déduit déjà les
                  // charges fixes restantes du mois et la TVA collectée. Ces 1 000 €
                  // ne couvrent que l'imprévu (casse, réparation), pas les échéances.
                  const PLANCHER_CASH = 1000;
                  // Fenêtre glissante (30 j, durée et bornes fournies par le serveur) : ventes
                  // et achats se comparent sur la même durée. Sur le mois calendaire,
                  // une commande passée le 2 mettait le budget à zéro jusqu'au 30.
                  // Sans la fenêtre (ancien cache, Square muet) on retombe sur le mois.
                  const glissant        = squareData?.ventesGlissantes?.groups && notionFinances?.cafe && notionFinances.achatsStock
                    ? squareData.ventesGlissantes : null;
                  const fenetreLabel    = glissant ? `${glissant.jours} jours glissants` : prorataLabel.toLowerCase();
                  // Deux budgets : les vinyles se financent sur les ventes de vinyles,
                  // la presse et le merch sur les leurs. Mélangés, un mois fort en
                  // magazines autorisait une commande de disques, et l'inverse.
                  // Achats = factures fournisseurs de la fenêtre (date de facture, HT) :
                  // une commande payable à 30 jours pèse dès qu'elle est facturée.
                  // S'y ajoutent les retraits cash tagués vinyle dans l'onglet DAB,
                  // qui n'ont pas de facture.
                  const dansFenetre = d => String(d).slice(0, 10) >= glissant.du && String(d).slice(0, 10) <= glissant.au;
                  const achatsFenetre = glissant ? [
                    ...notionFinances.achatsStock.filter(f => dansFenetre(f.date)).map(f => ({ montant: f.ht, vinyle: f.vinyle })),
                    ...Object.values(notionFinances.cafe)
                      .flatMap(p => p.transactions || [])
                      .filter(tx => tx.montant < 0 && tx.tag_vinyle && dansFenetre(tx.date))
                      .map(tx => ({ montant: Math.abs(tx.montant), vinyle: true })),
                  ] : [];
                  const estVinyle = a => a.vinyle;
                  const somme = as => Math.round(as.reduce((t, a) => t + a.montant, 0));
                  const g = glissant?.groups ?? {};
                  // Sans la fenêtre (ancien cache, Square muet) : un seul bloc, sur le mois.
                  const poles = (glissant ? [
                    { titre: 'vinyles',     ventesLabel: 'Ventes vinyles HT',
                      ventes: g.Vinyles?.ht ?? 0, achats: somme(achatsFenetre.filter(estVinyle)) },
                    { titre: 'mag · merch', ventesLabel: 'Ventes presse, livres, merch HT',
                      ventes: (g.Disquaire?.ht ?? 0) + (g.Retail?.ht ?? 0), achats: somme(achatsFenetre.filter(tx => !estVinyle(tx))) },
                  ] : [
                    { titre: 'vinyle · mag · merch', ventesLabel: 'Ventes retail HT', ventes: caRetailGroupHT, achats: achatsRetailSuivi },
                  ]).map(p => {
                    const budget = Math.round(p.ventes * TAUX_REINVEST);
                    return { ...p, budget, solde: budget - p.achats, theorique: Math.max(0, budget - p.achats) };
                  });
                  // Le plafond de trésorerie vaut pour l'ensemble : s'il mord, chaque
                  // budget est réduit dans la même proportion.
                  const totalTheorique  = poles.reduce((t, p) => t + p.theorique, 0);
                  const plafondCash     = Math.max(0, Math.round(tresoNetteCafe - PLANCHER_CASH));
                  const brideCash       = totalTheorique > plafondCash;
                  const partCash        = brideCash ? plafondCash / totalTheorique : 1;
                  return (
                    <>
                      <SectionTitle style={{ marginTop: S[7] }}>Budget stock disponible</SectionTitle>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile || poles.length < 2 ? '1fr' : '1fr 1fr', gap: S[4], marginBottom: S[5] }}>
                        {poles.map(p => {
                          const reste       = Math.round(p.theorique * partCash);
                          const pct         = p.budget > 0 ? Math.min(100, Math.round(p.achats / p.budget * 100)) : 100;
                          const variant     = reste > 500 ? 'success' : reste > 0 ? 'warning' : 'error';
                          const statusColor = SHADCN[variant];
                          const statusLabel = brideCash && p.theorique > 0
                            ? (reste > 0 ? 'Limité par la trésorerie' : 'Trésorerie au plancher')
                            : reste > 500 ? 'Marge disponible' : reste > 0 ? 'Marge serrée' : 'Budget consommé';
                          return (
                            <Card key={p.titre} padded={false} style={{ overflow: 'hidden' }}>
                              <div style={{ height: 4, background: SHADCN.muted }}>
                                <div style={{ height: '100%', width: `${100 - pct}%`, background: statusColor, transition: 'width 0.4s ease' }} />
                              </div>
                              <div style={{ padding: '16px 14px' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: S[3], marginBottom: S[4] }}>
                                  <div>
                                    <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[1] }}>Reste à investir — {p.titre}</div>
                                    <div style={{ ...TYPO.num, fontSize: 28, fontWeight: 700, lineHeight: 1, color: statusColor }}>{F(reste)}</div>
                                  </div>
                                  <StatusPill variant={variant}>{statusLabel}</StatusPill>
                                </div>
                                <div style={{ borderTop: `1px solid ${SHADCN.border}`, paddingTop: S[3], display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {[
                                    { label: `${p.ventesLabel} (${fenetreLabel})`, value: F(p.ventes), muted: false, bold: false },
                                    { label: `Budget de réassort : ${Math.round(TAUX_REINVEST * 100)} % de ces ventes`, value: F(p.budget), muted: true, bold: false },
                                    { label: glissant ? `− Stock déjà commandé (factures, ${fenetreLabel})` : `− Stock déjà acheté (${fenetreLabel})`, value: `− ${F(p.achats)}`, muted: true, bold: false },
                                    p.solde >= 0
                                      ? { label: '= Reste du budget', value: F(p.theorique), muted: false, bold: true }
                                      : { label: '= Budget dépassé de', value: F(-p.solde), muted: false, bold: true },
                                    ...(brideCash && p.theorique > 0 ? [{ label: `Plafond trésorerie, tous budgets (solde net − ${F(PLANCHER_CASH)} de plancher)`, value: F(plafondCash), muted: false, bold: false }] : []),
                                  ].map((row, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: S[3] }}>
                                      <span style={{ ...TYPO.caption, color: row.muted ? SHADCN.mutedFg : SHADCN.fg }}>{row.label}</span>
                                      <span style={{ ...TYPO.caption, ...TYPO.num, color: row.muted ? SHADCN.mutedFg : SHADCN.fg, fontWeight: row.bold ? 700 : 400, flexShrink: 0 }}>{row.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                {/* ── 6. Charges fixes — checklist visible ── */}
                <SectionTitle style={{ marginTop: S[7] }} right={cfMoisLabel && (
                  <span style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>Moyenne banque · {cfMoisLabel}</span>
                )}>Charges fixes — état du mois</SectionTitle>

                <Card>
                  {!hasTxDetail && (
                    <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                      Aucune transaction ce mois-ci · importer le relevé BNP
                    </div>
                  )}
                  <div style={{ display:'flex', flexDirection:'column' }}>
                    {CHARGES_FIXES_REF.map((c,i) => {
                      const paid = !chargesATomber.find(x => x.label === c.label);
                      // En retard : le jour d'échéance observé est passé et rien n'a
                      // été prélevé. Trois loyers SCI ont pu filer sans alerte parce
                      // que « en attente » couvrait aussi bien le 2 du mois qu'un
                      // impayé de deux semaines.
                      const late = !paid && isCurrentMonth && hasTxDetail && c.jourMois && dayOfMonth > c.jourMois;
                      const ton  = paid ? SHADCN.success : late ? SHADCN.destructive : SHADCN.warning;
                      return (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:i < CHARGES_FIXES_REF.length-1 ? `1px solid ${SHADCN.border}` : 'none', transition:'opacity 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <span
                              key={paid ? 'on' : 'off'}
                              style={{
                                display:'inline-flex',
                                animation: paid ? 'sk-charge-paid 520ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
                              }}
                            >
                              {paid ? <CheckCircle size={14} color={SHADCN.success} strokeWidth={2.2}/> : <Circle size={14} color={SHADCN.mutedFg} strokeWidth={1.5}/>}
                            </span>
                            <span style={{ ...TYPO.body, color:paid ? SHADCN.mutedFg : SHADCN.fg, transition:'color 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}>{c.label}</span>
                            {c.jourMois && !paid && <span style={{ ...TYPO.caption, color: late ? SHADCN.destructive : SHADCN.mutedFg }}>{late ? `attendu le ${c.jourMois}` : `vers le ${c.jourMois}`}</span>}
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <span style={{ ...TYPO.body, ...TYPO.num, color:paid ? SHADCN.mutedFg : SHADCN.fg, transition:'color 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}>−{F(c.montant)}</span>
                            <div style={{ ...TYPO.eyebrow, color:ton, background:softTone(ton).bg, border:`1px solid ${softTone(ton).border}`, borderRadius:R.xs, padding:'2px 7px', transition:'color 500ms cubic-bezier(0.22, 1, 0.36, 1), background 500ms cubic-bezier(0.22, 1, 0.36, 1), border-color 500ms cubic-bezier(0.22, 1, 0.36, 1)' }}>
                              {paid ? 'Passé' : late ? 'En retard' : 'En attente'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop: S[3], marginTop: S[1], borderTop:`2px solid ${SHADCN.border}` }}>
                      <span style={{ ...TYPO.eyebrow, fontWeight:700, color:SHADCN.fg }}>Total fixes</span>
                      <div style={{ display:'flex', gap: S[4], alignItems:'center' }}>
                        <span style={{ ...TYPO.body, ...TYPO.num, fontWeight:700, color:SHADCN.fg }}>{F(totalChargesCafe)}</span>
                        {montantATomber > 0 && <span style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.warning }}>Restant : {F(montantATomber)}</span>}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* ── 7. Détails complémentaires (collapses secondaires) ── */}
                <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom: S[2], marginTop: S[5] }}>DÉTAILS COMPLÉMENTAIRES</div>

                <Collapse title="Bénéfice — historique 3 mois et année" open={openRepli['net']} onToggle={() => setOpenRepli(p => ({ ...p, net: !p.net }))}>
                  {_chargesAnnuToVerify && (() => {
                    const s = softTone(SHADCN.warning);
                    return (
                      <div style={{ ...TYPO.caption, color: SHADCN.warning, marginBottom: S[3], padding:'8px 12px', background: s.bg, border: `1px solid ${s.border}`, borderRadius: R.md }}>
                        Charges annuelles estimées ({_chargesAnnuMens} €/mois) — à valider
                      </div>
                    );
                  })()}
                  {/* Le RAI "CE MOIS PROJETÉ" est déjà affiché dans le Hero — on liste ici les 2 vues complémentaires */}
                  <div style={{ display:'grid', gridTemplateColumns:isMobile ? '1fr' : 'repeat(2, 1fr)', gap: S[3] }}>
                    {[
                      { label:'MOYENNE 3 DERNIERS MOIS', val:_gliss3M?.avg ?? null, sub:_gliss3M ? `Moyenne sur ${_gliss3M.nb} mois complets` : 'Pas de données', subsub:null },
                      { label:`TOTAL DEPUIS LE 1ER JANVIER ${_annee}`, val:_ytdNet, sub:_moisClos.length > 0 ? `${_moisClos.length} mois complets · sans le mois en cours` : 'Pas de données', subsub:null },
                    ].map(({ label, val, sub, subsub }) => {
                      const pos = val !== null && val >= 0;
                      const neg = val !== null && val < 0;
                      const accent = pos ? SHADCN.success : neg ? SHADCN.error : SHADCN.mutedFg;
                      const soft = softTone(accent);
                      const panelFn = label.includes('3') ? _panelGliss3M : _panelYTD;
                      return (
                        <div
                          key={label}
                          onClick={() => val !== null && setDetailPanel(panelFn())}
                          style={{
                            background: soft.bg,
                            border: `1px solid ${soft.border}`,
                            borderRadius: R.md,
                            padding: '20px 18px',
                            cursor: val !== null ? 'pointer' : 'default',
                          }}
                        >
                          <div style={{ ...TYPO.eyebrow, color: accent, marginBottom: S[2] }}>{label}</div>
                          <div style={{ ...TYPO.display, fontSize:28, color: accent, lineHeight:1.1, marginBottom:6, textDecoration: val !== null ? 'underline dotted' : 'none', textUnderlineOffset:4 }}>
                            {val === null ? '—' : (val >= 0 ? '+' : '−') + F(Math.abs(val))}
                          </div>
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>{sub}</div>
                          {subsub && <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1] }}>{subsub}</div>}
                        </div>
                      );
                    })}
                  </div>
                </Collapse>

                <Collapse title="Coût des matières & fournisseurs" open={openRepli['food']} onToggle={() => setOpenRepli(p => ({ ...p, food: !p.food }))}>
                  {(() => {
                    const soft = softTone(foodBgColor);
                    return (
                      <div style={{ background: soft.bg, border: `1px solid ${soft.border}`, borderRadius: R.md, padding: S[4], marginBottom: S[4] }}>
                        <div style={{ ...TYPO.eyebrow, color: foodBgColor, marginBottom: S[2] }}>PART DU CAFÉ/NOURRITURE DANS LES VENTES</div>
                        <div style={{ display:'flex', alignItems:'center', gap: S[3], marginBottom: S[2] }}>
                          <div style={{ ...TYPO.metric, color: foodBgColor }}>{foodCostPct !== null ? `${foodCostPct}%` : 'N/A'}</div>
                          {foodStatus && (
                            <div style={{ ...TYPO.caption, fontWeight:600, color: foodBgColor, background: soft.bg, border:`1px solid ${soft.border}`, borderRadius: R.pill, padding:`${S[1]}px ${S[3]}px`, textTransform:'uppercase', letterSpacing:'0.04em' }}>{foodStatus}</div>
                          )}
                        </div>
                        <div style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>
                          {F(achatsFoodSuivi)} d'achats café/boissons (hors TVA) · sur des ventes de {caCafeGroupHT > 0 ? F(caCafeGroupHT) : '—'} (hors TVA) · objectif ≤ {Math.round(FOOD_COST_CIBLE*100)}%
                        </div>
                      </div>
                    );
                  })()}
                  {foodTxLines && foodTxLines.length > 0 && (
                    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom: S[5] }}>
                      <thead>
                        <tr style={{ borderBottom:`1px solid ${SHADCN.border}` }}>
                          <th style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, textAlign:'left', padding:`${S[1]}px 0` }}>Libellé</th>
                          <th style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, textAlign:'right', padding:`${S[1]}px ${S[2]}px` }}>Date</th>
                          <th style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, textAlign:'right', padding:`${S[1]}px 0` }}>Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        {foodTxLines.map((tx, i) => (
                          <tr key={i} style={{ borderBottom:`1px solid ${SHADCN.border}` }}>
                            <td style={{ ...TYPO.body, color:SHADCN.fg, padding:`${S[2]}px 0` }}>{(tx.libelle||'').substring(0,60)}</td>
                            <td style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.mutedFg, textAlign:'right', padding:`${S[2]}px ${S[2]}px` }}>{tx.date||'—'}</td>
                            <td style={{ ...TYPO.body, ...TYPO.num, color:SHADCN.fg, textAlign:'right', padding:`${S[2]}px 0`, fontWeight:500 }}>−{F(Math.abs(tx.montant))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {top3Fournisseurs.length > 0 && (
                    <div style={{ background: SHADCN.bg, border: `1px solid ${SHADCN.border}`, borderRadius: R.md, padding: S[4] }}>
                      <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                        TOP 3 FOURNISSEURS
                        <span style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginLeft: S[2], fontWeight:400, letterSpacing:0, textTransform:'none' }}>
                          vs moyenne {top3Fournisseurs[0]?.nbPrevMois > 0 ? `${top3Fournisseurs[0].nbPrevMois} mois précédents` : 'N/A'}
                        </span>
                      </div>
                      {top3Fournisseurs.map((f, i) => {
                        const delta = f.avg !== null ? f.current - f.avg : null;
                        const deltaColor = delta === null ? SHADCN.mutedFg : delta > 0 ? SHADCN.error : SHADCN.success;
                        return (
                          <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr auto auto', alignItems:'center', gap: S[4], padding:`${S[3]}px 0`, borderBottom:i < top3Fournisseurs.length-1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                            <div>
                              <div style={{ ...TYPO.eyebrow, color:SHADCN.fg, marginBottom: S[1] }}>{f.nom}</div>
                              <div style={{ height: S[1], background:SHADCN.border, borderRadius: R.pill, overflow:'hidden' }}>
                                <div style={{ height:'100%', background:delta === null ? SHADCN.mutedFg : delta > 0 ? SHADCN.error : SHADCN.success, width:`${Math.min(100, f.avg ? Math.round(f.current / f.avg * 100) : 100)}%`, borderRadius: R.pill }} />
                              </div>
                            </div>
                            <div style={{ textAlign:'right' }}>
                              <div style={{ ...TYPO.h2, ...TYPO.num, color:SHADCN.fg }}>{F(f.current)}</div>
                              {f.avg !== null && <div style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.mutedFg }}>moy. {F(f.avg)}</div>}
                            </div>
                            <div style={{ ...TYPO.body, ...TYPO.num, fontWeight:700, color:deltaColor, minWidth: S[7], textAlign:'right' }}>
                              {delta === null ? '—' : `${delta > 0 ? '+' : ''}${F(delta)}`}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Collapse>

              </>
            );
          })()}
          </EntityAccentContext.Provider>
          )}

          {/* ═══════ Catalogue Square — liste filtrable + audit TVA (sous-vues) ══ */}
          {activeTab === 'articles_liste' && (() => {
            const sum = tvaAudit?.summary;
            const totalAno = sum?.anomalies ?? 0;
            const BUCKETS = [
              { key:'sans-taxe',       label:'Sans taxe',       color:SHADCN.error   },
              { key:'multi-taxe',      label:'Multi-taxe',      color:SHADCN.warning },
              { key:'taxe-desactivee', label:'Taxe désactivée', color:SHADCN.warning },
              { key:'taux-incoherent', label:'Taux ≠ attendu',  color:SHADCN.error   },
            ];
            const exportCsv = () => {
              const rows = BUCKETS.flatMap(b => (tvaAudit?.buckets?.[b.key] || []).map(a => ({
                anomalie:  b.label,
                article:   a.name || '(sans nom)',
                categorie: a.category,
                applique:  a.appliedPercentages?.length ? a.appliedPercentages.map(p => `${p}%`).join(' + ') : '',
                attendu:   a.expectedPercentage !== null && a.expectedPercentage !== undefined ? `${a.expectedPercentage}%` : '',
                detail:    a.detail || '',
                lien:      `https://app.squareup.com/dashboard/items/library/${a.itemId}`,
              })));
              if (!rows.length) return;
              const esc = v => {
                const s = String(v ?? '');
                return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
              };
              const header = ['Anomalie','Article','Catégorie','Taux appliqué','Taux attendu','Détail','Lien Square'];
              const lines = [header, ...rows.map(r => [r.anomalie, r.article, r.categorie, r.applique, r.attendu, r.detail, r.lien])];
              const csv = '﻿' + lines.map(l => l.map(esc).join(';')).join('\r\n');
              const today = new Date().toISOString().slice(0, 10);
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `audit-tva-sonore_${today}.csv`;
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            };
            const tvaTitle = tvaAuditLoading
              ? 'Analyse du catalogue Square…'
              : !sum
                ? 'Audit indisponible'
                : totalAno === 0
                  ? 'Catalogue cohérent'
                  : `${totalAno} ${totalAno > 1 ? 'anomalies à corriger' : 'anomalie à corriger'}`;
            return (
              <>
                {/* Sous-nav in-page : liste filtrable ⇄ audit TVA (même source Square) */}
                <div style={{ display:'flex', marginBottom: S[5] }}>
                  <SegmentedControl
                    value={catalogueView}
                    onChange={setCatalogueView}
                    tabs={[
                      { id:'catalogue', label:'Catalogue', Icon: Tag },
                      { id:'tva',       label:'Audit TVA', Icon: Percent, count: totalAno || undefined },
                    ]}
                  />
                </div>

                {catalogueView === 'catalogue' ? (
                  <ArticlesCatalogue items={tvaAudit?.items} loading={tvaAuditLoading} taxesUsed={tvaAudit?.taxesUsed} />
                ) : (
                <>
                <PageHeader
                  eyebrow="Café · audit TVA"
                  title={tvaTitle}
                  meta="10% restauration sur place · 20% alcool / retail / services · 5,5% livres · 2,1% magazines"
                />
                <div style={{ display:'flex', flexDirection:'column', gap: S[5] }}>
                {tvaAuditLoading && (
                  <div style={{ ...TYPO.body, color:SHADCN.mutedFg }}>Chargement de l'audit…</div>
                )}

                {!tvaAuditLoading && sum && (
                  <>
                    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: S[3] }}>
                      <div style={{ background:SHADCN.bg, border:`1px solid ${SHADCN.border}`, borderRadius: R.lg, padding: S[4] }}>
                        <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom: S[2] }}>Articles</div>
                        <div style={{ fontFamily: SHADCN.font, fontWeight:600, fontSize:24, letterSpacing:'-0.015em', fontVariantNumeric:'tabular-nums', color:SHADCN.fg, lineHeight:1 }}>{sum.totalItems}</div>
                      </div>
                      <div style={{ background:SHADCN.bg, border:`1px solid ${SHADCN.border}`, borderRadius: R.lg, padding: S[4] }}>
                        <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom: S[2] }}>Conformes</div>
                        <div style={{ fontFamily: SHADCN.font, fontWeight:600, fontSize:24, letterSpacing:'-0.015em', fontVariantNumeric:'tabular-nums', color:SHADCN.success, lineHeight:1 }}>{sum.ok}</div>
                      </div>
                      <div style={{ background:SHADCN.bg, border:`1px solid ${SHADCN.border}`, borderRadius: R.lg, padding: S[4] }}>
                        <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom: S[2] }}>Anomalies</div>
                        <div style={{ fontFamily: SHADCN.font, fontWeight:600, fontSize:24, letterSpacing:'-0.015em', fontVariantNumeric:'tabular-nums', color: totalAno ? SHADCN.error : SHADCN.success, lineHeight:1 }}>{totalAno}</div>
                      </div>
                    </div>

                    {tvaAudit.taxesUsed?.length > 0 && (
                      <div>
                        <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, marginBottom:6 }}>Taux définis dans Square</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                          {tvaAudit.taxesUsed.map(t => (
                            <span key={t.id} style={{
                              ...TYPO.caption, ...TYPO.num,
                              background: t.enabled ? SHADCN.bg : SHADCN.muted,
                              border:`1px solid ${SHADCN.border}`,
                              color: t.enabled ? SHADCN.fg : SHADCN.mutedFg,
                              padding:'4px 8px', borderRadius: R.sm, whiteSpace:'nowrap',
                            }}>
                              {t.name} — {t.percentage}%{!t.enabled && ' (désactivée)'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {totalAno === 0 ? (
                      <div style={{ ...TYPO.body, color:SHADCN.success }}>
                        Aucune anomalie détectée. Tous les articles ont une taxe unique cohérente avec leur catégorie.
                      </div>
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', gap: S[4] }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap: S[3], flexWrap:'wrap' }}>
                          <div style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>
                            Exporte la liste complète pour transmettre au comptable.
                          </div>
                          <Button variant="primary" size="sm" onClick={exportCsv}>
                            Exporter CSV ({totalAno})
                          </Button>
                        </div>
                        {BUCKETS.map(b => {
                          const list = tvaAudit.buckets?.[b.key] || [];
                          if (!list.length) return null;
                          return (
                            <div key={b.key}>
                              <div style={{ ...TYPO.eyebrow, color:b.color, marginBottom:6 }}>
                                {b.label} — {list.length}
                              </div>
                              <ArticleTable
                                rows={list}
                                max={50}
                                columns={[
                                  { key:'name', label:'Article', render:a => a.name || '(sans nom)' },
                                  { key:'category', label:'Catégorie', nowrap:true, cellStyle:{ color:SHADCN.mutedFg }, render:a => a.category },
                                  { key:'detail', label:'Détail', cellStyle:TYPO.caption, render:a => a.detail },
                                  { key:'square', label:'Square', nowrap:true, render:a => <SquareItemLink itemId={a.itemId} /> },
                                ]}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {tvaAudit.unknownCategories?.length > 0 && (
                      <div style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>
                        Catégories sans taux attendu : {tvaAudit.unknownCategories.join(', ')}.
                      </div>
                    )}
                  </>
                )}
                </div>
                </>
                )}
              </>
            );
          })()}

          {/* ═════════════════════ TOP VENTES PRODUITS ══════════════════════ */}
          {activeTab === 'articles_ventes' && (() => {
            const DISQUAIRE_CATS = new Set(['Vinyles', 'Magazines', 'Affiches', 'Livres']);
            const filterDisquaire = rows => (rows || []).filter(it => !DISQUAIRE_CATS.has(it.category));
            const tp = topProduits ? { ...topProduits, top: filterDisquaire(topProduits.top), bottom: filterDisquaire(topProduits.bottom) } : null;
            const F  = v => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v ?? 0);
            const now2 = new Date();
            const selectedMois = topProduitsMois ?? `${now2.getFullYear()}-${String(now2.getMonth() + 1).padStart(2, '0')}`;

            const TopTable = ({ rows, label, color }) => (
              <div>
                <div style={{ ...TYPO.eyebrow, color, marginBottom: S[3] }}>{label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {rows.map((it, i) => (
                    <div key={it.itemId ?? i} style={{
                      display: 'flex', alignItems: 'center', gap: S[3],
                      padding: `${S[2]}px ${S[3]}px`, borderRadius: R.md,
                      background: i % 2 === 0 ? 'transparent' : SHADCN.muted,
                    }}>
                      <span style={{ ...TYPO.num, color: SHADCN.mutedFg, minWidth: 20, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ ...TYPO.body, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                      <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, flexShrink: 0 }}>{it.category}</span>
                      <span style={{ ...TYPO.num, flexShrink: 0, fontWeight: 600, minWidth: 60, textAlign: 'right' }}>{F(it.ca)}</span>
                      <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, flexShrink: 0, minWidth: 40, textAlign: 'right' }}>{it.qty} vte{it.qty > 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            );

            return (
              <>
                <PageHeader eyebrow="Café · Monitoring" title="Top ventes" />
                <div style={{ display: 'flex', alignItems: 'center', gap: S[3], flexWrap: 'wrap' }}>
                  <MoisSelector
                    mois={selectedMois}
                    onChange={m => { setTopProduitsMois(m); setTopProduits(null); }}
                  />
                  {tp && (
                    <span style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginLeft: 'auto', marginBottom: S[5] }}>
                      {tp.nbArticles} articles · {tp.nbCommandes} commandes
                    </span>
                  )}
                </div>

                {topProduitsLoading && <Skeleton h={300} />}

                {!topProduitsLoading && tp && (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: S[6] }}>
                    <TopTable rows={tp.top}    label={`Top ${tp.top.length} — meilleurs CA`}   color={SHADCN.success} />
                    {tp.bottom.length > 0 && (
                      <TopTable rows={tp.bottom} label={`Flop ${tp.bottom.length} — plus faibles CA`} color={SHADCN.mutedFg} />
                    )}
                  </div>
                )}

                {!topProduitsLoading && !tp && (
                  <div style={{ ...TYPO.body, color: SHADCN.mutedFg }}>Aucune donnée pour ce mois.</div>
                )}
              </>
            );
          })()}

          {/* ═══════════════════════ SCI ════════════════════════════════════ */}
          {activeTab === 'sci' && (
          <EntityAccentContext.Provider value={SHADCN.mutedFg}>
          {(() => {
            const S        = data.sci.loyersSuivi;
            // Solde live (dernier import BNP via /api/finances) — le snapshot
            // manuel data.tresorerie.sci n'est plus qu'un filet de secours.
            const tresoSCI = tresoSci;
            const sciSoldePeriode = Object.entries(notionFinances?.sci || {})
              .filter(([, v]) => v.solde != null).map(([k]) => k).sort().pop() || null;
            const pret     = data.sci.mensualitePret;
            const sciMoisLabel = (data.sci.moisReference || []).map(p => MOIS_FR[Number(p.slice(5, 7)) - 1]).join(' · ');
            const moisCourant = S?.loyers?.find(m => m.mois === `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
            const moisLabel = moisCourant ? moisCourant.label : MOIS_FR[now.getMonth()] + ' ' + now.getFullYear();

            // Loyers reçus ce mois
            const loyerBSRecu  = moisCourant?.bureauSonore?.recu  ?? 0;
            const loyerCSRecu  = moisCourant?.cafeSonore?.recu    ?? 0;
            const loyerAttendu = data.sci.loyersSuivi?.loyerAttendu ?? 0;
            const totalRecu    = loyerBSRecu + loyerCSRecu;
            const totalAttendu = moisCourant
              ? (moisCourant.bureauSonore?.attendu ?? 0) + (moisCourant.cafeSonore?.attendu ?? 0)
              : loyerAttendu * 2;
            const ratioEncaisse = totalAttendu > 0 ? totalRecu / totalAttendu : 0;

            // Retards tous mois confondus
            const totalRetard = S?.loyers?.reduce((acc, m) => {
              const bs = m.bureauSonore.statut !== 'en_cours' ? Math.max(0, m.bureauSonore.attendu - m.bureauSonore.recu) : 0;
              const cs = m.cafeSonore.statut   !== 'en_cours' ? Math.max(0, m.cafeSonore.attendu   - m.cafeSonore.recu)   : 0;
              return acc + bs + cs;
            }, 0) ?? 0;

            const peutPayerPret = tresoSCI + totalRecu >= pret;
            const tresoColor    = tresoSCI < 0 ? SHADCN.error : tresoSCI < 1000 ? SHADCN.warning : SHADCN.success;

            // Badges alignés sur softTone() — cohérents avec le thème dark
            const statutStyle = (s) => {
              const map = {
                ok:       { tone: softTone(SHADCN.success),   label: 'Reçu' },
                partiel:  { tone: softTone(SHADCN.warning),  label: 'Partiel' },
                retard:   { tone: softTone(SHADCN.warning),  label: 'Retard' },
                impaye:   { tone: softTone(SHADCN.error),  label: 'Impayé' },
                en_cours: { tone: softTone(SHADCN.mutedFg),  label: 'En cours' },
              };
              return map[s] || { tone: softTone(SHADCN.mutedFg), label: s };
            };

            const Badge = ({ statut }) => {
              const st = statutStyle(statut);
              return <span style={{ ...TYPO.eyebrow, fontWeight:500, background:st.tone.bg, color:st.tone.text, border:`1px solid ${st.tone.border}`, padding:'2px 8px', borderRadius:R.xs }}>{st.label}</span>;
            };

            const LoyerRow = ({ label, statut, recu, attendu, dateRecu }) => (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 0', borderBottom:`1px solid ${SHADCN.border}` }}>
                <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                  <span style={{ ...TYPO.body, fontWeight:500, color:SHADCN.fg }}>{label}</span>
                  {dateRecu && <span style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>reçu le {dateRecu}</span>}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap: S[3] }}>
                  <span style={{ ...TYPO.body, ...TYPO.num, color: recu >= attendu ? SHADCN.success : recu > 0 ? SHADCN.warning : SHADCN.mutedFg }}>
                    {fmtFull(recu)}
                    {recu < attendu && <span style={{ color:SHADCN.mutedFg }}> / {fmtFull(attendu)}</span>}
                  </span>
                  <Badge statut={statut} />
                </div>
              </div>
            );

            return (
              <>
                {/* ── En-tête harmonisé ────────────────────────────────────── */}
                <PageHeader
                  title={moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1)}
                  meta={`Loyers attendus ${fmtFull(totalAttendu)} · Échéance BNP le 11`}
                />

                {/* ── Alerte tréso négative ───────────────────────────────── */}
                {tresoSCI < 0 && (
                  <div style={{ ...TYPO.body, background:softTone(SHADCN.error).bg, border:`1px solid ${softTone(SHADCN.error).border}`, borderRadius: R.md, padding:`${S[3]}px 18px`, marginBottom: S[4], color:SHADCN.error }}>
                    ⚠️ <strong>Trésorerie SCI négative — {fmtFull(tresoSCI)}</strong> — Les loyers de {moisLabel} doivent rentrer avant le 11 pour couvrir l'échéance BNP.
                  </div>
                )}

                {/* ── 3 métriques clés ────────────────────────────────────── */}
                <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap:10, marginBottom:20 }}>
                  <MetricCard label="Trésorerie SCI"     value={tresoSCI}  color={tresoColor}  sub={sciSoldePeriode ? `BNP *6682 — import ${sciSoldePeriode}` : 'BNP *6682 — snapshot manuel'} />
                  <MetricCard label="Échéance BNP"        value={-pret}     color={SHADCN.error}     sub="Le 11 de chaque mois" />
                  <MetricCard label="Excédent mensuel" value={data.sci.excedentMensuel} color={data.sci.excedentMensuel >= 0 ? SHADCN.success : SHADCN.error} sub="Loyers encaissés − charges (moy. 3 mois BNP)" />
                </div>

                {/* ── Loyers du mois ──────────────────────────────────────── */}
                <Card>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: S[1] }}>
                    <SectionTitle style={{ marginBottom:0, paddingBottom:0, borderBottom:'none' }}>Loyers — {moisLabel}</SectionTitle>
                    <span style={{ ...TYPO.body, ...TYPO.num, fontWeight:700, color: totalRecu >= totalAttendu ? SHADCN.success : SHADCN.error }}>
                      {fmtFull(totalRecu)} / {fmtFull(totalAttendu)}
                    </span>
                  </div>
                  <div style={{ height:1, background:SHADCN.border, marginBottom: S[1] }} />
                  {moisCourant ? (
                    <>
                      <LoyerRow label="Bureau Sonore" {...moisCourant.bureauSonore} />
                      <LoyerRow label="Café Sonore"   {...moisCourant.cafeSonore} />
                    </>
                  ) : (
                    <div style={{ ...TYPO.body, padding:'20px 0', color:SHADCN.mutedFg, textAlign:'center' }}>Loyers du mois pas encore facturés (onglet Factures)</div>
                  )}
                  {/* Barre de progression */}
                  <div style={{ marginTop: S[4], height:6, background:SHADCN.muted, borderRadius: R.pill, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:'100%', transform:`scaleX(${Math.min(ratioEncaisse,1)})`, transformOrigin:'left', background: totalRecu>=totalAttendu ? SHADCN.success : SHADCN.warning, borderRadius: R.pill, transition:'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)' }} />
                  </div>
                  <div style={{ ...TYPO.caption, ...TYPO.num, display:'flex', justifyContent:'space-between', marginTop:6, color:SHADCN.mutedFg }}>
                    <span>{Math.round(ratioEncaisse*100)}% encaissé</span>
                    <span>Objectif {fmtFull(totalAttendu)}</span>
                  </div>
                </Card>

                {/* ── Retards à régulariser ───────────────────────────────── */}
                {totalRetard > 0 && S?.loyers && (
                  <Card>
                    <SectionTitle>Retards à régulariser</SectionTitle>
                    {S.loyers.map((m) => {
                      const items = [
                        m.bureauSonore.statut !== 'en_cours' && m.bureauSonore.attendu - m.bureauSonore.recu > 0
                          ? { label:`Bureau Sonore — ${m.label}`, manque: m.bureauSonore.attendu - m.bureauSonore.recu, statut: m.bureauSonore.statut }
                          : null,
                        m.cafeSonore.statut !== 'en_cours' && m.cafeSonore.attendu - m.cafeSonore.recu > 0
                          ? { label:`Café Sonore — ${m.label}`, manque: m.cafeSonore.attendu - m.cafeSonore.recu, statut: m.cafeSonore.statut }
                          : null,
                      ].filter(Boolean);
                      return items.map((item, i) => (
                        <div key={`${m.mois}-${i}`} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${SHADCN.border}` }}>
                          <span style={{ ...TYPO.body, color:SHADCN.fg }}>{item.label}</span>
                          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <span style={{ ...TYPO.body, ...TYPO.num, fontWeight:700, color:SHADCN.error }}>−{fmtFull(item.manque)}</span>
                            <Badge statut={item.statut} />
                          </div>
                        </div>
                      ));
                    })}
                    <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap: S[2], paddingTop: S[3], marginTop: S[1] }}>
                      <span style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>Total en attente</span>
                      <span style={{ ...TYPO.metric, fontSize: FS.lg, color:SHADCN.error }}>{fmtFull(totalRetard)}</span>
                    </div>
                  </Card>
                )}

                {/* ── Flux mensuel (compact) ───────────────────────────────── */}
                <Card>
                  <SectionTitle right={sciMoisLabel && <span style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>Moyenne BNP · {sciMoisLabel}</span>}>Flux mensuel moyen</SectionTitle>
                  {[
                    { label:'Bureau Sonore', v: data.sci.loyersParLocataire?.eurl ?? 0, c:SHADCN.success },
                    { label:'Café Sonore',   v: data.sci.loyersParLocataire?.cafe ?? 0, c:SHADCN.success },
                    { label:'Prêt BNP',      v:-pret,         c:SHADCN.error },
                    { label:'Énergie',       v:-data.sci.chargesEnergie, c:SHADCN.error },
                    ...(data.sci.autresCharges ? [{ label:'Autres charges', v:-data.sci.autresCharges, c:SHADCN.error }] : []),
                    { label:'Excédent',      v: data.sci.excedentMensuel, c: data.sci.excedentMensuel >= 0 ? SHADCN.success : SHADCN.error, bold:true },
                  ].map((r,i,arr) => (
                    <div key={i} style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom: i < arr.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                      <span style={{ color: r.bold ? SHADCN.fg : SHADCN.mutedFg, fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                      <span style={{ ...TYPO.num, color:r.c, fontWeight: r.bold ? 700 : 500 }}>{r.v > 0 ? '+' : ''}{fmtFull(r.v)}</span>
                    </div>
                  ))}
                </Card>
              </>
            );
          })()}
          </EntityAccentContext.Provider>
          )}

          {/* ══════════════════════ BUREAU — Toggle période (mois / année) ════ */}
          {(activeTab === 'bureau_global' || activeTab === 'bureau_depenses' || activeTab === 'facturation') && (
            <EntityAccentContext.Provider value={SHADCN.mutedFg}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap: S[2], marginBottom: S[3] }}>
                <span style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg }}>Période</span>
                <SegmentedControl
                  size="sm"
                  value={bureauPeriod}
                  onChange={setBureauPeriod}
                  tabs={[
                    { id: 'mois',  label: 'Ce mois' },
                    { id: 'annee', label: 'Cette année' },
                  ]}
                />
              </div>
            </EntityAccentContext.Provider>
          )}

                    {/* ══════════════════════ BUREAU — Global · Cette année ═════════ */}
          {activeTab === 'bureau_global' && bureauPeriod === 'annee' && (
            <EntityAccentContext.Provider value={SHADCN.mutedFg}>
            <>
              {/* ── En-tête harmonisé ────────────────────────────────────── */}
              <PageHeader
                title={`Bureau Sonore — ${now.getFullYear()}`}
                meta={`Objectif annuel ${fmtFull(bObjectifAnnuel)} · atteint à ${bCouvAnnuelPct}%${bManqueAnnuel > 0 ? ` · manque ${fmtFull(bManqueAnnuel)}` : ''}`}
              />

              {/* Hero — CA ANNUEL (vue principale pour Bureau, contrairement au Café) */}
              {!qontoSettled ? (
                <Card style={{ marginBottom:14 }}><Skeleton h={140} /></Card>
              ) : (() => {
                const ecartRythme = bCouvAnnuelPct - bRythmeAttendu;
                const objectifAtteint = bCouvAnnuelPct >= 100;
                const successColors = softTone(SHADCN.success);
                return (
                <Card style={{ marginBottom: 14, padding: '24px 28px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap: S[3], marginBottom: S[2] }}>
                    <div>
                      <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg }}>
                        CHIFFRE D'AFFAIRES {now.getFullYear()} — JANV → {bMoisEnCours.label?.toUpperCase()}
                      </div>
                      <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, opacity:0.7, marginTop: 2 }}>
                        {bCaSource === 'facture'
                          ? `CA comptable · ${bNbFacturesYTD} facture${bNbFacturesYTD > 1 ? 's' : ''} émise${bNbFacturesYTD > 1 ? 's' : ''} HT · hors brouillons et annulées`
                          : 'factures indisponibles — repli sur les encaissements TTC Qonto (CCA et virements internes inclus)'}
                      </div>
                    </div>
                    {objectifAtteint ? (
                      <span style={{
                        ...TYPO.tag,
                        background: successColors.bg,
                        border: `1px solid ${successColors.border}`,
                        color: SHADCN.success,
                        padding: '4px 10px',
                        borderRadius: R.pill,
                      }}>▲ Objectif atteint</span>
                    ) : (
                      <DeltaPill
                        direction={ecartRythme >= 0 ? 'up' : 'down'}
                        value={Math.abs(ecartRythme)}
                        suffix="vs rythme"
                        unit="pts"
                        size="sm"
                      />
                    )}
                  </div>
                  <div style={{ ...TYPO.display, color:SHADCN.fg, marginBottom: S[3] }}>
                    {fmtFull(bCaYTD)}
                    <span style={{ ...TYPO.h2, color:SHADCN.mutedFg, marginLeft: S[3], fontWeight:500 }}>{bCouvAnnuelPct}%</span>
                  </div>
                  {/* Barre de progression annuelle — neutre */}
                  <div style={{ height:6, background:SHADCN.muted, borderRadius: R.pill, marginBottom: S[3], overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${Math.min(100, bCouvAnnuelPct)}%`, background:SHADCN.fg, borderRadius: R.pill, opacity: 0.85 }} />
                  </div>
                  <div style={{ ...TYPO.body, ...TYPO.num, color:SHADCN.mutedFg }}>
                    Objectif <strong style={{ color:SHADCN.fg, fontWeight:500 }}>{fmtFull(bObjectifAnnuel)}</strong>
                    {' · '}rythme attendu <strong style={{ color:SHADCN.fg, fontWeight:500 }}>{bRythmeAttendu}%</strong>
                    {bManqueAnnuel > 0 && <> · reste <strong style={{ color:SHADCN.fg, fontWeight:500 }}>{fmtFull(bManqueAnnuel)}</strong></>}
                  </div>
                  {/* Comparatif n-1 / n-2 — inline, plus de card dédiée */}
                  {(bCa2024 || bCa2025) && (
                    <div style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.mutedFg, marginTop: S[4], paddingTop: S[3], borderTop:`1px solid ${SHADCN.border}`, display:'flex', gap: S[5], flexWrap:'wrap' }}>
                      {bCa2025 ? <span>2025 <span style={{ color:SHADCN.fg, fontWeight:500, marginLeft: S[2] }}>{fmtFull(bCa2025)}</span></span> : null}
                      {bCa2024 ? <span>2024 <span style={{ color:SHADCN.fg, fontWeight:500, marginLeft: S[2] }}>{fmtFull(bCa2024)}</span></span> : null}
                    </div>
                  )}
                </Card>
                );
              })()}

              {/* Deux stats légères — colonnes plates, pas de cards */}
              {!qontoSettled ? (
                <div style={{ marginBottom: S[5] }}><Skeleton h={60} /></div>
              ) : (
                <div style={{
                  display:'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? S[4] : S[7],
                  marginBottom: S[5],
                }}>
                  <div>
                    <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[2] }}>
                      Ce mois — {bMoisEnCours.label}
                    </div>
                    <div style={{ ...TYPO.subhead, ...TYPO.num, color: SHADCN.fg }}>{fmtFull(bCaMois)}</div>
                    <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1] }}>
                      {bCouverturePct}% du seuil mensuel ({fmtFull(bSeuilRenta)})
                    </div>
                  </div>
                  <div>
                    <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: S[2] }}>
                      CA moyen / mois — {bMoisEcoules} mois écoulés
                    </div>
                    <div style={{ ...TYPO.subhead, ...TYPO.num, color: SHADCN.fg }}>{fmtFull(bCaMoyenMois)}</div>
                    <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1] }}>
                      {bCaMoyenMois >= bSeuilRenta ? 'Au-dessus du seuil' : `Manque ${fmtFull(bSeuilRenta - bCaMoyenMois)} pour le seuil`}
                    </div>
                  </div>
                </div>
              )}

              {/* Charges FIXES + VARIABLES côte à côte — base du calcul de marge */}
              <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:14,marginBottom:14 }}>
                <Card>
                  <SectionTitle>Charges fixes — {fmtFull(bChargesTotal)}/mois</SectionTitle>
                  <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                    Engagées tous les mois indépendamment du CA
                  </div>
                  {(bCF.detail || []).map((r, i) => {
                    const pct = bChargesTotal ? Math.round(r.v / bChargesTotal * 100) : 0;
                    return (
                      <div key={i} style={{ padding:'7px 0',borderBottom:i < (bCF.detail||[]).length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                        <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', marginBottom: S[1] }}>
                          <span style={{ color:SHADCN.mutedFg }}>{r.label}</span>
                          <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500 }}>{fmtFull(r.v)}</span>
                        </div>
                        <div style={{ height:4,background:SHADCN.muted,borderRadius: R.pill }}>
                          <div style={{ height:'100%',width:`${pct}%`,background:SHADCN.mutedFg,borderRadius: R.pill,opacity:0.7 }} />
                        </div>
                      </div>
                    );
                  })}
                  <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0 0', marginTop: S[3], borderTop:`1px solid ${SHADCN.border}` }}>
                    <span style={{ color:SHADCN.mutedFg }}>+ URSSAF (cotisations sociales)</span>
                    <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500 }}>{fmtFull(bRemuTotal)}</span>
                  </div>
                  <div style={{ ...TYPO.caption, marginTop: S[2], color:SHADCN.mutedFg }}>
                    Total annuel fixe : <strong style={{ color:SHADCN.fg }}>{fmtFull(bChargesFixesAn)}</strong>
                  </div>
                </Card>

                <Card>
                  <SectionTitle>Top dépenses — {bMoisEnCours.label}</SectionTitle>
                  <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                    10 plus grosses sorties du mois · source Qonto · pas de catégorisation
                  </div>
                  {bTopDepensesMois.length === 0 ? (
                    <div style={{ ...TYPO.body, color:SHADCN.mutedFg, fontStyle:'italic', padding:`${S[4]}px 0` }}>
                      Aucune dépense remontée par Qonto pour ce mois.
                    </div>
                  ) : (
                    bTopDepensesMois.map((r, i) => {
                      const max = bTopDepensesMois[0].montant || 1;
                      const pct = Math.round(r.montant / max * 100);
                      return (
                        <div key={i} style={{ padding:'7px 0',borderBottom:i < bTopDepensesMois.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                          <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', marginBottom: S[1], gap: S[3] }}>
                            <span style={{ color:SHADCN.mutedFg, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.label || '—'}</span>
                            <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500, flexShrink:0 }}>{fmtFull(r.montant)}</span>
                          </div>
                          <div style={{ height:4,background:SHADCN.muted,borderRadius: R.pill }}>
                            <div style={{ height:'100%',width:`${pct}%`,background:SHADCN.fg,borderRadius: R.pill,opacity:0.5 }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div style={{ ...TYPO.caption, marginTop: S[3], color:SHADCN.mutedFg }}>
                    Total dépenses du mois : <strong style={{ color:SHADCN.fg }}>{fmtFull(bDepensesMois)}</strong> · YTD : <strong style={{ color:SHADCN.fg }}>{fmtFull(bDepensesYTD)}</strong>
                  </div>
                </Card>
              </div>

              {/* Marges — grand livre, plus de cards nestées */}
              <Card style={{ marginBottom: 14 }}>
                <SectionTitle>
                  Marges {now.getFullYear()} — {bFenetreDep ? `${bFenetreDep} (fenêtre dépenses Qonto)` : 'depuis le 1er janvier'}
                </SectionTitle>
                {(() => {
                  const tauxBrut = bCaFenetreDep > 0 ? Math.round((bMargeBrute / bCaFenetreDep) * 100) : 100;
                  const lignes = [
                    { label: "Chiffre d'affaires",                       v:  bCaFenetreDep,     sign: ' ', kind: 'item', sub: 'factures émises HT' },
                    { label: 'Charges variables',                        v: -bChargesVarReel,   sign: '−', kind: 'item', sub: `${bTauxVarReel}% du CA · dépenses Qonto − fixes récurrents (URSSAF incluse les mois où elle tombe)` },
                    { label: 'Marge brute',                              v:  bMargeBrute,       sign: '=', kind: 'subtotal', sub: `${tauxBrut}% du CA` },
                    { label: `Charges fixes (${bMoisEcoulesDep} mois)`,  v: -(bChargesFixProrata + Math.round(bRemuTotal * bMoisEcoulesDep)), sign: '−', kind: 'item', sub: `récurrents ${fmtFull(bChargesFixMensuel)}/mois + URSSAF ${fmtFull(bRemuTotal)}/mois théorique` },
                    { label: 'Marge nette',                              v:  bMargeNette,       sign: '=', kind: 'total' },
                  ];
                  return (
                    <div style={{ marginTop: S[2] }}>
                      {lignes.map((r, i) => {
                        const isTotal    = r.kind === 'total';
                        const isSubtotal = r.kind === 'subtotal';
                        const labelTypo  = isTotal ? TYPO.h2 : TYPO.body;
                        const amountTypo = isTotal ? { ...TYPO.metric, fontSize: 28 } : TYPO.amount;
                        const labelColor = isTotal ? SHADCN.fg : (isSubtotal ? SHADCN.fg : SHADCN.mutedFg);
                        const amountColor = isTotal
                          ? (r.v >= 0 ? SHADCN.success : SHADCN.error)
                          : SHADCN.fg;
                        const fontWeight = isTotal ? 600 : (isSubtotal ? 600 : 400);
                        return (
                          <div key={i} style={{
                            display:'flex', alignItems:'baseline', justifyContent:'space-between',
                            padding: isTotal ? `${S[4]}px 0 ${S[2]}px` : `${S[3]}px 0`,
                            borderTop: isTotal ? `2px solid ${SHADCN.fg}` : 'none',
                            borderBottom: !isTotal && i < lignes.length - 1 ? `1px solid ${SHADCN.border}` : 'none',
                          }}>
                            <div style={{ display:'flex', alignItems:'baseline', gap: S[3], minWidth: 0 }}>
                              <span style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.mutedFg, width: 12, textAlign:'center', flexShrink:0 }}>{r.sign}</span>
                              <div>
                                <div style={{ ...labelTypo, color: labelColor, fontWeight }}>{r.label}</div>
                                {r.sub && <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop: 2 }}>{r.sub}</div>}
                              </div>
                            </div>
                            <span style={{ ...amountTypo, ...TYPO.num, color: amountColor, fontWeight }}>{fmtFull(r.v)}</span>
                          </div>
                        );
                      })}
                      {bChargesVarReel === 0 && bDepensesYTD > 0 && (
                        <div style={{ ...TYPO.caption, marginTop: S[4], color:SHADCN.mutedFg, opacity:0.75 }}>
                          Dépenses Qonto ({fmtFull(bDepensesYTD)}) ≤ fixes récurrents ({fmtFull(bChargesFixProrata)}) sur {bMoisEcoulesDep} mois. Variables bornées à 0.
                        </div>
                      )}
                      {bDepensesYTD === 0 && (
                        <div style={{ ...TYPO.caption, marginTop: S[4], color:SHADCN.mutedFg, opacity:0.75 }}>
                          Aucune dépense Qonto remontée pour 2026.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Card>

              {/* CA historique 2026 */}
              <Card>
                <SectionTitle>CA {bAnnee} — {bCaSource === 'facture' ? 'facturé HT par mois' : 'réel par mois'}</SectionTitle>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={bCaHisto} margin={{ top:14,right:40,left:0,bottom:0 }}>
                    <defs>
                      {/* Dégradé "Midnight Mint" : couleur sémantique en haut → cyan (chart1) en bas */}
                      <linearGradient id="bar-grad-good" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={SHADCN.chart3} stopOpacity={1} />
                        <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.85} />
                      </linearGradient>
                      <linearGradient id="bar-grad-low" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={SHADCN.chart5} stopOpacity={1} />
                        <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.7} />
                      </linearGradient>
                      <linearGradient id="bar-grad-progress" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={SHADCN.chart4} stopOpacity={1} />
                        <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.6} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={SHADCN.border} />
                    <XAxis dataKey="label" tick={{ fill:SHADCN.mutedFg, fontSize:11, fontFamily: SHADCN.font }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmt} tick={{ fill:SHADCN.mutedFg, fontSize:11, fontFamily: SHADCN.font }} axisLine={false} tickLine={false} width={42} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={bSeuilRenta} stroke={SHADCN.error} strokeDasharray="4 3"
                      label={{ value:'Seuil', position:'right', fontSize:11, fill:SHADCN.error, fontFamily: SHADCN.font }} />
                    <Bar dataKey="ca" name="CA" radius={[4,4,0,0]}>
                      {bCaHisto.map((entry, idx) => (
                        <Cell key={idx} fill={`url(#${entry.enCours ? 'bar-grad-progress' : entry.ca >= bSeuilRenta ? 'bar-grad-good' : 'bar-grad-low'})`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ ...TYPO.eyebrow, display:'flex', gap:18, justifyContent:'center', marginTop: S[3], color:SHADCN.mutedFg }}>
                  <span><span style={{ color:SHADCN.chart3 }}>■</span> Au-dessus du seuil</span>
                  <span><span style={{ color:SHADCN.chart5 }}>■</span> En dessous</span>
                  <span><span style={{ color:SHADCN.chart4 }}>■</span> En cours</span>
                </div>
              </Card>
            </>
            </EntityAccentContext.Provider>
          )}

          {/* ══════════════════════ BUREAU — Global · Ce mois ═════════════════ */}
          {activeTab === 'bureau_global' && bureauPeriod === 'mois' && (() => {
            const margeBruteMois  = bCaMois - bChargesVarMois;
            const bChargesFixMois = bChargesFixMensuel;
            const margeNetteMois  = margeBruteMois - bChargesFixMois - bRemuTotal;
            const tauxVarMois     = bCaMois > 0 ? Math.round(bChargesVarMois / bCaMois * 100) : 0;
            const ecartMoyen = bCaMois - bCaMoyenMois;
            return (
              <>
                <PageHeader
                  title={`${bMoisEnCours.label || ''} ${now.getFullYear()}`}
                  meta={`Seuil mensuel ${fmtFull(bSeuilRenta)} · atteint à ${bCouverturePct}%${bManqueSeuil > 0 ? ` · manque ${fmtFull(bManqueSeuil)}` : ''}`}
                />

                {/* Hero — CA du mois */}
                {!qontoSettled ? (
                  <Card style={{ marginBottom: 14 }}><Skeleton h={140} /></Card>
                ) : (
                  <Card style={{ marginBottom: 14, padding: '24px 28px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap: S[3], marginBottom: S[2] }}>
                      <div>
                        <div style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg }}>
                          CHIFFRE D'AFFAIRES — {(bMoisEnCours.label || '').toUpperCase()} {now.getFullYear()}
                        </div>
                        <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, opacity:0.7, marginTop: 2 }}>
                          {bCaSource === 'facture'
                            ? `CA comptable · ${bMoisEnCours.nbFactures || 0} facture${(bMoisEnCours.nbFactures || 0) > 1 ? 's' : ''} émise${(bMoisEnCours.nbFactures || 0) > 1 ? 's' : ''} HT`
                            : 'factures indisponibles — repli sur les encaissements TTC Qonto'}
                        </div>
                      </div>
                      {bCaMoyenMois > 0 && (
                        <DeltaPill
                          direction={ecartMoyen >= 0 ? 'up' : 'down'}
                          value={Math.abs(Math.round(ecartMoyen / Math.max(1, bCaMoyenMois) * 100))}
                          suffix="vs moyenne"
                          size="sm"
                        />
                      )}
                    </div>
                    <div style={{ ...TYPO.display, color:SHADCN.fg, marginBottom: S[3] }}>
                      {fmtFull(bCaMois)}
                      <span style={{ ...TYPO.h2, color:SHADCN.mutedFg, marginLeft: S[3], fontWeight: 500 }}>{bCouverturePct}%</span>
                    </div>
                    <div style={{ height: 6, background: SHADCN.muted, borderRadius: R.pill, marginBottom: S[3], overflow:'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.min(100, bCouverturePct)}%`, background: SHADCN.fg, borderRadius: R.pill, opacity: 0.85 }} />
                    </div>
                    <div style={{ ...TYPO.body, ...TYPO.num, color: SHADCN.mutedFg }}>
                      Seuil <strong style={{ color:SHADCN.fg, fontWeight: 500 }}>{fmtFull(bSeuilRenta)}</strong>
                      {' · '}moyenne YTD <strong style={{ color:SHADCN.fg, fontWeight: 500 }}>{fmtFull(bCaMoyenMois)}</strong>
                      {bManqueSeuil > 0 && <> · reste <strong style={{ color:SHADCN.fg, fontWeight: 500 }}>{fmtFull(bManqueSeuil)}</strong></>}
                    </div>
                  </Card>
                )}

                {/* Marges du mois — grand livre */}
                <Card style={{ marginBottom: 14 }}>
                  <SectionTitle>Marges {bMoisEnCours.label || ''} {now.getFullYear()}</SectionTitle>
                  {(() => {
                    const lignes = [
                      { label: "Chiffre d'affaires",                  v:  bCaMois,           sign: ' ', kind: 'item' },
                      { label: 'Charges variables',                   v: -bChargesVarMois,   sign: '−', kind: 'item', sub: bChargesVarMois > 0 ? `${tauxVarMois}% du CA · dépenses Qonto − fixes récurrents (URSSAF incluse si elle tombe)` : `dépenses Qonto ${fmtFull(bDepensesMois)} ≤ fixes récurrents ${fmtFull(bChargesFixMois)}` },
                      { label: 'Marge brute',                         v:  margeBruteMois,    sign: '=', kind: 'subtotal', sub: `${100 - tauxVarMois}% du CA` },
                      { label: 'Charges fixes du mois',               v: -(bChargesFixMois + bRemuTotal), sign: '−', kind: 'item', sub: `récurrents ${fmtFull(bChargesFixMois)} + URSSAF ${fmtFull(bRemuTotal)}/mois théorique` },
                      { label: 'Marge nette',                         v:  margeNetteMois,    sign: '=', kind: 'total' },
                    ];
                    return (
                      <div style={{ marginTop: S[2] }}>
                        {lignes.map((r, i) => {
                          const isTotal    = r.kind === 'total';
                          const isSubtotal = r.kind === 'subtotal';
                          const labelTypo  = isTotal ? TYPO.h2 : TYPO.body;
                          const amountTypo = isTotal ? { ...TYPO.metric, fontSize: 28 } : TYPO.amount;
                          const labelColor = isTotal ? SHADCN.fg : (isSubtotal ? SHADCN.fg : SHADCN.mutedFg);
                          const amountColor = isTotal ? (r.v >= 0 ? SHADCN.success : SHADCN.error) : SHADCN.fg;
                          const fontWeight = isTotal ? 600 : (isSubtotal ? 600 : 400);
                          return (
                            <div key={i} style={{
                              display:'flex', alignItems:'baseline', justifyContent:'space-between',
                              padding: isTotal ? `${S[4]}px 0 ${S[2]}px` : `${S[3]}px 0`,
                              borderTop: isTotal ? `2px solid ${SHADCN.fg}` : 'none',
                              borderBottom: !isTotal && i < lignes.length - 1 ? `1px solid ${SHADCN.border}` : 'none',
                            }}>
                              <div style={{ display:'flex', alignItems:'baseline', gap: S[3], minWidth: 0 }}>
                                <span style={{ ...TYPO.caption, ...TYPO.num, color: SHADCN.mutedFg, width: 12, textAlign: 'center', flexShrink: 0 }}>{r.sign}</span>
                                <div>
                                  <div style={{ ...labelTypo, color: labelColor, fontWeight }}>{r.label}</div>
                                  {r.sub && <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: 2 }}>{r.sub}</div>}
                                </div>
                              </div>
                              <span style={{ ...amountTypo, ...TYPO.num, color: amountColor, fontWeight }}>{fmtFull(r.v)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </Card>

                {/* Charges FIXES + top dépenses du mois — même squelette que la vue annuelle */}
                <div style={{ display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:14,marginBottom:14 }}>
                  <Card>
                    <SectionTitle>Charges fixes — {fmtFull(bChargesTotal)}/mois</SectionTitle>
                    <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                      Engagées tous les mois indépendamment du CA
                    </div>
                    {(bCF.detail || []).map((r, i) => {
                      const pct = bChargesTotal ? Math.round(r.v / bChargesTotal * 100) : 0;
                      return (
                        <div key={i} style={{ padding:'7px 0',borderBottom:i < (bCF.detail||[]).length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                          <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', marginBottom: S[1] }}>
                            <span style={{ color:SHADCN.mutedFg }}>{r.label}</span>
                            <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500 }}>{fmtFull(r.v)}</span>
                          </div>
                          <div style={{ height:4,background:SHADCN.muted,borderRadius: R.pill }}>
                            <div style={{ height:'100%',width:`${pct}%`,background:SHADCN.mutedFg,borderRadius: R.pill,opacity:0.7 }} />
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0 0', marginTop: S[3], borderTop:`1px solid ${SHADCN.border}` }}>
                      <span style={{ color:SHADCN.mutedFg }}>+ URSSAF (cotisations sociales)</span>
                      <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500 }}>{fmtFull(bRemuTotal)}</span>
                    </div>
                  </Card>

                  <Card>
                    <SectionTitle>Top dépenses — {bMoisEnCours.label}</SectionTitle>
                    <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginBottom: S[3] }}>
                      10 plus grosses sorties du mois · source Qonto · pas de catégorisation
                    </div>
                    {bTopDepensesMois.length === 0 ? (
                      <div style={{ ...TYPO.body, color:SHADCN.mutedFg, fontStyle:'italic', padding:`${S[4]}px 0` }}>
                        Aucune dépense remontée par Qonto pour ce mois.
                      </div>
                    ) : (
                      bTopDepensesMois.map((r, i) => {
                        const max = bTopDepensesMois[0].montant || 1;
                        const pct = Math.round(r.montant / max * 100);
                        return (
                          <div key={i} style={{ padding:'7px 0',borderBottom:i < bTopDepensesMois.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                            <div style={{ ...TYPO.body, display:'flex', justifyContent:'space-between', marginBottom: S[1], gap: S[3] }}>
                              <span style={{ color:SHADCN.mutedFg, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.label || '—'}</span>
                              <span style={{ ...TYPO.num, color:SHADCN.fg, fontWeight:500, flexShrink:0 }}>{fmtFull(r.montant)}</span>
                            </div>
                            <div style={{ height:4,background:SHADCN.muted,borderRadius: R.pill }}>
                              <div style={{ height:'100%',width:`${pct}%`,background:SHADCN.fg,borderRadius: R.pill,opacity:0.5 }} />
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div style={{ ...TYPO.caption, marginTop: S[3], color:SHADCN.mutedFg }}>
                      Total dépenses du mois : <strong style={{ color:SHADCN.fg }}>{fmtFull(bDepensesMois)}</strong> · YTD : <strong style={{ color:SHADCN.fg }}>{fmtFull(bDepensesYTD)}</strong>
                    </div>
                  </Card>
                </div>

                {/* CA historique 2026 — contexte du mois dans l'année */}
                <Card>
                  <SectionTitle>CA {bAnnee} — {bCaSource === 'facture' ? 'facturé HT par mois' : 'réel par mois'}</SectionTitle>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={bCaHisto} margin={{ top:14,right:40,left:0,bottom:0 }}>
                      <defs>
                        <linearGradient id="bar-grad-good-m" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor={SHADCN.chart3} stopOpacity={1} />
                          <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.85} />
                        </linearGradient>
                        <linearGradient id="bar-grad-low-m" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor={SHADCN.chart5} stopOpacity={1} />
                          <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.7} />
                        </linearGradient>
                        <linearGradient id="bar-grad-progress-m" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor={SHADCN.chart4} stopOpacity={1} />
                          <stop offset="100%" stopColor={SHADCN.chart1} stopOpacity={0.6} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={SHADCN.border} />
                      <XAxis dataKey="label" tick={{ fill:SHADCN.mutedFg, fontSize:11, fontFamily: SHADCN.font }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmt} tick={{ fill:SHADCN.mutedFg, fontSize:11, fontFamily: SHADCN.font }} axisLine={false} tickLine={false} width={42} />
                      <Tooltip content={<CustomTooltip />} />
                      <ReferenceLine y={bSeuilRenta} stroke={SHADCN.error} strokeDasharray="4 3"
                        label={{ value:'Seuil', position:'right', fontSize:11, fill:SHADCN.error, fontFamily: SHADCN.font }} />
                      <Bar dataKey="ca" name="CA" radius={[4,4,0,0]}>
                        {bCaHisto.map((entry, idx) => (
                          <Cell key={idx} fill={`url(#${entry.enCours ? 'bar-grad-progress-m' : entry.ca >= bSeuilRenta ? 'bar-grad-good-m' : 'bar-grad-low-m'})`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </>
            );
          })()}

          {/* ══════════════════════ BUREAU — Dépenses (mois / année via toggle) ══ */}
          {activeTab === 'bureau_depenses' && (() => {
            const isMois = bureauPeriod === 'mois';
            // Nombre de mois réellement couverts par les dépenses Qonto (6 glissants),
            // pas le nombre de mois de CA — sinon les fixes seraient prorata-isés trop haut.
            const monthsCount = isMois ? 1 : Math.max(1, bMoisDep.length);

            // Tableau FIXES = liste déclarée bCF.detail × N mois (source de vérité = data.json)
            const fixesArr = (bCF.detail || [])
              .map(f => ({
                label: f.label,
                montant: Math.round((f.v || 0) * monthsCount),
                sub: isMois ? null : `${fmtFull(f.v)}/mois × ${monthsCount}`,
              }))
              .sort((a, b) => b.montant - a.montant);

            // Helpers : matche un libellé contre une liste de keywords (case-insensitive substring)
            const matchesAny = (label, keywords) => {
              if (!label || !keywords?.length) return false;
              const up = label.toUpperCase();
              return keywords.some(kw => up.includes(kw.toUpperCase()));
            };
            const isFixe = (label) =>
              (bCF.detail || []).some(f => matchesAny(label, f.keywords));
            const fluxInternesKws = bPilotage.fluxInternes?.keywords || ['BNP - BUREAU SONORE'];
            const isFluxInterne = (label) => matchesAny(label, fluxInternesKws);

            // Tableau VARIABLES = Qonto top dépenses, filtré (exclut fixes ET flux internes)
            // FLUX INTERNES = virements entre comptes (BNP ↔ Qonto), pas une vraie dépense.
            // L'API les exclut déjà des agrégats (champ fluxInternes) — le filtre par
            // keyword reste en fallback si la réponse API est antérieure à l'exclusion.
            let variablesArr;
            let fluxInternesTotal = isMois
              ? (bMoisEnCours.fluxInternes || 0)
              : bCaHisto.reduce((s, m) => s + (m.fluxInternes || 0), 0);
            if (isMois) {
              const vars = [];
              for (const t of (bTopDepensesMois || [])) {
                if (isFluxInterne(t.label))      fluxInternesTotal += t.montant;
                else if (!isFixe(t.label))       vars.push({ label: t.label, montant: t.montant });
              }
              variablesArr = vars.sort((a, b) => b.montant - a.montant);
            } else {
              const varByLabel = {};
              for (const m of bCaHisto) {
                for (const t of (m.topDepenses || [])) {
                  if (isFluxInterne(t.label))    fluxInternesTotal += t.montant;
                  else if (!isFixe(t.label))     varByLabel[t.label] = (varByLabel[t.label] || 0) + (t.montant || 0);
                }
              }
              variablesArr = Object.entries(varByLabel)
                .map(([label, montant]) => ({ label, montant: Math.round(montant) }))
                .sort((a, b) => b.montant - a.montant);
            }
            fluxInternesTotal = Math.round(fluxInternesTotal);

            const sumFixes = fixesArr.reduce((s, r) => s + r.montant, 0);
            const sumVariables = variablesArr.reduce((s, r) => s + r.montant, 0);
            const totalDepensesPeriode = isMois ? bDepensesMois : bDepensesYTD;

            const renderTable = (title, rows, total, sub) => (
              <Card>
                <SectionTitle>{title} — {fmtFull(total)}</SectionTitle>
                <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginBottom: S[3] }}>{sub}</div>
                {rows.length === 0 ? (
                  <div style={{ ...TYPO.body, color: SHADCN.mutedFg, fontStyle: 'italic', padding: `${S[4]}px 0` }}>
                    Aucune ligne sur cette période.
                  </div>
                ) : (
                  rows.map((r, i) => {
                    const pct = total ? Math.round(r.montant / total * 100) : 0;
                    return (
                      <div key={i} style={{ padding: '7px 0', borderBottom: i < rows.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                        <div style={{ ...TYPO.body, display: 'flex', justifyContent: 'space-between', marginBottom: S[1], gap: S[3] }}>
                          <span style={{ color: SHADCN.mutedFg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                            {r.label || '—'}
                          </span>
                          <span style={{ ...TYPO.num, color: SHADCN.fg, fontWeight: 500, flexShrink: 0 }}>{fmtFull(r.montant)}</span>
                        </div>
                        {r.sub && (
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, opacity: 0.7, marginBottom: S[1] }}>
                            {r.sub}
                          </div>
                        )}
                        <div style={{ height: 4, background: SHADCN.muted, borderRadius: R.pill }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: SHADCN.fg, borderRadius: R.pill, opacity: 0.5 }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </Card>
            );

            return (
              <>
                <PageHeader
                  title={isMois ? `Dépenses — ${bMoisEnCours.label || ''} ${now.getFullYear()}` : `Dépenses ${now.getFullYear()}`}
                  meta={`Fixes : engagements déclarés · Variables : Qonto ${isMois ? 'mois en cours' : `cumul YTD sur ${monthsCount} mois`}`}
                />

                {/* Récap total */}
                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', padding:`${S[4]}px 0`, borderBottom:`1px solid ${SHADCN.border}`, marginBottom: S[5], gap: S[4] }}>
                  <div>
                    <div style={{ ...TYPO.eyebrow, color: SHADCN.mutedFg, marginBottom: 2 }}>Total dépenses {isMois ? 'du mois' : 'YTD'} (Qonto)</div>
                    <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
                      Fixes théoriques {fmtFull(sumFixes)} · variables réelles {fmtFull(sumVariables)}
                      {fluxInternesTotal > 0 && <> · virements internes exclus {fmtFull(fluxInternesTotal)}</>}
                    </div>
                  </div>
                  <div style={{ ...TYPO.metric, ...TYPO.num, color: SHADCN.fg, fontSize: 32 }}>
                    {fmtFull(totalDepensesPeriode)}
                  </div>
                </div>

                {/* Deux tableaux côte à côte */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  {renderTable(
                    'Charges fixes',
                    fixesArr,
                    sumFixes,
                    isMois ? `Engagements déclarés (data.json) · ${fmtFull(bChargesTotal)} / mois` : `Engagements déclarés × ${monthsCount} mois`,
                  )}
                  {renderTable(
                    'Charges variables',
                    variablesArr,
                    sumVariables,
                    isMois
                      ? `Qonto · top 10 du mois · ${variablesArr.length} libellés non fixes`
                      : `Qonto · cumul YTD · ${variablesArr.length} libellés distincts (top 10/mois)`,
                  )}
                </div>

                {!isMois && (
                  <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[3], opacity: 0.75 }}>
                    Variables limitées au top 10 Qonto par mois. Pour une vue exhaustive, étendre /api/qonto-treso pour retourner toutes les transactions.
                  </div>
                )}
              </>
            );
          })()}

          {/* ═══════════════════ PLANNING / FACTURES / IMPORT / DSN ═══════════════
              Onglets chargés à la demande (React.lazy). Un seul Suspense partagé
              pour éviter un nouveau fallback à chaque switch d'onglet déjà chargé. */}
          {(activeTab === 'planning' || activeTab === 'todos' || activeTab === 'focus' || activeTab === 'perso_projets' || activeTab === 'factures' || activeTab === 'facturation' || activeTab === 'facturation_cafe' || activeTab === 'import' || activeTab === 'import_bureau' || activeTab === 'import_sci' || activeTab === 'dab' || activeTab === 'sci_invest' || activeTab === 'sci_factures' || activeTab === 'bureau_creances' || activeTab === 'vinyles' || activeTab === 'reservations_vinyles' || activeTab === 'magazines' || activeTab === 'tickets' || activeTab === 'affiches' || activeTab === 'story_open' || activeTab === 'story_programme' || activeTab === 'etiquettes' || activeTab === 'etiquettes_prix' || activeTab === 'wishlist' || activeTab === 'fiches_paie' || activeTab === 'rapports' || activeTab === 'monitoring_ventes' || activeTab === 'monitoring_evolution' || activeTab === 'monitoring_boissons' || activeTab === 'monitoring_casse' || activeTab === 'monitoring_verdicts' || activeTab === 'monitoring_traca' || activeTab === 'monitoring_meteo' || activeTab === 'ressources' || activeTab === 'prospection' || activeTab === 'prospection_bureau' || activeTab === 'dispos' || activeTab === 'voix_off' || activeTab === 'emploi_du_temps' || activeTab === 'fermeture') && (
            <Suspense fallback={<div style={{ padding: S[5] }}><Skeleton h={120} /></div>}>
              {activeTab === 'planning'  && <EntityAccentContext.Provider value={SHADCN.mutedFg}><EquipeTab role={role} peutEditer={peut('emploi_du_temps')} data={squareTeam} loading={squareTeamLoading} events={events} onRefetch={refetchSquareTeam} onRefetchEvents={refetchEvents} /></EntityAccentContext.Provider>}
              {activeTab === 'focus'     && session?.perso && <FocusTab session={session} membres={squareTeam?.membres ?? []} events={events} eventsLoading={eventsLoading} onNavigate={setActiveTab} />}
              {activeTab === 'perso_projets' && session?.perso && <PersoProjetsTab />}
              {activeTab === 'todos'     && <EntityAccentContext.Provider value={SHADCN.mutedFg}><TodosTab membres={squareTeam?.membres ?? []} /></EntityAccentContext.Provider>}
              {activeTab === 'vinyles'   && <EntityAccentContext.Provider value={SHADCN.mutedFg}><VinylesTab /></EntityAccentContext.Provider>}
              {activeTab === 'magazines' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><MagazinesTab /></EntityAccentContext.Provider>}
              {activeTab === 'reservations_vinyles' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><ReservationsVinylesTab /></EntityAccentContext.Provider>}
              {activeTab === 'affiches'  && <EntityAccentContext.Provider value={SHADCN.mutedFg}><AffichesTab /></EntityAccentContext.Provider>}
              {activeTab === 'story_open' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><StoryOpenTab /></EntityAccentContext.Provider>}
              {activeTab === 'story_programme' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><StoryProgrammeTab /></EntityAccentContext.Provider>}
              {activeTab === 'etiquettes' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><EtiquettesTab /></EntityAccentContext.Provider>}
              {activeTab === 'etiquettes_prix' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><EtiquettesPrixTab /></EntityAccentContext.Provider>}
              {activeTab === 'factures'  && <FacturesTab role={role} />}
              {activeTab === 'facturation_cafe' && peut('facturation_clients') && <FacturesStripeTab />}
              {activeTab === 'facturation' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><FacturationTab scope={bureauPeriod === 'mois' ? 'month' : 'year'} period={selectedPeriode} /></EntityAccentContext.Provider>}
              {activeTab === 'tickets'   && <TicketsTab role={role} />}
              {activeTab === 'ressources' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><RessourcesTab role={role} /></EntityAccentContext.Provider>}
              {activeTab === 'prospection' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><ProspectionTab prenom={session?.prenom} /></EntityAccentContext.Provider>}
              {activeTab === 'prospection_bureau' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><ProspectionBureauTab role={role} session={session} /></EntityAccentContext.Provider>}
              {activeTab === 'wishlist'  && <WishlistTab role={role} prenom={session?.prenom} />}
              {activeTab === 'import'        && <ImportTab only="cafe" uploads={uploads} onSave={handleSave} onClear={handleClear} />}
              {activeTab === 'import_bureau' && <ImportTab only="bureau" uploads={uploads} onSave={handleSave} onClear={handleClear} />}
              {activeTab === 'import_sci'    && <ImportTab only="sci" uploads={uploads} onSave={handleSave} onClear={handleClear} />}
              {activeTab === 'dab'       && <EntityAccentContext.Provider value={SHADCN.mutedFg}><DabTab /></EntityAccentContext.Provider>}
              {activeTab === 'sci_invest' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><SciInvestTab sciFinances={notionFinances?.sci} /></EntityAccentContext.Provider>}
              {activeTab === 'sci_factures' && peut('sci') && <EntityAccentContext.Provider value={SHADCN.mutedFg}><SciFacturesTab /></EntityAccentContext.Provider>}
              {activeTab === 'bureau_creances' && <EntityAccentContext.Provider value={SHADCN.mutedFg}><BureauCreancesTab /></EntityAccentContext.Provider>}
              {activeTab === 'fiches_paie' && peut('fiches_paie') && <FichesPaieTab />}
              {activeTab === 'dispos' && peut('dispos') && <EntityAccentContext.Provider value={SHADCN.mutedFg}><DisposTab /></EntityAccentContext.Provider>}
              {activeTab === 'emploi_du_temps' && peut('emploi_du_temps') && <EntityAccentContext.Provider value={SHADCN.mutedFg}><EmploiDuTempsTab /></EntityAccentContext.Provider>}
              {activeTab === 'fermeture' && peut('fermeture') && <EntityAccentContext.Provider value={SHADCN.mutedFg}><FermetureTab /></EntityAccentContext.Provider>}
              {activeTab === 'voix_off' && peut('voix_off') && <EntityAccentContext.Provider value={SHADCN.mutedFg}><VoixOffTab /></EntityAccentContext.Provider>}
              {activeTab === 'rapports'   && <RapportsTab zCaisseHistory={zCaisseHistory} zCaisseLoading={zCaisseLoading} zCaisseError={zCaisseError} />}
              {(activeTab === 'monitoring_ventes' || activeTab === 'monitoring_evolution' || activeTab === 'monitoring_boissons' || activeTab === 'monitoring_casse' || activeTab === 'monitoring_verdicts' || activeTab === 'monitoring_traca' || activeTab === 'monitoring_meteo') && (
                <MonitoringTab report={activeTab.replace('monitoring_', '')} zCaisseHistory={zCaisseHistory} prenom={session?.prenom} />
              )}
            </Suspense>
          )}

          {/* ══════════════════════════ DSN ═══════════════════════════════════ */}
          {activeTab === 'dsn' && (() => {
            const dsn = (notionFinances?.dsnHistory || []).slice().sort((a, b) => b.periode.localeCompare(a.periode));
            const fmtPeriode = p => {
              const [y, m] = p.split('-');
              const MOIS = ['jan.','fév.','mar.','avr.','mai','juin','juil.','août','sep.','oct.','nov.','déc.'];
              return `${MOIS[parseInt(m,10)-1]} ${y}`;
            };
            const totalCS = r => (r.urssaf||0) + (r.klesia||0) + (r.alan||0) + (r.pas||0);
            const F = (v, d=0) => new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',maximumFractionDigits:d,minimumFractionDigits:d}).format(v);
            return (
              <>
                <PageHeader title="DSN" />
                <Card>
                  {!dsn.length ? (
                    <div style={{ ...TYPO.body, color: SHADCN.mutedFg, textAlign: 'center', padding: `${S[6]}px ${S[4]}px` }}>
                      <FileText size={28} strokeWidth={1.5} style={{ color: SHADCN.mutedFg, marginBottom: S[3] }} />
                      <div style={{ ...TYPO.h2, color: SHADCN.fg, marginBottom: S[2] }}>Aucune DSN</div>
                      <div>Récapitulatif des DSN mensuelles (URSSAF, Klesia, Alan, PAS, masse salariale) — alimenté par <code style={{ background: SHADCN.muted, padding: '2px 6px', borderRadius: 3, fontFamily: 'monospace', fontSize: FS.xs }}>cafe_dsn_history</code>.</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                          <thead>
                            <tr style={{ borderBottom:`2px solid ${SHADCN.border}` }}>
                              {['Période','Masse sal.','URSSAF','Klesia','Alan','PAS','Total CS'].map((h, i) => (
                                <th key={h} style={{ ...TYPO.eyebrow, color:SHADCN.mutedFg, textAlign: i===0 ? 'left' : 'right', padding:'4px 8px 8px', whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dsn.map((row, i) => {
                              const prev = dsn[i + 1];
                              const cs = totalCS(row);
                              const prevCs = prev ? totalCS(prev) : null;
                              const delta = prevCs !== null ? cs - prevCs : null;
                              const deltaColor = delta === null ? SHADCN.mutedFg : delta > 50 ? SHADCN.error : delta < -50 ? SHADCN.success : SHADCN.mutedFg;
                              const cell = (val, prevVal) => {
                                if (val == null) return <td style={{ ...TYPO.body, ...TYPO.num, color:SHADCN.mutedFg, textAlign:'right', padding:'9px 8px' }}>—</td>;
                                const d = prevVal != null ? val - prevVal : null;
                                const dc = d === null ? null : d > 30 ? SHADCN.error : d < -30 ? SHADCN.success : null;
                                return (
                                  <td style={{ ...TYPO.body, ...TYPO.num, color:SHADCN.fg, textAlign:'right', padding:'9px 8px', whiteSpace:'nowrap' }}>
                                    {F(val)}
                                    {dc && <span style={{ ...TYPO.caption, color:dc, marginLeft:5 }}>{d > 0 ? '+' : ''}{F(d)}</span>}
                                  </td>
                                );
                              };
                              return (
                                <tr key={row.periode} style={{ borderBottom:`1px solid ${SHADCN.border}` }}>
                                  <td style={{ ...TYPO.body, color:SHADCN.fg, padding:'9px 8px', whiteSpace:'nowrap' }}>{fmtPeriode(row.periode)}</td>
                                  {cell(row.masse_salariale, prev?.masse_salariale)}
                                  {cell(row.urssaf, prev?.urssaf)}
                                  {cell(row.klesia, prev?.klesia)}
                                  {cell(row.alan, prev?.alan)}
                                  {cell(row.pas, prev?.pas)}
                                  <td style={{ ...TYPO.body, ...TYPO.num, color: delta !== null && delta > 50 ? SHADCN.error : SHADCN.fg, fontWeight:600, textAlign:'right', padding:'9px 8px', whiteSpace:'nowrap' }}>
                                    {F(cs)}
                                    {delta !== null && Math.abs(delta) > 10 && (
                                      <span style={{ ...TYPO.caption, color:deltaColor, marginLeft:5 }}>{delta > 0 ? '+' : ''}{F(delta)}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop: S[3] }}>Données extraites automatiquement des DSN. CS = URSSAF + Klesia + Alan + PAS.</div>
                    </>
                  )}
                </Card>
              </>
            );
          })()}

          {/* ══════════════════════ RECORDS ══════════════════════ */}
          {activeTab === 'records' && (() => {
            const rows = [...(zCaisseHistory || [])]
              .filter(r => {
                const hasEv = !!(evMap[r.date]?.length);
                if (recordFilter === 'event')    return hasEv;
                if (recordFilter === 'no-event') return !hasEv;
                return true;
              })
              .sort((a, b) => Number(b.total_ttc) - Number(a.total_ttc))
              .slice(0, 10);
            const F = v => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v ?? 0);
            const fmtDate = d => {
              const [y, m, j] = d.split('-');
              const MOIS = ['jan.','fév.','mar.','avr.','mai','juin','juil.','août','sep.','oct.','nov.','déc.'];
              return `${parseInt(j, 10)} ${MOIS[parseInt(m, 10) - 1]} ${y}`;
            };
            const top3 = rows.slice(0, 3);
            const rest = rows.slice(3);
            // Ordre visuel du podium : 2e, 1er, 3e
            const podium = [top3[1], top3[0], top3[2]].filter(Boolean);
            const GRID_COLS = '40px 1fr minmax(7ch, max-content)';
            return (
              <>
                <PageHeader eyebrow="Café Sonore" title="Meilleures journées" />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: S[4] }}>
                  {[['all', 'Tous'], ['event', 'Avec événement'], ['no-event', 'Sans événement']].map(([v, l]) => (
                    <Button key={v} variant="pill" active={recordFilter === v} onClick={() => setRecordFilter(v)}>
                      {l}
                    </Button>
                  ))}
                </div>
                {zCaisseLoading ? <Skeleton h={240} /> :
                 !rows.length ? (
                   <div style={{ ...TYPO.body, color: SHADCN.mutedFg, padding: `${S[5]}px 0` }}>
                     Aucune donnée disponible.
                   </div>
                 ) : (
                   <>
                     {/* Podium top 3 — #2 · #1 · #3 — trois rangs partageant la même grille de colonnes,
                         pour que les BAS des barres s'alignent (vrai podium) et non les bas des colonnes. */}
                     {(() => {
                       const COLS = podium.length === 3 ? '1fr 1.5fr 1fr' : `repeat(${podium.length}, 1fr)`;
                       const mint = softTone(SHADCN.success);
                       const rankOf = (row) => row === top3[0] ? 1 : row === top3[1] ? 2 : 3;
                       const barHeightOf = (r) => r === 1 ? 200 : r === 2 ? 120 : 76;
                       const amountSizeOf = (r) => r === 1
                         ? 'clamp(28px, 6.5vw, 52px)'
                         : r === 2
                           ? 'clamp(20px, 4vw, 32px)'
                           : 'clamp(15px, 3vw, 22px)';
                       const trackingOf = (r) => r === 1 ? '-0.035em' : '-0.025em';
                       const gapPct = top3[1] && Number(top3[1].total_ttc) > 0
                         ? ((Number(top3[0].total_ttc) - Number(top3[1].total_ttc)) / Number(top3[1].total_ttc)) * 100
                         : null;
                       return (
                         <div style={{ padding: `${S[7]}px 0 ${S[7]}px` }}>
                           {/* Motion d'entrée — une seule séquence, ease-out-quart, ≤700ms total.
                               Désactivée par prefers-reduced-motion. */}
                           <style>{`
                             @keyframes recPodiumRise {
                               from { clip-path: inset(85% 0 0 0); opacity: 0; }
                               to   { clip-path: inset(0 0 0 0); opacity: 1; }
                             }
                             @keyframes recPodiumFade {
                               from { opacity: 0; transform: translateY(4px); }
                               to   { opacity: 1; transform: translateY(0); }
                             }
                             [data-rec-bar="1"] { animation: recPodiumRise 340ms cubic-bezier(0.22, 1, 0.36, 1) 0ms both; }
                             [data-rec-bar="2"] { animation: recPodiumRise 280ms cubic-bezier(0.22, 1, 0.36, 1) 40ms both; }
                             [data-rec-bar="3"] { animation: recPodiumRise 280ms cubic-bezier(0.22, 1, 0.36, 1) 70ms both; }
                             [data-rec-meta] {
                               animation: recPodiumFade 220ms cubic-bezier(0.23, 1, 0.32, 1) 160ms both;
                             }
                             [data-rec-pill] {
                               animation: recPodiumFade 220ms cubic-bezier(0.23, 1, 0.32, 1) 220ms both;
                             }
                             @media (prefers-reduced-motion: reduce) {
                               [data-rec-bar], [data-rec-meta], [data-rec-pill] {
                                 animation: none !important;
                                 clip-path: none !important;
                                 opacity: 1 !important;
                                 transform: none !important;
                               }
                             }
                           `}</style>
                           {/* Rang 1 — pill mint fusionnée RECORD ABSOLU · ▲X% (seulement #1) */}
                           <div style={{
                             display: 'grid',
                             gridTemplateColumns: COLS,
                             columnGap: S[4],
                             marginBottom: S[3],
                           }}>
                             {podium.map((row) => {
                               const rank = rankOf(row);
                               return (
                                 <div key={`pill-${row.date}`} style={{ display: 'flex', justifyContent: 'center', minHeight: 22 }}>
                                   {rank === 1 && (
                                     <span data-rec-pill style={{
                                       display: 'inline-flex',
                                       alignItems: 'center',
                                       gap: 6,
                                       background: mint.bg,
                                       border: `1px solid ${mint.border}`,
                                       color: mint.text,
                                       padding: '4px 12px',
                                       borderRadius: R.pill,
                                       ...TYPO.tag,
                                       textTransform: 'uppercase',
                                       fontVariantNumeric: 'tabular-nums',
                                     }}>
                                       Record absolu
                                       {gapPct !== null && gapPct > 0 && (
                                         <>
                                           <span aria-hidden style={{ opacity: 0.5 }}>·</span>
                                           <span>▲ {gapPct.toFixed(1)}%</span>
                                         </>
                                       )}
                                     </span>
                                   )}
                                 </div>
                               );
                             })}
                           </div>

                           {/* Rang 2 — barres : montant in-bar pour les TROIS, bas alignés */}
                           <div style={{
                             display: 'grid',
                             gridTemplateColumns: COLS,
                             columnGap: S[4],
                             alignItems: 'end',
                           }}>
                             {podium.map((row) => {
                               const rank = rankOf(row);
                               const isFirst = rank === 1;
                               return (
                                 <div key={`bar-${row.date}`} data-rec-bar={rank} style={{
                                   height: barHeightOf(rank),
                                   borderRadius: R.lg,
                                   background: isFirst ? SHADCN.fg : SHADCN.muted,
                                   color: isFirst ? SHADCN.bg : SHADCN.fg,
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   padding: `0 ${S[3]}px`,
                                   overflow: 'hidden',
                                 }}>
                                   <div style={{
                                     fontFamily: SHADCN.font,
                                     fontSize: amountSizeOf(rank),
                                     fontWeight: 600,
                                     lineHeight: 1,
                                     letterSpacing: trackingOf(rank),
                                     fontVariantNumeric: 'tabular-nums',
                                     textAlign: 'center',
                                   }}>
                                     {F(row.total_ttc)}
                                   </div>
                                 </div>
                               );
                             })}
                           </div>

                           {/* Rang 3 — méta uniforme : ordinal · date · caption */}
                           <div style={{
                             display: 'grid',
                             gridTemplateColumns: COLS,
                             columnGap: S[4],
                             alignItems: 'start',
                             marginTop: S[5],
                           }}>
                             {podium.map((row) => {
                               const rank = rankOf(row);
                               return (
                                 <div key={`meta-${row.date}`} data-rec-meta style={{ textAlign: 'center', minWidth: 0 }}>
                                   <div style={{
                                     ...TYPO.tag,
                                     color: SHADCN.mutedFg,
                                     textTransform: 'uppercase',
                                     letterSpacing: '0.12em',
                                     fontVariantNumeric: 'tabular-nums',
                                   }}>
                                     {String(rank).padStart(2, '0')}
                                   </div>
                                   <div style={{
                                     ...TYPO.h2,
                                     color: SHADCN.fg,
                                     marginTop: S[2],
                                     whiteSpace: 'nowrap',
                                     overflow: 'hidden',
                                     textOverflow: 'ellipsis',
                                   }}>
                                     {fmtDate(row.date)}
                                   </div>
                                   <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[2] }}>
                                     {row.nb_transactions} tickets · ⌀ {F(row.ticket_moyen)}
                                   </div>
                                   {Array.isArray(row.presents) && row.presents.length > 0 && (
                                     <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1], opacity: 0.75 }}>
                                       {row.presents.map(p => p.prenom).join(' · ')}
                                     </div>
                                   )}
                                   {evMap[row.date]?.length > 0 && (
                                     <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: S[1], fontStyle: 'italic' }}>
                                       {evMap[row.date][0]}
                                     </div>
                                   )}
                                 </div>
                               );
                             })}
                           </div>
                         </div>
                       );
                     })()}

                     {/* #4-#10 — bloc grand livre, dense */}
                     {rest.length > 0 && (
                       <ol style={{
                         borderTop: `1px solid ${SHADCN.border}`,
                         listStyle: 'none', padding: 0, margin: 0,
                       }}>
                         {rest.map((row, i) => (
                           <li key={row.date} style={{
                             display: 'grid',
                             gridTemplateColumns: GRID_COLS,
                             alignItems: 'baseline',
                             columnGap: S[5],
                             padding: `${S[3]}px 0`,
                             borderBottom: i < rest.length - 1 ? `1px solid ${SHADCN.border}` : 'none',
                           }}>
                             <div style={{ ...TYPO.body, color: SHADCN.mutedFg, fontVariantNumeric: 'tabular-nums' }}>
                               {String(i + 4).padStart(2, '0')}
                             </div>
                             <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: S[3], flexWrap: 'wrap' }}>
                               <div style={{ ...TYPO.body, fontWeight: 500, color: SHADCN.fg }}>{fmtDate(row.date)}</div>
                               <div style={{ ...TYPO.caption, color: SHADCN.mutedFg }}>
                                 {row.nb_transactions} tickets · ticket moyen {F(row.ticket_moyen)}
                                 {Array.isArray(row.presents) && row.presents.length > 0 && (
                                   <> · {row.presents.map(p => p.prenom).join(', ')}</>
                                 )}
                                 {evMap[row.date]?.length > 0 && (
                                   <> · <em>{evMap[row.date][0]}</em></>
                                 )}
                               </div>
                             </div>
                             <div style={{ ...TYPO.amount, color: SHADCN.fg, justifySelf: 'end' }}>{F(row.total_ttc)}</div>
                           </li>
                         ))}
                       </ol>
                     )}
                   </>
                 )}
              </>
            );
          })()}

          {/* ══════════════════════ SETTINGS — PROFIL ══════════════════════ */}
          {/* ══════════════════════ CAFÉ — INFOS ENTREPRISE ════════════════ */}
          {activeTab === 'infos_cafe' && (
            <>
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<ExternalLink size={14} />}
                  onClick={() => window.open('/facturation-cafe.html', '_blank')}
                >
                  Page de facturation
                </Button>
              </div>
              <Card>
                <SectionTitle right={<DocDownload id="kbis" label="Extrait Kbis" />}>Identité légale</SectionTitle>
                <InfoRows rows={CAFE_INFOS.identite} />
              </Card>
              <Card>
                <SectionTitle>TVA &amp; fiscal</SectionTitle>
                <InfoRows rows={CAFE_INFOS.fiscal} />
              </Card>
              <Card>
                <SectionTitle>Adresse &amp; contact</SectionTitle>
                <InfoRows rows={CAFE_INFOS.contact} />
              </Card>
              <Card>
                <SectionTitle>Banque</SectionTitle>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: S[4] }}>
                  {CAFE_INFOS.banques.map(b => (
                    <div key={b.nom} style={{ border:`1px solid ${SHADCN.border}`, borderRadius: R.lg, padding: `${S[4]}px ${S[4]}px ${S[1]}px` }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap: S[3], flexWrap:'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...TYPO.h3, color: SHADCN.fg }}>{b.nom}</div>
                          <div style={{ ...TYPO.caption, color: SHADCN.mutedFg, marginTop: 2 }}>{b.detail}</div>
                        </div>
                        <DocDownload id={b.doc} label="RIB" />
                      </div>
                      <InfoRows rows={b.rows} />
                    </div>
                  ))}
                </div>
              </Card>
              <Card>
                <SectionTitle>Dirigeant &amp; dates</SectionTitle>
                <InfoRows rows={CAFE_INFOS.direction} />
              </Card>
            </>
          )}

          {activeTab === 'settings_profile' && (
            <>
              <ProfilePanel
                role={role}
                session={session}
                onLogout={handleLogout}
                onSessionRefresh={onSessionRefresh}
              />
              <Card>
                <SectionTitle>Apparence</SectionTitle>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: S[4], flexWrap:'wrap' }}>
                  <div>
                    <div style={{ ...TYPO.body, fontWeight:500, color:SHADCN.fg }}>Thème</div>
                    <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop:2 }}>Bascule clair / sombre — préférence enregistrée localement.</div>
                  </div>
                  <ThemeSegmented />
                </div>
              </Card>
            </>
          )}

          {/* ══════════════════════ SETTINGS — CONNEXIONS ══════════════════ */}
          {activeTab === 'settings_connections' && (
            <Card>
              <SectionTitle>Connexions</SectionTitle>
              <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                {(() => {
                  // Fraîcheur relative depuis le dernier fetch réussi de la session
                  const ago = (iso) => {
                    if (!iso) return null;
                    const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
                    return min < 1 ? 'à l’instant' : min < 60 ? `il y a ${min} min` : `il y a ${Math.round(min / 60)} h`;
                  };
                  // detail : erreur réelle si panne, sinon info + fraîcheur
                  const line = (key, okData, okDetail) => {
                    const h = sourcesHealth[key];
                    if (h && h.ok === false) return { ok: false, detail: h.error || 'Erreur' };
                    if (!okData) return { ok: false, detail: 'Non connecté' };
                    return { ok: true, detail: h?.at ? `${okDetail} · ${ago(h.at)}` : okDetail };
                  };
                  return [
                    { label: 'Square', sub: 'Caisse Café Sonore — POS', ...line('square', squareData, `Données ${squareData?.mois?.length || 0} mois`) },
                    { label: 'Stripe', sub: 'Cowork & Privatisation', ...line('stripe', stripeData, stripeData ? `Solde ${fmtFull(stripeData.disponible)} disponible` : '') },
                    ...(role === 'admin' ? [{ label: 'Qonto', sub: 'Soldes bancaires Bureau Sonore', ...line('qonto', qontoTreso, 'Soldes à jour') }] : []),
                    { label: 'Revolut', sub: 'Soldes bancaires Café Sonore — token 90 j', ...line('revolut', revolutTreso, 'Soldes à jour') },
                    { label: 'Supabase', sub: 'Transactions BNP importées', ...line('finances', notionFinances, 'Agrégats à jour') },
                    { label: 'Pipeline IA', sub: 'Claude — extraction factures email', ok: true, detail: 'Apps Script → Vercel → Supabase' },
                  ];
                })().map((conn, i, arr) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 0', borderBottom: i < arr.length - 1 ? `1px solid ${SHADCN.border}` : 'none' }}>
                    <div style={{ display:'flex', alignItems:'center', gap: S[3] }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background: conn.ok ? SHADCN.success : SHADCN.error, flexShrink:0 }} />
                      <div>
                        <div style={{ ...TYPO.body, fontWeight:500, color:SHADCN.fg }}>{conn.label}</div>
                        <div style={{ ...TYPO.caption, color:SHADCN.mutedFg, marginTop:2 }}>{conn.sub}</div>
                      </div>
                    </div>
                    <div style={{ ...TYPO.caption, color: conn.ok ? SHADCN.mutedFg : SHADCN.error, textAlign:'right' }}>{conn.detail}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === 'settings_connections' && <NotificationsCard />}

          {/* ═══════════════════ SETTINGS — PERSONNEL ══════════════════════ */}
          {activeTab === 'settings_acces' && role === 'admin' && (
            <Suspense fallback={null}><AccesTab /></Suspense>
          )}

          {activeTab === 'settings_personnel' && (role === 'admin' || role === 'cafe_admin') && (
            <>
              <MembersAdminPanel currentRole={role} />
              <PersonnelColors onSave={setMemberColors} />
            </>
          )}

          {/* ══════════════════════ SETTINGS — DEBUG ════════════════════════ */}
          {activeTab === 'settings_methode' && (role === 'admin' || role === 'cafe_admin') && (
            <Suspense fallback={<div style={{ padding: S[6], ...TYPO.body, color:SHADCN.mutedFg }}>Chargement…</div>}>
              <CalculsPage inputs={{
                seuilHT, chargesFixesHT, urssafMois: data?.cafe?.urssafMois,
                caNetHT: TOTAL_CA_HT, caCafeGroupHT, achatsFoodHT,
                coutSalarialCharge,
                dayOfMonth, daysInMonth,
                bSeuilRenta, bChargesTotal, bRemuTotal,
                sciLoyersAttendu: data?.sci?.loyersSuivi?.loyerAttendu,
                sciPret: data?.sci?.mensualitePret,
                cfRef,
                tvaSurPlace: TVA_SUR_PLACE,
                tvaAEmporter: TVA_A_EMPORTER,
                tvaRetail: TVA_RETAIL,
                foodCostCible: FOOD_COST_CIBLE,
                foodCostAlerte: FOOD_COST_ALERTE,
              }} />
            </Suspense>
          )}

          {activeTab === 'settings_designsystem' && (role === 'admin' || role === 'cafe_admin') && (
            <Suspense fallback={<PageLoader />}>
              <DesignSystemTab />
            </Suspense>
          )}

          {activeTab === 'settings_debug' && (role === 'admin' || role === 'cafe_admin') && (
            <Card>
              <SectionTitle>Debug · état brut</SectionTitle>
              <div style={{ ...TYPO.caption, ...TYPO.num, color:SHADCN.mutedFg, marginBottom: S[3] }}>v{APP_VERSION}</div>
              <pre style={{ fontFamily:'monospace', fontSize:11, lineHeight:1.5, color:SHADCN.mutedFg, background:SHADCN.muted, borderRadius: R.md, padding:14, overflowX:'auto', whiteSpace:'pre-wrap', wordBreak:'break-all', maxHeight:480, overflowY:'auto' }}>
                {JSON.stringify({ version:APP_VERSION, squareData, qontoTreso, stripeData, notionFinances: notionFinances ? { generatedAt: notionFinances.generatedAt } : null, uploads, generatedAt: new Date().toISOString() }, null, 2)}
              </pre>
            </Card>
          )}

          </div>
        </div>

        <div style={{ borderTop:`1px solid ${SHADCN.border}`,padding:'14px 32px',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
          <img src="/logo-white.png" alt="café sonore" style={{ height: 16, width: 'auto', opacity: 0.4 }} />
          <span style={{ ...TYPO.caption, color:SHADCN.mutedFg }}>Màj {data.lastUpdate}</span>
        </div>
      </div>

      <DetailPanel panel={detailPanel} onClose={() => setDetailPanel(null)} />

      {/* FAB Scan factures — mobile + tab today uniquement */}
      {isMobile && displayTab === 'today' && (
        <a
          href="/scan"
          aria-label="Scanner une facture"
          style={{
            position: 'fixed',
            right: S[5],
            bottom: `calc(env(safe-area-inset-bottom) + ${S[5]}px)`,
            zIndex: 200,
            display: 'flex', alignItems: 'center', gap: S[2],
            padding: `${S[4]}px ${S[5]}px`,
            background: SHADCN.fg, color: SHADCN.bg,
            borderRadius: R.pill,
            fontFamily: SHADCN.font, fontSize: FS.md, fontWeight: 600,
            textDecoration: 'none',
            boxShadow: SHADCN.shadowXl,
          }}
        >
          <Upload size={20} strokeWidth={2.25} />
          SCAN
        </a>
      )}
    </div>
  );
}
